import * as Sentry from "@sentry/browser";
import {
  Card as AntdCard,
  Badge,
  Button,
  Checkbox,
  Col,
  Collapse,
  Descriptions,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Menu,
  message,
  Modal,
  Row,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { FormInstance } from "antd/lib/form";
import { UploadFile } from "antd/lib/upload/interface";
import { AxiosResponse } from "axios";
import React, { Fragment, RefObject, useMemo } from "react";
import { RouteComponentProps } from "react-router";
import Card from "../components/Card";
import EditButton from "../components/EditButton";
import EntitledEntity from "../components/EntitledEntity";
import EntitledSection from "../components/EntitledSection";
import InteractionsSection from "../components/InteractionsSection";
import RestrictedSelfServiceNotice from "../components/RestrictedSelfServiceNotice";
import { MergerSection } from "../components/MergerSection";
import PaymentAccountsView from "../components/PaymentsAccountsView";
import entitlements from "../config/entitlements";
import {
  ActiveDrawerContent,
  Activity,
  APIError,
  Attachment,
  Changelog,
  CloseInteractionPayload,
  CompanyWithCustomerCompanyAssociationId,
  Contribution,
  Country,
  Customer,
  CustomerInteractionAttachment,
  InteractionNotificationPreview,
  CustomerLien,
  CustomerStatement,
  CustomerTransaction,
  Employer,
  Entitlement,
  FormFieldConfig,
  LoanRequest,
  SyncLog,
  Note,
  NotificationRecord,
  Opportunity,
  OpportunityInteraction,
  ProductContributionSubscriptionMapping,
  ProductWithSubscriptionId,
  RedemptionsRequest,
  SalesPerson,
  Scheme,
  SelectOptionsInterface,
  SelfServiceActivity,
  Subscription,
  Template,
  Ticket,
  TransferRequest,
  UpdateBeneficiaryInformationFnProps,
  UpdateCustomerCompanyAssociationFnProps,
  UpdatePaymentInformationFnProps,
  User,
  UssdAccount,
  VirtualAccountNumber,
  FixedIncomeRequest,
  MaritalStatus,
  AssociatedEmail,
  AssociatedPhone,
  AssociatedAddress,
  EmailInterface,
  PhoneInterface,
  AddressInterface,
  CustomerBackgroundInformation,
  Company,
  FinancialInstitution,
  BranchMap,
  Beneficiary,
  PaymentAccount,
  CustomerCompanyAssociation,
  FileTypes,
} from "../helpers/shared-interfaces";
import {
  capitalizeFirstLetter,
  convertCompaniesToSelectOptions,
  disabledDate,
  formatSnakeCase,
  subscriptionInitialTags,
  formatter,
  generateDisplayErrorMessage,
  generateNameFromCustomer,
  handleValidationErrors,
  isAutomaticTag,
  mapSubscriptionToContribution,
  request,
  responseHandler,
  spaceCamel,
  getStateColor,
  isSavingsBooster,
  isSavingsBoosterProduct,
  isTierThreeProduct,
  mapAPIErrorToFormError,
} from "../helpers/utilities";
import CustomerOnboardingDetails, {
  Views as CustomerOnboardingViews,
  createDocumentUploadFormFields,
} from "../components/CustomerOnboardingDetails";

import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  DownOutlined,
  EyeInvisibleOutlined,
  LeftOutlined,
  LinkOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import DescriptionsItem from "antd/lib/descriptions/Item";
import Search from "antd/lib/input/Search";
import { ColumnType } from "antd/lib/table";
import { ColumnsType } from "antd/lib/table/Table";
import debounce from "lodash.debounce";
import moment, { Moment } from "moment";
import { NavLink } from "react-router-dom";
import { FieldData } from "rc-field-form/lib/interface";
import ActivitiesSection, {
  CALCULATE_DUE_DATE_QUERY,
} from "../components/ActivitiesSection";
import ContactDetailsView from "../components/ContactDetailsView";
import VerifiedMark from "../components/VerifiedMark";
import CustomerAssociationsView from "../components/CustomerAssociationsView";
import CustomerBeneficiariesView from "../components/CustomerBeneficiariesView";
import CustomerLienSection from "../components/CustomerLienSection";
import DelayUpdateModal from "../components/DelayUpdateModal";
import BaseForm from "../components/Form/BaseForm";
import { GenerateStatementUI } from "../components/GenerateStatementUI";
import CustomerInteractionSection from "../components/InteractionSection";
import ModalCreator from "../components/ModalCreator";
import CreateCustomerSubscription from "../components/ProductSubscription";
import { SelfServiceActivityUI } from "../components/SelfServiceActivities";
import TagsManagement from "../components/TagsManagement";
import TagsSection from "../components/TagsSection";
import {
  Constants as AssociatedEntityConstants,
  createAddressFields,
  createEmailFields,
  createPhoneFields,
  createBackgroundInfoFields,
} from "../components/AssociatedEntitiesSection";
import {
  ASSIGN_ATTACHMENT_TO_CUSTOMER_INTERACTION,
  ASSIGN_NOTE_TO_CUSTOMER_INTERACTION,
  CLOSE_CUSTOMER_INTERACTION,
  CREATE_CUSTOMER_INTERACTION,
  CREATE_CUSTOMER_LIEN,
  CREATE_CUSTOMER_TICKET,
  CREATE_INTERACTION_CUSTOMER_COMPANY,
  CREATE_INTERACTION_CUSTOMER_SUBSCRIPTION,
  DELETE_CUSTOMER_LIEN,
  DELETE_CUSTOMER_SUBSCRIPTION,
  GENERATE_CUSTOMER_STATEMENT,
  GET_CUSTOMER_CHANGELOG,
  GET_CUSTOMER_INTERACTION_CHANGELOG,
  GET_CUSTOMER_INTERACTION_NOTIFICATION_PREVIEW,
  GET_CUSTOMER_INTERACTIONS_QUERY,
  GET_CUSTOMER_LOAN_REQUESTS,
  GET_CUSTOMER_NOTIFIER_RECORDS_QUERY,
  GET_CUSTOMER_ONLINE_TRANSACTIONS,
  GET_CUSTOMER_QUERY,
  GET_CUSTOMER_REDEMPTION_REQUESTS,
  GET_CUSTOMER_SCHEME_EXTENDED,
  GET_CUSTOMER_SCHEMES,
  GET_CUSTOMER_SELF_ACTIVITIES_QUERY,
  GET_CUSTOMER_STATEMENTS,
  GET_CUSTOMER_SUBSCRIPTIONS,
  GET_CUSTOMER_TICKETS,
  GET_CUSTOMER_TRANSFER_REQUESTS,
  HOLD_CUSTOMER_INTERACTION,
  MERGE_CUSTOMERS,
  OPEN_CUSTOMER_INTERACTION,
  REVERSE_MERGE_CUSTOMERS,
  BULK_REVERSE_MERGE_CUSTOMERS,
  SEARCH_CUSTOMER_BY_PETRAID,
  SEND_CUSTOMER_INTERACTION_DELAY_UPDATE,
  UNASSIGN_ATTACHMENT_FROM_CUSTOMER_INTERACTION,
  UPDATE_CUSTOMER_INTERACTION,
  UPDATE_CUSTOMER_LIEN,
  UPDATE_CUSTOMER_SUBSCRIPTION,
  ASSIGN_TAGS_TO_CUSTOMER_SUBSCRIPTION,
  ASSIGN_ADDRESS_TO_CUSTOMER,
  ASSIGN_EMAIL_TO_CUSTOMER,
  ASSIGN_PHONE_TO_CUSTOMER,
  UNASSIGN_ADDRESS_FROM_CUSTOMER,
  UNASSIGN_EMAIL_FROM_CUSTOMER,
  UNASSIGN_PHONE_FROM_CUSTOMER,
  UPDATE_CUSTOMER_ADDRESS,
  UPDATE_CUSTOMER_EMAIL,
  UPDATE_CUSTOMER_PHONE,
  CREATE_CUSTOMER_BACKGROUND_INFORMATION,
  UPDATE_CUSTOMER_BACKGROUND_INFORMATION,
  DELETE_CUSTOMER_ASSOCIATION,
  UPDATE_CUSTOMER_COMPANY_ASSOCIATION,
  UPDATE_CUSTOMER_MUTATION,
  GET_SYNC_LOGS,
  SYNC_CUSTOMER_MANUALLY,
  GET_CUSTOMER_FIXED_INCOME_REQUESTS,
  GET_CUSTOMER_TAGS,
  ASSIGN_TAGS_TO_CUSTOMER,
} from "../gql/customers";
import {
  DELETE_PAYOUT_ACCOUNT,
  DELETE_DEBIT_ACCOUNT,
} from "../gql/accounts";
import { DELETE_BENEFICIARY } from "../gql/beneficiaries";
import {
  CREATE_NOTE,
  CREATE_ADDRESS,
  CREATE_EMAIL,
  CREATE_PHONE,
  GET_FINANCIAL_INSTITUTIONS,
  GET_COUNTRIES,
  MARK_ADDRESS_AS_PRIMARY,
  MARK_EMAIL_AS_PRIMARY,
  MARK_PHONE_AS_PRIMARY,
  GET_MARITAL_STATUSES,
  GET_TITLES,
  LOG_SELF_SERVICE_ACTIVITIES,
  DELETE_FILE,
  GET_TAGS,
} from "../gql/general";
import { GET_COMPANIES } from "../gql/companies";
import { UPDATE_CUSTOMER_FIRST_CONTRIBUTION } from "../gql/transfer-requests";
import { GET_SALES_PEOPLE } from "../gql/users";
import {
  RESET_USSD_ACCOUNT_PIN,
  UPDATE_USSD_ACCOUNT_PHONE,
  UPDATE_USSD_ACCOUNT_STATUS,
} from "../gql/ussd";
import { GET_DELAY_NOTIFICATION_TEMPLATES } from "../gql/templates";
import NewRedemptionRequest from "./NewRedemptionRequest";
import NewTransferRequest from "./NewTransferRequest";
import {
  notificationColumns,
  NotificationRecordTableFactory,
} from "./NotificationRecords";
import { ticketColumns, transferRequestsColumns } from "./ViewCompany";
import { FirstContributionUI } from "./ViewTransferRequest";
import { NewLoanRequest } from "./NewLoanRequest";
import { NewFixedIncomeRequest } from "./NewFixedIncomeRequest";
import CustomerBalanceBreakdown from "../components/CustomerBalanceBreakdown";
import { DEBOUNCE_TIME, ghanaCardRegex, tabPaneStyle } from "../helpers/constants";

const { Panel } = Collapse;

const user = JSON.parse(localStorage.getItem("user") as string);

enum mapServerPropToLocalProp {
  dateOfBirth = "Date of Birth",
}

interface State {
  id: string | number;
  isEditingInteraction: boolean;
  isLoading: boolean;
  isMerging: boolean;
  contributionsLoader: boolean;
  customer?: Customer;
  showQuickEditModal: boolean;
  isQuickEditSaving: boolean;
  titles: SelectOptionsInterface[];
  maritalStatuses: MaritalStatus[];
  isQuickEditVerified: boolean;
  signatureUrl?: string;
  ghanaCardIdUrl?: string;
  ghanaCardIdBackUrl?: string;
  photoIdUrl?: string;
  verificationProofUrl?: string;
  showContactModal: boolean;
  contactModalType: "email" | "phone" | "address" | null;
  contactModalMode: "add" | "edit";
  contactModalLoading: boolean;
  selectedEmail?: AssociatedEmail | null;
  selectedPhone?: AssociatedPhone | null;
  selectedAddress?: AssociatedAddress | null;
  addressType: string;
  showContactDeleteModal: boolean;
  deleteContactType: "email" | "phone" | "address" | null;
  deleteContactId: string | null;
  showBackgroundInfoModal: boolean;
  backgroundInfoMode: "add" | "edit";
  isBackgroundInfoSaving: boolean;
  showBackgroundInfoDeleteModal: boolean;
  fatherDeceased?: boolean;
  motherDeceased?: boolean;
  showBeneficiariesModal: boolean;
  showPaymentAccountsModal: boolean;
  showAssociationsModal: boolean;
  paymentAccountsActiveKey?: string;
  beneficiariesActiveKey?: string;
  associationsActiveKey?: string;
  showStandaloneDeleteModal: boolean;
  deleteTargetType:
    | "beneficiary"
    | "payout"
    | "debit"
    | "association"
    | null;
  deleteTargetId: string | null;
  financialInstitutions: FinancialInstitution[];
  branchMap: BranchMap;
  companies: Company[];
  countries: Country[];
  interactions: OpportunityInteraction[];
  changeLogs: Changelog[];
  interactionsChangeLogs: Changelog[];
  showChangeLog: boolean;
  isFiltering: boolean;
  filterBy: string;
  filteredChangelogs: Changelog[];
  filteredSelfServiceActivities: SelfServiceActivity[];
  isSelfActivityFiltering: boolean;
  showAccountMergerModal: boolean;
  showReverseMergeModal: boolean;
  customerSearchQuery: string;
  customers: Customer[];
  selectedCustomers: string[];
  isSearchingForCustomer: boolean;
  showDrawer: boolean;
  activeDrawerContent: ActiveDrawerContent;
  isReversingMerge: boolean;
  isBulkReversingMerge: boolean;
  customerInteractions: Activity[];
  currentInteractionId?: string;
  currentFundCode: string;
  currentContribution?: ProductContributionSubscriptionMapping;
  showStatementModal: boolean;
  isStatementLoading: boolean;
  canCreateTicket: boolean;
  generatedStatementUrl?: string;
  attachmentIds: string[];
  attachments: Attachment[];
  tickets: Ticket[];
  customerBalanceSummary: Scheme[];
  isTicketLoading: boolean;
  showInteractionModal: boolean;
  extendedScheme: Scheme | null;
  hasLoadedContributions: boolean;
  currentCustomerVan: VirtualAccountNumber | undefined;
  currentCustomerVanIsSavingsBooster: boolean;
  customerTransactions: CustomerTransaction[];
  redemptionRequests: RedemptionsRequest[];
  transferRequests: TransferRequest[];
  activitiesCount: number;
  activitiesPage: number;
  isInteractionLoading: boolean;
  showCancelModal: boolean;
  currentCustomerToProcess: number;
  ussdAccount: UssdAccount | undefined;
  showConfirmCreateModal: boolean;
  showConfirmUpdateModal: boolean;
  phoenixTickets: Ticket[];
  showConfirmProductDelete: boolean;
  subscriptionIdToDelete: string | undefined;
  showSubscriptionEditModal: boolean;
  subscriptionToEdit?: Subscription;
  isSavingSubscriptionEdits: boolean;
  salesPersons: SalesPerson[];
  salesPersonSearchTerm: string;
  customerStatements: CustomerStatement[];
  disableCreateInteractionButton: boolean;
  showFirstContributionModal: boolean;
  proofOfContributionDocumentUrl?: string;
  hasNewProofUpload: boolean;
  notes?: Note[];
  lienAttachmentUrl: string;
  customerInteractionAttachments: CustomerInteractionAttachment[];
  isRedemptionLoading: boolean;
  isTransferLoading: boolean;
  isTransactionsLoading: boolean;
  loanRequests: LoanRequest[];
  isLoanRequestsLoading: boolean;
  syncLogs: SyncLog[];
  isSyncLogsLoading: boolean;
  fixedIncomeRequests: FixedIncomeRequest[];
  isFixedIncomeRequestsLoading: boolean;
  showAllocationDetailsModal: boolean;
  currentSubscriptionForAllocation: Subscription | null;
  allCustomerTags: import("../helpers/shared-interfaces").TagRecord[];
  isLoadingCustomerTags: boolean;
  productTags: SelectOptionsInterface[];
  showAddDocumentModal: boolean;
  showDeleteConfirmationModal: boolean;
  modalTarget: string;
  idToBeDeleted?: string | null;
  showDelayUpdateModal: boolean;
  isDelayTemplatesLoading: boolean;
  isDelayPreviewLoading: boolean;
  isSendingDelayUpdate: boolean;
  delayNotificationTemplates: Template[];
  selectedDelayTemplateId?: string;
  delayUpdateComment: string;
  delayNotificationPreview?: InteractionNotificationPreview | null;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; requests?: Customer[] }
  > {
  entitlement: Entitlement;
  user: User;
  activityId?: string;
}

interface RouteParams {
  id: string;
}

export const onlineTransactionColumns = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return (
        <NavLink
          to={`/online-transactions/${record.id}`}
          data-cy="test-online-transactions-link"
        >
          {record.id}
        </NavLink>
      );
    },
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return <span>{record.description}</span>;
    },
  },
  {
    title: "Product",
    dataIndex: ["customerSubscription,product,name"],
    key: "productName",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return <span>{record.customerSubscription.product.name}</span>;
    },
  },
  {
    title: "Type",
    dataIndex: "transactionType",
    key: "transactionType",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return <span>{record.transactionType}</span>;
    },
  },
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return <span>{record.provider}</span>;
    },
  },
  {
    title: "Mode",
    dataIndex: "mode",
    key: "mode",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return <span>{record.mode}</span>;
    },
  },
  {
    title: "Date",
    dataIndex: "date",
    key: "date",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return (
        <span>
          {record.createdAt
            ? moment(record.createdAt, "YYYY-MM-DD HH:mm:ss").format(
                "DD MMM YYYY",
              )
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return <span>{formatter.format(record.totalAmount)}</span>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render(text: string, record: CustomerTransaction): JSX.Element {
      return (
        <Tag
          color={getStateColor(record.status)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(record.status)}
        </Tag>
      );
    },
  },
];

export const changelogColumns = [
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "date",
    width: "25%",
    render(text: string): JSX.Element {
      const [date, time] = text.split(" ");
      return (
        <span>
          {moment(date, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")}
          <br />
          at {time}
        </span>
      );
    },
  },
  {
    title: "Audit",
    dataIndex: "auditItem",
    key: "audit",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "User",
    dataIndex: "user",
    key: "user",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Action",
    dataIndex: "action",
    key: "action",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
  {
    title: "Comments",
    dataIndex: "comment",
    key: "comment",
    align: "left" as const,
    render(text: string): JSX.Element {
      return <span>{text}</span>;
    },
  },
];

export const loanColumns = [
  {
    title: "View",
    dataIndex: "loanRequest",
    key: "loanRequest",
    render(text: any, record: LoanRequest): JSX.Element {
      return record ? (
        <NavLink to={`/loan-requests/${record.id}`}>{record.id}</NavLink>
      ) : (
        <span>N/A</span>
      );
    },
  },
  {
    title: "Request Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render(createdAt: string): JSX.Element {
      return (
        <span>
          {createdAt
            ? moment(createdAt, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY")
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    render(product: any): JSX.Element {
      return <span>{product.name}</span>;
    },
  },
  {
    title: "Status",
    dataIndex: "state",
    key: "state",
    render(state: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(state)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(state)}
        </Tag>
      );
    },
  },
  {
    title: "Source",
    dataIndex: "source",
    key: "source",
    render(source: string): JSX.Element {
      return <span>{source}</span>;
    },
  },
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
    render(provider: any): JSX.Element {
      return (
        <NavLink to={`/providers/${provider.id}`}>{provider.name}</NavLink>
      );
    },
  },
  {
    title: "Loan Type",
    dataIndex: "loanType",
    key: "loanType",
    render(text: string): JSX.Element {
      return (
        <span style={{ textTransform: "capitalize" }}>
          {(text && formatSnakeCase(text)) || "N/A"}
        </span>
      );
    },
  },
  {
    title: "Tenor (Months)",
    dataIndex: "loanTenureMonths",
    key: "loanTenureMonths",
    render(loanTenureMonths: string): JSX.Element {
      return <span>{loanTenureMonths}</span>;
    },
  },
  {
    title: "Amount Requested",
    dataIndex: "loanAmountRequested",
    key: "loanAmountRequested",
    align: "right" as const,
    render(amount: number): JSX.Element {
      return <span>{amount ? formatter.format(amount) : "N/A"}</span>;
    },
  },
];

export const fixedIncomeColumns = [
  {
    title: "View",
    dataIndex: "fixedIncomeRequest",
    key: "fixedIncomeRequest",
    render(text: any, record: FixedIncomeRequest): JSX.Element {
      return record ? (
        <NavLink to={`/fixed-income-requests/${record.id}`}>
          {record.id}
        </NavLink>
      ) : (
        <span>N/A</span>
      );
    },
  },
  {
    title: "Request Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render(createdAt: string): JSX.Element {
      return (
        <span>
          {createdAt
            ? moment(createdAt, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    render(product: any): JSX.Element {
      return <span>{product.name}</span>;
    },
  },
  {
    title: "Provider",
    dataIndex: "provider",
    key: "provider",
    render(provider: any): JSX.Element {
      return (
        <NavLink to={`/providers/${provider.id}`}>{provider.name}</NavLink>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "state",
    key: "state",
    render(state: string): JSX.Element {
      return (
        <Tag
          color={getStateColor(state)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(state)}
        </Tag>
      );
    },
  },
  {
    title: "Source",
    dataIndex: "source",
    key: "source",
    render(source: string): JSX.Element {
      return <span>{source}</span>;
    },
  },
  {
    title: "Assigned To",
    dataIndex: "assignedTo",
    key: "assignedTo",
    render(assignedTo: any): JSX.Element {
      return <span>{assignedTo?.name || "Unassigned"}</span>;
    },
  },
];

export function ChangelogTableFactory(data: Changelog[]): JSX.Element {
  const tempData: Changelog[] = data.map(
    (log: Changelog, index: number): Changelog => {
      return { ...log, key: String(index) };
    },
  );
  return (
    <Table
      style={{ width: "100%" }}
      bordered={true}
      columns={changelogColumns}
      dataSource={tempData}
      pagination={false}
    />
  );
}

export function breakdownColumnCreator(
  fields: string[],
): ColumnsType<Contribution> {
  const columns = fields.map((field: string, index: number) => {
    return {
      title: field,
      dataIndex: ["mappings,amount"],
      key: field,
      render(text: string, record: Contribution) {
        return (
          <p>{formatter.format(Number(record.mappings[index]?.amount))}</p>
        );
      },
    };
  });

  return columns;
}

export function genDyFields(contribution?: Contribution): string[] {
  const fields: string[] = [];
  if (!contribution) return fields;

  const keys: string[] = [];
  contribution &&
    contribution.mappings.forEach((mapping) => {
      keys.push(mapping.deal_contribution_type);
    });

  for (const key of keys) {
    switch (key) {
      case "employee_post_tax":
        fields.push("Employee Post Tax");
        break;
      case "employer_post_tax":
        fields.push("Employer Post Tax");
        break;
      case "employee_pre_tax":
        fields.push("Employee Pre Tax");
        break;
      case "employer_pre_tax":
        fields.push("Employer Pre Tax");
        break;
      case "contribution":
        fields.push("Contribution");
        break;
    }
  }
  return fields;
}

export const redemptionRequestsColumns = [
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    render(text: string, record: RedemptionsRequest): JSX.Element {
      return (
        <NavLink to={`/redemption-requests/${record.id}`}>
          <span>{record.id}</span>
        </NavLink>
      );
    },
  },
  {
    title: "Scheme",
    dataIndex: ["customerSubscription", "product", "name"],
    key: "name",
    render(text: string, record: RedemptionsRequest): JSX.Element {
      return <span>{record.customerSubscription?.product.name}</span>;
    },
  },
  {
    title: "Request Date",
    dataIndex: "requestDate",
    key: "requestDate",
    render(text: string, record: RedemptionsRequest): JSX.Element {
      return (
        <span>
          {record.requestDate
            ? moment(record.requestDate, "YYYY-MM-DD HH:mm:ss").format(
                "DD/MM/YYYY",
              )
            : "N/A"}
        </span>
      );
    },
  },
  {
    title: "State",
    dataIndex: "state",
    key: "state",
    render(text: string, record: RedemptionsRequest): JSX.Element {
      return (
        <Tag
          color={getStateColor(record.state)}
          style={{ textTransform: "capitalize" }}
        >
          {formatSnakeCase(record.state)}
        </Tag>
      );
    },
  },
  {
    title: "Redemption Type",
    dataIndex: "redemptionType",
    key: "redemptionType",
    render(text: string, record: RedemptionsRequest): JSX.Element {
      return <span>{record.redemptionType}</span>;
    },
  },
  {
    title: "Redemption Amount",
    dataIndex: "redemptionAmount",
    key: "redemptionAmount",
    render(text: string, record: RedemptionsRequest): JSX.Element {
      return <span>{formatter.format(record.redemptionAmount)}</span>;
    },
  },
];

export function RedemptionsRequestsTableFactory(
  data: RedemptionsRequest[],
): JSX.Element {
  return (
    <Table
      bordered={true}
      columns={redemptionRequestsColumns}
      dataSource={data}
    />
  );
}

export const contributionColumnsFirstHalf = [
  {
    title: "Deal ID",
    dataIndex: "ta_id",
    key: "ta_id",
    render(text: string, record: Contribution): JSX.Element {
      return <span>{record.ta_id}</span>;
    },
  },
  {
    title: "Client",
    dataIndex: "client_name",
    key: "client_name",
    render(text: string, record: Contribution): JSX.Element {
      return <p>{record.client_name}</p>;
    },
  },
  {
    title: "Price Date",
    dataIndex: "price_date",
    key: "priceDate",
    render(text: string, record: Contribution): JSX.Element {
      return (
        <p>
          {record.price_date
            ? moment(record.price_date, "YYYY-MM-DD HH:mm:ss").format(
                "DD/MM/YYYY",
              )
            : "N/A"}
        </p>
      );
    },
  },
  {
    title: "Description",
    dataIndex: "deal_description",
    key: "description",
    render(text: string, record: Contribution): JSX.Element {
      return <p>{record.deal_description}</p>;
    },
  },
];

export const contributionColumnsSecondHalf = [
  {
    title: "Total Amount",
    dataIndex: "total_amount",
    key: "totalAmount",
    render(text: string, record: Contribution): JSX.Element {
      return record.deal_type === "redemption" ? (
        <span
          style={{
            color: "red",
            marginLeft: "12px",
          }}
        >
          {formatter.format(record.total_amount)}
        </span>
      ) : (
        <span
          style={{
            color: "green",
            marginLeft: "12px",
          }}
        >
          {formatter.format(record.total_amount)}
        </span>
      );
    },
  },
  {
    title: "Total Units",
    dataIndex: "total_units",
    key: "totalUnits",
    render(text: string, record: Contribution): JSX.Element {
      return record.deal_type === "redemption" ? (
        <span
          style={{
            color: "red",
            marginLeft: "12px",
          }}
        >
          {record.total_units}
        </span>
      ) : (
        <span
          style={{
            color: "green",
            marginLeft: "12px",
          }}
        >
          {record.total_units}
        </span>
      );
    },
  },
];

export function ContributionsTableFactory(
  data: Contribution[] | undefined,
  columns: ColumnsType<Contribution>,
): JSX.Element {
  const initialData = data ? data : [];
  const tempData: Contribution[] = initialData.map(
    (c: Contribution): Contribution => {
      return { ...c, key: c.id };
    },
  );

  return <Table bordered={true} columns={columns} dataSource={tempData} />;
}

export function getCustomerStatementTableColumns<
  T extends CustomerStatement,
>(): ColumnType<T>[] {
  return [
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render(text: string): JSX.Element {
        return <span>{text}</span>;
      },
    },
    {
      title: "Email Sent",
      dataIndex: "emailSent",
      key: "emailSent",
      render(text: string): JSX.Element {
        return <span>{text.toString()}</span>;
      },
    },
    {
      title: "SMS Sent",
      dataIndex: "smsSent",
      key: "smsSent",
      render(text: string): JSX.Element {
        return <span>{text.toString()}</span>;
      },
    },
    {
      title: "Date Sent",
      dataIndex: "dateSent",
      key: "dateSent",
      render(text: string): JSX.Element {
        return (
          <span>
            {text ? moment(text, "YYYY-MM-DD HH:mm:ss").format("ll") : "N/A"}
          </span>
        );
      },
    },
    {
      title: "Email Resent At",
      dataIndex: "emailResentAt",
      key: "emailResentAt",
      render(text: string): JSX.Element {
        return (
          <span>
            {text ? moment(text, "YYYY-MM-DD HH:mm:ss").format("ll") : "N/A"}
          </span>
        );
      },
    },
    {
      title: "SMS Resent At",
      dataIndex: "smsResentAt",
      key: "smsResentAt",
      render(text: string): JSX.Element {
        return (
          <span>
            {text ? moment(text, "YYYY-MM-DD HH:mm:ss").format("ll") : "N/A"}
          </span>
        );
      },
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      align: "center" as const,

      render(text: string, record: CustomerStatement): JSX.Element {
        return (
          <a href={`${record?.url}`} target="_blank" rel="noopener noreferrer">
            <LinkOutlined />
          </a>
        );
      },
    },
  ];
}

export const AccountMergerUI = (props: {
  searchQuery: string;
  customers: Customer[];
  isLoading: boolean;
  isMerging: boolean;
  handleSearchInput: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  handleCheckboxGroupChange: (values: CheckboxValueType[]) => void;
  selectedCustomers: string[];
}): JSX.Element => {
  return (
    <Fragment>
      <Spin spinning={props.isMerging}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div>Search</div>
            <Search
              placeholder="Search by PetraId"
              style={{ width: "100%", marginTop: "10px" }}
              onChange={props.handleSearchInput}
              value={props.searchQuery}
              data-testid="filter-input"
              allowClear
              id="search-input"
            />
            <div
              style={{
                marginTop: "10px",
              }}
            >
              <Spin spinning={props.isLoading}>
                Results ({props.customers.length})
                {props.customers.length === 0 && <Empty />}
                <div>
                  <Checkbox.Group
                    defaultValue={props.selectedCustomers}
                    onChange={props.handleCheckboxGroupChange}
                  >
                    <Row>
                      {props.customers.map((customer, index): JSX.Element => {
                        return (
                          <Col
                            span={24}
                            key={index}
                            style={{ marginTop: "10px" }}
                          >
                            <Checkbox value={customer.id}>
                              {generateNameFromCustomer(customer)} /{" "}
                              {customer.petraId}
                            </Checkbox>
                          </Col>
                        );
                      })}
                    </Row>
                  </Checkbox.Group>
                </div>
              </Spin>
            </div>
          </Col>
        </Row>
      </Spin>
    </Fragment>
  );
};

export const ReverseMergeUI = (props: {
  customer: Customer | undefined;
  isReversingMerge: boolean;
}): JSX.Element => {
  return (
    <Fragment>
      <Spin spinning={props.isReversingMerge}>
        <Row>
          <Col
            span={24}
            style={{
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                marginTop: "10px",
              }}
            >
              You are about to reverse a merge between this account(
              {props.customer && props.customer.petraId}) and another.
            </div>
          </Col>
        </Row>
      </Spin>
    </Fragment>
  );
};

export const VanUI = (props: {
  van?: VirtualAccountNumber;
  showOldVan?: boolean;
}): JSX.Element => {
  return (
    <Fragment>
      <Descriptions>
        <Descriptions.Item label={<b>Van Custodian</b>}>
          {props.van ? props.van.financialInstitution.name : ""}
        </Descriptions.Item>
      </Descriptions>
      <Descriptions>
        <Descriptions.Item label={<b>Van Number</b>}>
          {props.van ? props.van.van : ""}
        </Descriptions.Item>
      </Descriptions>
      {props.showOldVan && (
        <Descriptions>
          <Descriptions.Item label={<b>Old Van Number</b>}>
            {props.van && props.van.oldVan ? props.van.oldVan : "N/A"}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Fragment>
  );
};

export const customerBalanceSummaryColumn = [
  {
    title: "Product Name",
    dataIndex: "name",
    key: "productName",
    render(text: string): JSX.Element {
      return <p>{text}</p>;
    },
  },
  {
    title: "Total Balance",
    dataIndex: "balance",
    key: "totalBalance",
    render(value: number): JSX.Element {
      return <p>{formatter.format(value)}</p>;
    },
  },
  {
    title: "Redeemable Balance",
    dataIndex: "redeemableBalance",
    key: "redeemableBalance",
    render(value: number): JSX.Element {
      return <p>{formatter.format(value)}</p>;
    },
  },
  {
    title: "Product Lien",
    dataIndex: "liens",
    key: "liens",
    render(value: number): JSX.Element {
      return value ? <p>{formatter.format(value)}</p> : <p>N/A</p>;
    },
  },
  {
    title: "Date Joined Scheme",
    dataIndex: "dateJoinedScheme",
    key: "dateJoinedScheme",
    render(text: string): JSX.Element {
      return (
        <span>{moment(text, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY")}</span>
      );
    },
  },
];

export const generateColumns = (scheme: Scheme) => {
  const dyFields = genDyFields(scheme.contributions?.[0]);
  const columns = [
    ...contributionColumnsFirstHalf,
    ...breakdownColumnCreator(dyFields),
    ...contributionColumnsSecondHalf,
  ];
  return columns;
};

export const Details = (props: {
  customer: Customer;
  quickEditButton?: JSX.Element;
  openContactModal: (
    type: "email" | "phone" | "address",
    mode: "add" | "edit",
    record?: AssociatedEmail | AssociatedPhone | AssociatedAddress,
  ) => void;
  openDeleteContactModal: (
    type: "email" | "phone" | "address",
    id: string,
  ) => void;
  openBackgroundInfoModal: (mode: "add" | "edit") => void;
  openBackgroundInfoDeleteModal: () => void;
  openBeneficiariesModal: (beneficiary?: Beneficiary) => void;
  openBeneficiariesDeleteModal: (beneficiary: Beneficiary) => void;
  openPaymentAccountsModal: (
    account?: PaymentAccount,
    type?: "payout" | "debit",
  ) => void;
  openPaymentAccountsDeleteModal: (
    account: PaymentAccount,
    type: "payout" | "debit",
  ) => void;
  openAssociationsModal: (association?: CustomerCompanyAssociation) => void;
  openAssociationsDeleteModal: (
    association: CustomerCompanyAssociation,
  ) => void;
  markAssociationAsFormer: (
    association: CustomerCompanyAssociation,
  ) => Promise<void>;
  markAssociationAsCurrent: (
    association: CustomerCompanyAssociation,
  ) => Promise<void>;
  onMakePrimaryEmail: (email: AssociatedEmail) => void;
  onMakePrimaryPhone: (phone: AssociatedPhone) => void;
  onMakePrimaryAddress: (address: AssociatedAddress) => void;
  onReactivateEmail: (email: AssociatedEmail) => void;
  onReactivatePhone: (phone: AssociatedPhone) => void;
  onReactivateAddress: (address: AssociatedAddress) => void;
  toggleFileModal: (open?: boolean) => void;
  toggleDeleteConfirmationModal: (id?: string) => void;
  setEntityToBeDeleted: (entity: string) => void;
  toggleDrawer: (value: ActiveDrawerContent) => () => void;
  toggleLienSection: (value: string) => void;
  toggleAccountMergerModal: () => void;
  toggleReverseMergeModal: () => void;
  contributionsLoader: boolean;
  interactionReference: RefObject<FormInstance>;
  createInteraction: () => Promise<void>;
  setContribution: (item: ProductContributionSubscriptionMapping) => void;
  getProductContribution: (key: string | string[]) => void;
  customerBalanceSummary: Scheme[];
  toggleVansSection: (van?: VirtualAccountNumber, productName?: string) => void;
  customerTransactions: CustomerTransaction[];
  redemptionRequests: RedemptionsRequest[];
  transferRequests: TransferRequest[];
  isRedemptionLoading: boolean;
  isTransferLoading: boolean;
  isTransactionsLoading: boolean;
  onReverse: (duplicateId: string) => void;
  onReverseAll: (duplicateIds?: string[]) => void;
  isReversingMerge: boolean;
  isBulkReversing: boolean;
  extra?: JSX.Element;
  phoenixTickets?: Ticket[];
  toggleConfirmProductDelete: (subscriptionId: string | undefined) => void;
  openSubscriptionEditModal: (subscription: Subscription) => void;
  handlePanelChange?: (key: string | string[]) => void;
  customerStatements: CustomerStatement[];
  toggleFirstContributionModal?: (
    ProductContributionSubscriptionMapping: ProductContributionSubscriptionMapping,
  ) => void;
  createInteractionButtonRef?: React.RefObject<HTMLButtonElement>;
  isLoanRequestsLoading: boolean;
  loanRequests: LoanRequest[];
  fixedIncomeRequests: FixedIncomeRequest[];
  isFixedIncomeRequestsLoading: boolean;
  toggleAllocationDetailsModal: (subscription: Subscription) => void;
}): JSX.Element => {
  const [redemptionSearch, setRedemptionSearch] = React.useState<string>("");
  const [loanRequestSearch, setLoanRequestSearch] = React.useState<string>("");
  const [fixedIncomeSearch, setFixedIncomeSearch] = React.useState<string>("");

  const hasSubscription =
    props.customer.customerSubscriptions &&
    props.customer.customerSubscriptions.length > 0;

  const subscriptionsWithContributions = mapSubscriptionToContribution(
    [],
    props.customer.customerSubscriptions,
    props.customerBalanceSummary,
  );

  const filteredRedemptionRequests = props.redemptionRequests.filter((req) => {
    const productName =
      req.customerSubscription?.product?.name?.toLowerCase() || "";
    const requestDate = req.requestDate?.toLowerCase() || "";

    return (
      productName.includes(redemptionSearch.toLowerCase()) ||
      moment(requestDate, "YYYY-MM-DD HH:mm:ss")
        .format("DD/MM/YYYY")
        .includes(redemptionSearch)
    );
  });

  const filteredLoanRequests = useMemo(() => {
    if (!props.loanRequests) return [];

    if (!loanRequestSearch) return props.loanRequests;

    const searchLower = loanRequestSearch.toLowerCase();

    return props.loanRequests.filter((request: any) => {
      const loanAmount = request.loanAmountRequested?.toString() || "";
      const requestDate = moment(request.createdAt).format("DD/MM/YYYY");
      const status = request.state?.toLowerCase() || "";
      const provider = request.provider?.name?.toLowerCase() || "";
      const product = request.product?.name?.toLowerCase() || "";

      return (
        loanAmount.includes(searchLower) ||
        requestDate.includes(searchLower) ||
        status.includes(searchLower) ||
        provider.includes(searchLower) ||
        product.includes(searchLower)
      );
    });
  }, [props.loanRequests, loanRequestSearch]);

  const filteredFixedIncomeRequests = useMemo(() => {
    if (!props.fixedIncomeRequests) return [];
    if (!fixedIncomeSearch) return props.fixedIncomeRequests;
    const searchLower = fixedIncomeSearch.toLowerCase();
    return props.fixedIncomeRequests.filter((request: any) => {
      const investmentAmount =
        request.initialInvestmentAmount?.toString() || "";
      const requestDate = moment(request.createdAt).format("DD/MM/YYYY");
      const provider = request.provider?.name?.toLowerCase() || "";
      const product = request.product?.name?.toLowerCase() || "";
      const activity =
        request.anticipatedInvestmentActivity?.toLowerCase() || "";
      return (
        investmentAmount.includes(searchLower) ||
        requestDate.includes(searchLower) ||
        provider.includes(searchLower) ||
        product.includes(searchLower) ||
        activity.includes(searchLower)
      );
    });
  }, [props.fixedIncomeRequests, fixedIncomeSearch]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.success("Copied to clipboard");
      })
      .catch(() => {
        message.error("Failed to copy text");
      });
  };

  const renderCopyableValue = (value: string) => {
    return (
      <div
        onClick={() => value && copyToClipboard(value)}
        style={{
          cursor: "pointer",
          padding: "6px 10px",
          margin: "-6px -10px",
        }}
      >
        {value || "n/a"}
      </div>
    );
  };

  return (
    <Fragment>
      <Descriptions
        title={
          <Row style={{ display: "flex", alignItems: "center", gap: 4 }}>
           
            <EntitledSection
              entitlement={entitlements.RedemptionRequests.create}
            >
              <Button
                className="base-button"
                onClick={() =>
                  props.createInteractionButtonRef?.current?.click()
                }
              >
                Create Interaction
              </Button>
            </EntitledSection>
            <Dropdown
              overlay={
                <Menu>
                  {props.customer.isGhanaCardVerified && (
                    <Menu.Item
                      key="loan"
                      onClick={props.toggleDrawer("loanRequest")}
                    >
                      Loans
                    </Menu.Item>
                  )}
                  <Menu.Item
                    key="fixed-income"
                    onClick={props.toggleDrawer("fixedIncomeRequest")}
                  >
                    Fixed Income
                  </Menu.Item>
                  <Menu.Item
                    key="redemption"
                    onClick={props.toggleDrawer("redemptionRequests")}
                  >
                    Redemptions
                  </Menu.Item>
                  <Menu.Item
                    key="transfer"
                    onClick={props.toggleDrawer("transferRequests")}
                  >
                    Transfers
                  </Menu.Item>
                  <Menu.Divider />
                </Menu>
              }
              trigger={["click"]}
              getPopupContainer={(trigger) => trigger.parentElement!}
            >
              <Button className="base-button">
                Requests <DownOutlined />
              </Button>
            </Dropdown>
            <Button
              className="base-button"
              onClick={props.toggleDrawer("selfServiceActivity")}
            >
              Self Service Activities
            </Button>
            <Button
              className="base-button"
              onClick={props.toggleDrawer("notifierRecords")}
            >
              Notifications
            </Button>
            <EntitledSection entitlement={entitlements.Customers.changelogList}>
              <Button
                className="base-button"
                onClick={props.toggleDrawer("audits")}
              >
                Audit Logs
              </Button>
              <Button
                className="base-button"
                onClick={props.toggleDrawer("syncLogs")}
              >
                Sync Logs
              </Button>
              <Button
                style={{
                  marginLeft: "5px",
                }}
                className="base-button"
                onClick={() => (props as any).syncCustomerManually?.()}
              >
                Sync Customer
              </Button>
            </EntitledSection>

            <EntitledSection
              entitlement={entitlements.CustomerSubscriptions.create}
            >
              <Button
                className="base-button"
                onClick={props.toggleDrawer("productSubscription")}
              >
                Add Product
              </Button>
            </EntitledSection>
            {props?.extra}
          </Row>
        }
      />
      <AntdCard
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span>Customer</span>
            {props.customer.tags.length > 0 && (
              <TagsSection
                compact
                style={{ margin: 0 }}
                tags={props.customer.tags}
              />
            )}
            {!!props.customer.exclusionListTags?.length && (
              <TagsSection
                compact
                color="purple"
                style={{ margin: 0 }}
                tags={props.customer.exclusionListTags}
              />
            )}
            <span style={{ marginLeft: "auto" }}>{props.quickEditButton}</span>
          </div>
        }
      >
        <Descriptions>
          <Descriptions.Item label={<b>Petra ID</b>} key="Petra ID">
            {renderCopyableValue(props.customer.petraId)}
          </Descriptions.Item>

          <Descriptions.Item label={<b>SSNIT Number</b>} key="ssnitNumber">
            {renderCopyableValue(props.customer.ssnitNumber)}
          </Descriptions.Item>

          <Descriptions.Item label={<b>Date of Birth</b>} key="dateOfBirth">
            {props.customer.dateOfBirth ? (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {renderCopyableValue(props.customer.dateOfBirth)}
                {props.customer.isDobVerified && (
                  <CheckCircleTwoTone twoToneColor="#52c41a" title="Verified" />
                )}
              </span>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>

          <Descriptions.Item label={<b>Marital Status</b>} key="Marital Status">
            {props.customer.maritalStatus
              ? renderCopyableValue(
                  capitalizeFirstLetter(props.customer.maritalStatus.name),
                )
              : "N/A"}
          </Descriptions.Item>

          <Descriptions.Item label={<b>Country</b>} key="Country">
            {props.customer.country
              ? renderCopyableValue(
                  capitalizeFirstLetter(props.customer.country.name),
                )
              : "N/A"}
          </Descriptions.Item>

          <Descriptions.Item label={<b>Gender</b>} key="Gender">
            {props.customer.gender
              ? renderCopyableValue(
                  capitalizeFirstLetter(props.customer.gender),
                )
              : "N/A"}
          </Descriptions.Item>

          <Descriptions.Item label={<b>ID Type</b>} key="ID Type">
            {props.customer.idType
              ? renderCopyableValue(props.customer.idType.name)
              : "N/A"}
          </Descriptions.Item>

          <Descriptions.Item label={<b>ID Number</b>} key="ID Number">
            {props.customer.photoIdNumber
              ? renderCopyableValue(
                  capitalizeFirstLetter(props.customer.photoIdNumber),
                )
              : "N/A"}
          </Descriptions.Item>

          <Descriptions.Item
            label={<b>Ghana Card ID Number</b>}
            key="Ghana Card ID Number"
          >
            {props.customer.ghanaCardNo ? (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {renderCopyableValue(
                  capitalizeFirstLetter(props.customer.ghanaCardNo),
                )}
                {props.customer.isGhanaCardVerified && (
                  <CheckCircleTwoTone twoToneColor="#52c41a" title="Verified" />
                )}
              </span>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={<b>Ghana Card ID Expiry Date</b>}
            key="idExpiryDate"
          >
            {props.customer.idExpiryDate ? (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {renderCopyableValue(props.customer.idExpiryDate)}
                {moment(props.customer.idExpiryDate).isBefore(
                  moment(),
                  "day",
                ) && (
                  <CloseCircleTwoTone twoToneColor="#f5222d" title="Expired" />
                )}
              </span>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={<b>Ghana Card ID Issue Date</b>}
            key="idIssueDate"
          >
            {props.customer.idIssueDate
              ? renderCopyableValue(props.customer.idIssueDate)
              : "N/A"}
          </Descriptions.Item>

          <Descriptions.Item label={<b>Ghana Card ID</b>} key="Ghana Card ID">
            {props.customer.ghanaCardIdUrl ? (
              <a
                target="_blank"
                href={props.customer.ghanaCardIdUrl}
                rel="noopener noreferrer"
              >
                Click To Open Image
              </a>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={<b>Ghana Card ID (Back)</b>}
            key="Ghana Card ID (Back))"
          >
            {props.customer.ghanaCardIdBackUrl ? (
              <a
                target="_blank"
                href={props.customer.ghanaCardIdBackUrl}
                rel="noopener noreferrer"
              >
                Click To Open Image
              </a>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>

          <Descriptions.Item label={<b>ID</b>} key="ID">
            {props.customer.photoIdUrl ? (
              <a
                target="_blank"
                href={props.customer.photoIdUrl}
                rel="noopener noreferrer"
              >
                Click To Open Image
              </a>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>

          <Descriptions.Item
            label={<b>Customer Signature</b>}
            key="Customer Signature"
          >
            {props.customer.signatureUrl ? (
              <a
                target="_blank"
                href={props.customer.signatureUrl}
                rel="noopener noreferrer"
              >
                Click To Open Image
              </a>
            ) : (
              "N/A"
            )}
          </Descriptions.Item>
        </Descriptions>
      </AntdCard>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "30px",
          marginTop: "30px",
        }}
      >
        <ContactDetailsView
          phones={props.customer.activeCustomerPhones}
          emails={props.customer.customerEmails}
          adresses={props.customer.customerAddresses}
          onUploadCustomerInfoFile={() => props.toggleFileModal(true)}
          onDeleteCustomerInfoFile={props.toggleDeleteConfirmationModal}
          onSetEntityToBeDeleted={props.setEntityToBeDeleted}
          onAddEmail={() => props.openContactModal("email", "add")}
          onEditEmail={(email) => props.openContactModal("email", "edit", email)}
          onDeleteEmail={(email) => props.openDeleteContactModal("email", email.id)}
          onMakePrimaryEmail={props.onMakePrimaryEmail}
          onReactivateEmail={props.onReactivateEmail}
          onAddPhone={() => props.openContactModal("phone", "add")}
          onEditPhone={(phone) => props.openContactModal("phone", "edit", phone)}
          onDeletePhone={(phone) => props.openDeleteContactModal("phone", phone.id)}
          onMakePrimaryPhone={props.onMakePrimaryPhone}
          onReactivatePhone={props.onReactivatePhone}
          onAddAddress={() => props.openContactModal("address", "add")}
          onEditAddress={(address) =>
            props.openContactModal("address", "edit", address)
          }
          onDeleteAddress={(address) =>
            props.openDeleteContactModal("address", address.id)
          }
          onMakePrimaryAddress={props.onMakePrimaryAddress}
          onReactivateAddress={props.onReactivateAddress}
          onAddBackgroundInfo={() => props.openBackgroundInfoModal("add")}
          onEditBackgroundInfo={() => props.openBackgroundInfoModal("edit")}
          onDeleteBackgroundInfo={props.openBackgroundInfoDeleteModal}
          formerEmployees={{ data: [], totalCount: 0 }}
          currentEmployees={{ data: [], totalCount: 0 }}
          customerInfoFileUploads={props.customer.customerInfoFileUploads}
          customer={props.customer}
        />

        <MergerSection
          mergeLogs={props.customer.customerMergeLogs || []}
          mergedTo={props.customer.mergedTo}
          onReverse={props.onReverse}
          onReverseAll={props.onReverseAll}
          onInitiate={props.toggleAccountMergerModal}
          isReversing={props.isReversingMerge}
          isBulkReversing={props.isBulkReversing}
          type="customer"
          canManage={true}
        />

        
        <CustomerAssociationsView
          employers={
            props.customer.employers ??
            props.customer.customerCompanyAssociations.map(
              (association) => association.company,
            )
          }
          formerCompanies={props.customer.formerCompanies}
          companyAssociations={props.customer.customerCompanyAssociations}
          onAddAssociation={() => props.openAssociationsModal()}
          onEditAssociation={(association) =>
            props.openAssociationsModal(association)
          }
          onDeleteAssociation={(association) =>
            props.openAssociationsDeleteModal(association)
          }
          onMarkAssociationAsFormer={(association) =>
            void props.markAssociationAsFormer(association)
          }
          onMarkAssociationAsCurrent={(association) =>
            void props.markAssociationAsCurrent(association)
          }
        />


        <div className="section-card">
          <Spin spinning={props.contributionsLoader}>
            <Descriptions title="Contributions" />
            <Collapse
              accordion
              style={{
                marginBottom: "12px",
              }}
              onChange={props.getProductContribution}
            >
              <Panel header="Total Balance Summary" key="totalBalanceSummary">
                <Table
                  dataSource={props.customerBalanceSummary}
                  columns={customerBalanceSummaryColumn}
                  bordered
                />
              </Panel>
              {hasSubscription &&
                subscriptionsWithContributions.map((item) => {
                  const payoutInfo = props.customer.customerSubscriptions?.find(
                    (subItem) =>
                      subItem.product.id === item.subscription.product.id,
                  )?.customerPayoutAccounts?.[0];
                  const productDuration =
                    props.customer.customerSubscriptions?.find(
                      (subItem) =>
                        subItem.product.id === item.subscription.product.id,
                    )?.productDuration;
                  const productSignOnDate = item.subscription.createdAt
                    ? moment(
                        item.subscription?.createdAt,
                        "YYYY-MM-DD HH:mm:ss",
                      ).format("DD/MM/YYYY")
                    : "n/a";
                  const firstContributionDate = item.subscription
                    .updateComputedFirstContributionDate
                    ? moment(
                        item.subscription?.updateComputedFirstContributionDate,
                        "YYYY-MM-DD HH:mm:ss",
                      ).format("DD/MM/YYYY")
                    : "n/a";
                  return (
                    <Panel
                      header={
                        <span>
                          {item.subscription.product.name}
                          {item.subscription.product?.isActive === false && (
                            <Tag color="red" style={{ marginLeft: 8 }}>
                              Phased Out
                            </Tag>
                          )}
                        </span>
                      }
                      key={item.subscription.product.fundCode as string}
                    >
                      <div
                        style={{
                          display: "flex",
                          columnGap: 8,
                          marginBottom: 12,
                        }}
                      >
                        <Button
                          className="base-button"
                          id="generate-statement-btn"
                          onClick={() => props.setContribution(item)}
                        >
                          Generate Statement
                        </Button>
                        <Button
                          className="base-button"
                          id="view-liens-btn"
                          onClick={(): void => {
                            props.toggleLienSection(
                              item.subscription.product.fundCode
                                ? item.subscription.product.fundCode
                                : "",
                            );
                          }}
                        >
                          View Liens
                        </Button>
                        <Button
                          className="base-button"
                          id="view-vans-btn"
                          onClick={(): void => {
                            props.toggleVansSection(
                              item.subscription.customerVan,
                              item.subscription.product?.name,
                            );
                          }}
                        >
                          View Vans
                        </Button>
                        <Button
                          className="base-button"
                          id="view-allocation-breakdown-btn"
                          onClick={() =>
                            props.toggleAllocationDetailsModal(
                              item.subscription,
                            )
                          }
                        >
                          Check Balance
                        </Button>
                        {item.subscription.product?.isActive !== false && (
                          <Button
                            className="base-button"
                            id="view-allocation-breakdown-btn"
                            onClick={() =>
                              props.openSubscriptionEditModal(item.subscription)
                            }
                          >
                            Edit
                          </Button>
                        )}
                        <EntitledSection
                          entitlement={
                            entitlements.CustomerSubscriptions.delete
                          }
                        >
                          <Button
                            danger
                            id="view-vans-btn"
                            onClick={(): void => {
                              props.toggleConfirmProductDelete(
                                item.subscription.id,
                              );
                            }}
                            style={{marginLeft: "auto"}}
                          >
                            Delete Product
                          </Button>
                        </EntitledSection>
                      </div>
                      <Row>
                        <Col span={24}>
                          <Descriptions>
                            <DescriptionsItem label="Balance">
                              {formatter.format(item.scheme.balance)}
                            </DescriptionsItem>
                            <DescriptionsItem label="Redeemable Balance">
                              {formatter.format(item.scheme.redeemableBalance)}
                            </DescriptionsItem>
                            <DescriptionsItem label="Total Contributions">
                              {formatter.format(item.scheme.totalContributions)}
                            </DescriptionsItem>
                          </Descriptions>
                        </Col>
                      </Row>
                      <Row>
                        <Col span={24}>
                          <Descriptions>
                            <DescriptionsItem label="Total Redemptions">
                              {formatter.format(item.scheme.redemptions)}
                            </DescriptionsItem>
                            <DescriptionsItem label="Product Duration">
                              {productDuration ?? "n/a"}
                            </DescriptionsItem>
                            <DescriptionsItem label="Product Sign On Date">
                              {productSignOnDate}
                            </DescriptionsItem>
                          </Descriptions>
                        </Col>
                      </Row>
                      <Row>
                        <Col span={24}>
                          <Descriptions>
                            <DescriptionsItem label="Bank">
                              {payoutInfo?.bankBranch?.name ?? "n/a"}
                            </DescriptionsItem>
                            <DescriptionsItem label="Bank Account Name">
                              {payoutInfo ? (
                                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  {payoutInfo.name}
                                  {payoutInfo.isPayoutAccountVerified && <VerifiedMark />}
                                </span>
                              ) : "n/a"}
                            </DescriptionsItem>
                            <DescriptionsItem label="Bank Account Number">
                              {payoutInfo?.accountNumber ?? "n/a"}
                            </DescriptionsItem>
                          </Descriptions>
                        </Col>
                      </Row>
                      <Row>
                        <Col span={24}>
                          <Descriptions>
                            <DescriptionsItem label="First Contribution Date">
                              {firstContributionDate}
                            </DescriptionsItem>
                            {item.subscription
                              .proofOfContributionDocumentUrl && (
                              <DescriptionsItem label="Proof Of First Contribution Document">
                                <a
                                  href={
                                    item.subscription
                                      .proofOfContributionDocumentUrl
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Proof Of Contribution Document
                                </a>
                              </DescriptionsItem>
                            )}
                            {item.subscription
                              .firstSchemeContributionDate && (
                              <DescriptionsItem label="First Scheme Contribution Date">
                                {moment(
                                  item.subscription.firstSchemeContributionDate,
                                  "YYYY-MM-DD",
                                ).format("DD/MM/YYYY")}
                              </DescriptionsItem>
                            )}
                            <DescriptionsItem label="Sales Person">
                              {item.subscription.salesPerson?.name ?? "N/A"}
                            </DescriptionsItem>
                          </Descriptions>
                        </Col>
                      </Row>       
                        <Row>
                          {item.subscription.brokerageProductId && (
                            <Col span={24}>
                              <Descriptions>
                                <DescriptionsItem label="Brokerage Product ID">
                                  {item.subscription.brokerageProductId}
                                </DescriptionsItem>
                              </Descriptions>
                            </Col>
                          )}
                        </Row>
                      {item.subscription.customerSubscriptionTags.length >
                        0 && (
                        <Row
                          style={{ marginTop: "10px", marginBottom: "10px" }}
                        >
                          <TagsSection
                            tags={item.subscription.customerSubscriptionTags.map(
                              (subscriptionTag) => subscriptionTag.tag,
                            )}
                          />
                        </Row>
                      )}

                      <Divider />
                      {ContributionsTableFactory(
                        item.scheme.contributions,
                        generateColumns(item.scheme),
                      )}
                    </Panel>
                  );
                })}
            </Collapse>
          </Spin>
        </div>

        <PaymentAccountsView
          payoutAccounts={props.customer.customerPayoutAccounts}
          debitAccounts={props.customer.customerDebitAccounts}
          onAddPayoutAccount={() => props.openPaymentAccountsModal()}
          onAddDebitAccount={() => props.openPaymentAccountsModal(undefined, "debit")}
          onEditPayoutAccount={(account) =>
            props.openPaymentAccountsModal(account, "payout")
          }
          onDeletePayoutAccount={(account) =>
            props.openPaymentAccountsDeleteModal(account, "payout")
          }
          onEditDebitAccount={(account) =>
            props.openPaymentAccountsModal(account, "debit")
          }
          onDeleteDebitAccount={(account) =>
            props.openPaymentAccountsDeleteModal(account, "debit")
          }
        />

        <div className="section-card">
          <Descriptions title={<div>Requests</div>} />
          <Collapse
            style={{
              marginBottom: "12px",
            }}
            onChange={props.handlePanelChange}
          >
            <Panel key="loanRequests" header="Loan Requests">
              <Spin spinning={props.isLoanRequestsLoading}>
                <Input.Search
                  placeholder="Search by loan amount, request date, status"
                  allowClear
                  style={{ width: "50%", marginBottom: 16 }}
                  value={loanRequestSearch}
                  onChange={(e) => setLoanRequestSearch(e.target.value)}
                />
                <Table
                  dataSource={filteredLoanRequests}
                  columns={loanColumns}
                  bordered
                />
              </Spin>
            </Panel>

            <Panel key="fixedIncomeRequests" header="Fixed Income Requests">
              <Spin spinning={props.isFixedIncomeRequestsLoading}>
                <Input.Search
                  placeholder="Search by income amount, request date, provider, product"
                  allowClear
                  style={{ width: "50%", marginBottom: 16 }}
                  value={fixedIncomeSearch}
                  onChange={(e) => setFixedIncomeSearch(e.target.value)}
                />
                <Table
                  dataSource={filteredFixedIncomeRequests}
                  columns={fixedIncomeColumns}
                  bordered
                />
              </Spin>
            </Panel>

            <Panel key="redemptionRequests" header="Redemption Requests">
              <Spin spinning={props.isRedemptionLoading}>
                <Input.Search
                  placeholder="Search by scheme name, request date"
                  allowClear
                  style={{ width: "50%", marginBottom: 16 }}
                  value={redemptionSearch}
                  onChange={(e) => setRedemptionSearch(e.target.value)}
                />
                {RedemptionsRequestsTableFactory(filteredRedemptionRequests)}
              </Spin>
            </Panel>

            <Panel key="transferRequests" header="Transfer Requests">
              <Spin spinning={props.isTransferLoading}>
                <Table
                  dataSource={props.transferRequests}
                  columns={transferRequestsColumns}
                  bordered
                />
              </Spin>
            </Panel>

            <Panel key="customerTransactions" header="Online Transactions">
              <Spin spinning={props.isTransactionsLoading}>
                <Table
                  dataSource={props.customerTransactions}
                  columns={onlineTransactionColumns}
                  bordered
                />
              </Spin>
            </Panel>
          </Collapse>
        </div>

        <CustomerBeneficiariesView
          subscriptions={props.customer.customerSubscriptions}
          onAddBeneficiary={() => props.openBeneficiariesModal()}
          onEditBeneficiary={(beneficiary) =>
            props.openBeneficiariesModal(beneficiary)
          }
          onDeleteBeneficiary={(beneficiary) =>
            props.openBeneficiariesDeleteModal(beneficiary)
          }
        />

        <div className="section-card">
          <Descriptions title={<div>Statements</div>}></Descriptions>

          <Collapse
            style={{
              marginBottom: "12px",
            }}
            accordion={true}
            onChange={props.handlePanelChange}
          >
            <Panel key="customerStatements" header="Statements">
              <Table
                bordered={true}
                columns={getCustomerStatementTableColumns()}
                dataSource={props.customerStatements}
                pagination={{
                  defaultPageSize: 10,
                  total: props.customerStatements.length,
                }}
              />
            </Panel>
          </Collapse>
        </div>

        <div className="section-card">
          <Descriptions title={<div>Phoenix Tickets</div>}></Descriptions>

          <Collapse onChange={props.handlePanelChange}>
            <Panel key="phoenixTickets" header="Phoenix Tickets">
              <Table
                dataSource={props.phoenixTickets}
                columns={ticketColumns}
                bordered
              />
            </Panel>
          </Collapse>
        </div>

        <div className="section-card">
          <Descriptions title={<div>Product Interactions</div>}></Descriptions>

          <Collapse>
            {props.customer.opportunities &&
              props.customer.opportunities.map(
                (opportunity: Opportunity): JSX.Element => {
                  return (
                    <Panel
                      header={
                        <span>
                          {opportunity.product && opportunity.product.name}
                        </span>
                      }
                      key={opportunity.id as string}
                    >
                      <InteractionsSection
                        reference={props.interactionReference}
                        createInteraction={props.createInteraction}
                        interactions={opportunity.opportunityInteractions}
                        showForm={false}
                      />
                    </Panel>
                  );
                },
              )}
          </Collapse>
        </div>
      </div>
    </Fragment>
  );
};

export default class ViewCustomer extends React.Component<Props, State> {
  hasShownSelfServiceWarning = false;
  reference: RefObject<FormInstance>;
  accountMergerRef: RefObject<FormInstance>;
  noteReference: RefObject<FormInstance>;
  ticketReference: RefObject<FormInstance>;
  lienReference: RefObject<FormInstance>;
  statementReference: RefObject<FormInstance>;
  subscriptionEditReference: RefObject<FormInstance>;
  contributionReference: RefObject<FormInstance>;
  quickEditCustomerReference: RefObject<FormInstance>;
  contactEmailReference: RefObject<FormInstance>;
  contactPhoneReference: RefObject<FormInstance>;
  contactAddressReference: RefObject<FormInstance>;
  backgroundInfoReference: RefObject<FormInstance>;
  docsReference: RefObject<FormInstance>;
  beneficiariesModalRef: RefObject<CustomerOnboardingDetails>;
  paymentAccountsModalRef: RefObject<CustomerOnboardingDetails>;
  associationsModalRef: RefObject<CustomerOnboardingDetails>;
  createInteractionButtonRef = React.createRef<HTMLButtonElement>();

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isEditingInteraction: false,
      interactionsChangeLogs: [],
      activeDrawerContent: "",
      isMerging: false,
      selectedCustomers: [],
      showDrawer: false,
      isSearchingForCustomer: false,
      customers: [],
      customerSearchQuery: "",
      showAccountMergerModal: false,
      showReverseMergeModal: false,
      isTicketLoading: false,
      isFiltering: false,
      filterBy: "",
      showChangeLog: false,
      filteredChangelogs: [],
      filteredSelfServiceActivities: [],
      isSelfActivityFiltering: false,
      changeLogs: [],
      interactions: [],
      countries: [],
      customer: undefined,
      showQuickEditModal: false,
      isQuickEditSaving: false,
      titles: [],
      maritalStatuses: [],
      isQuickEditVerified: false,
      signatureUrl: "",
      ghanaCardIdUrl: "",
      ghanaCardIdBackUrl: "",
      photoIdUrl: "",
      verificationProofUrl: "",
      showContactModal: false,
      contactModalType: null,
      contactModalMode: "add",
      contactModalLoading: false,
      selectedEmail: null,
      selectedPhone: null,
      selectedAddress: null,
      addressType: AssociatedEntityConstants.PhysicalAddressCode,
      showContactDeleteModal: false,
      deleteContactType: null,
      deleteContactId: null,
      showBackgroundInfoModal: false,
      backgroundInfoMode: "add",
      isBackgroundInfoSaving: false,
      showBackgroundInfoDeleteModal: false,
      fatherDeceased: false,
      motherDeceased: false,
      showBeneficiariesModal: false,
      showPaymentAccountsModal: false,
      showAssociationsModal: false,
      paymentAccountsActiveKey: undefined,
      beneficiariesActiveKey: undefined,
      associationsActiveKey: undefined,
      showStandaloneDeleteModal: false,
      deleteTargetType: null,
      deleteTargetId: null,
      financialInstitutions: [],
      branchMap: {},
      companies: [],
      isLoading: false,
      contributionsLoader: false,
      id: this.props.match.params.id,
      isReversingMerge: false,
      isBulkReversingMerge: false,
      customerInteractions: [],
      currentFundCode: "",
      showStatementModal: false,
      isStatementLoading: false,
      canCreateTicket: false,
      generatedStatementUrl: undefined,
      attachmentIds: [],
      attachments: [],
      tickets: [],
      customerBalanceSummary: [],
      showInteractionModal: false,
      extendedScheme: null,
      hasLoadedContributions: false,
      currentCustomerVan: undefined,
      currentCustomerVanIsSavingsBooster: false,
      customerTransactions: [],
      activitiesCount: 0,
      activitiesPage: 1,
      isInteractionLoading: false,
      showCancelModal: false,
      currentCustomerToProcess: 0,
      ussdAccount: undefined,
      showConfirmCreateModal: false,
      showConfirmUpdateModal: false,
      phoenixTickets: [],
      showConfirmProductDelete: false,
      subscriptionIdToDelete: undefined,
      showSubscriptionEditModal: false,
      subscriptionToEdit: undefined,
      isSavingSubscriptionEdits: false,
      salesPersons: [],
      salesPersonSearchTerm: "",
      customerStatements: [],
      disableCreateInteractionButton: false,
      showFirstContributionModal: false,
      hasNewProofUpload: false,
      lienAttachmentUrl: "",
      customerInteractionAttachments: [],
      redemptionRequests: [],
      transferRequests: [],
      isRedemptionLoading: false,
      isTransactionsLoading: false,
      isTransferLoading: false,
      loanRequests: [],
      isLoanRequestsLoading: false,
      syncLogs: [],
      isSyncLogsLoading: false,
      fixedIncomeRequests: [],
      isFixedIncomeRequestsLoading: false,
      showAllocationDetailsModal: false,
      currentSubscriptionForAllocation: null,
      allCustomerTags: [],
      isLoadingCustomerTags: false,
      productTags: [],
      showAddDocumentModal: false,
      showDeleteConfirmationModal: false,
      modalTarget: "",
      idToBeDeleted: null,
      showDelayUpdateModal: false,
      isDelayTemplatesLoading: false,
      isDelayPreviewLoading: false,
      isSendingDelayUpdate: false,
      delayNotificationTemplates: [],
      selectedDelayTemplateId: undefined,
      delayUpdateComment: "",
      delayNotificationPreview: null,
    };

    this.reference = React.createRef<FormInstance>();
    this.accountMergerRef = React.createRef<FormInstance>();
    this.noteReference = React.createRef<FormInstance>();
    this.ticketReference = React.createRef<FormInstance>();
    this.lienReference = React.createRef<FormInstance>();
    this.statementReference = React.createRef<FormInstance>();
    this.subscriptionEditReference = React.createRef<FormInstance>();
    this.contributionReference = React.createRef<FormInstance>();
    this.quickEditCustomerReference = React.createRef<FormInstance>();
    this.contactEmailReference = React.createRef<FormInstance>();
    this.contactPhoneReference = React.createRef<FormInstance>();
    this.contactAddressReference = React.createRef<FormInstance>();
    this.backgroundInfoReference = React.createRef<FormInstance>();
    this.docsReference = React.createRef<FormInstance>();
    this.beneficiariesModalRef = React.createRef<CustomerOnboardingDetails>();
    this.paymentAccountsModalRef = React.createRef<CustomerOnboardingDetails>();
    this.associationsModalRef = React.createRef<CustomerOnboardingDetails>();
  }

  toggleModal = (): void => {
    this.setState({
      showCancelModal: !this.state.showCancelModal,
    });
  };

  fetchCustomerTags = async (): Promise<void> => {
    const { id } = this.state;
    if (!id) return;
    this.setState({ isLoadingCustomerTags: true });
    try {
      interface Response {
        data: {
          customer: {
            allCustomerTags: import("../helpers/shared-interfaces").TagRecord[];
          };
        };
      }
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_CUSTOMER_TAGS,
        variables: { customerId: id },
      });
      this.setState({ allCustomerTags: data.customer.allCustomerTags ?? [] });
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoadingCustomerTags: false });
    }
  };

  fetchProductTags = async (): Promise<void> => {
    interface Response {
      data: {
        tags: SelectOptionsInterface[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TAGS,
        variables: { forProduct: true },
      });

      this.setState({
        productTags: (data.tags || []).filter(
          (tag) => !isAutomaticTag(tag.name),
        ),
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  assignCustomerTags = async (tagIds: string[]): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;
    try {
      const response = await request({
        query: ASSIGN_TAGS_TO_CUSTOMER,
        variables: { customerId: customer.id, tagIds },
      });
      const { err } = responseHandler<{
        assignTagsToCustomer: {
          customer: { tags: SelectOptionsInterface[] };
          errors: { property: string; description: string }[];
        };
      }>(response, "assignTagsToCustomer");
      if (err) {
        message.error(`Failed to update tags - ${generateDisplayErrorMessage(err)}`);
        return;
      }
      await this.fetchCustomerTags();
    } catch (error) {
      message.error(`Failed to update tags - ${generateDisplayErrorMessage(error)}`);
    }
  };

  syncCustomerManually = async (): Promise<void> => {
    interface Response {
      data: {
        syncCustomerManually: {
          result: boolean;
        };
      };
    }
    const { id } = this.state;
    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<Response> = await request({
        query: SYNC_CUSTOMER_MANUALLY,
        variables: { customerId: id },
      });
      const { err } = responseHandler<Response>(
        response,
        "syncCustomerManually",
      );
      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else if (response.data.data.syncCustomerManually.result) {
        message.success("Sync started successfully");
      } else {
        message.error(`Failed to start sync - ${generateDisplayErrorMessage(err)}`);
      }
    } catch (error) {
      message.error(`Failed to start sync - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  toggleInteractionEdit = (): void => {
    this.setState({ isEditingInteraction: !this.state.isEditingInteraction });
  };

  toggleTicketCreate = (): void => {
    this.setState({ canCreateTicket: !this.state.canCreateTicket });
  };

  toggleConfirmProductDelete = (
    subscriptionIdToDelete: string | undefined,
  ): void => {
    if (subscriptionIdToDelete !== undefined) {
      this.setState(
        {
          subscriptionIdToDelete,
        },
        () => {
          this.setState({
            showConfirmProductDelete: !this.state.showConfirmProductDelete,
          });
        },
      );
    } else {
      this.setState({
        showConfirmProductDelete: !this.state.showConfirmProductDelete,
      });
    }
  };

  filterLogs = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.persist();
    const input = e.target.value.toLowerCase();

    const filteredChangelogs = this.state.changeLogs.filter((log) =>
      Object.keys(log).some((key) => {
        const value = log[key as keyof Changelog];
        return typeof value === "string" && value.toLowerCase().includes(input);
      }),
    );

    this.setState({
      isFiltering: filteredChangelogs.length > 0,
      filteredChangelogs: filteredChangelogs,
    });
  };

  filterSelfServiceActivities = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    e.persist();
    const input = e.target.value;
    const activities = this.state.customer?.selfServiceActivities || [];
    const filteredData = activities.filter((activity) => {
      return (
        activity.channel.includes(input) ||
        activity.description.includes(input) ||
        activity.category?.includes(input) ||
        activity.subCategory?.includes(input) ||
        activity.updatedAt.includes(input)
      );
    });

    this.setState({
      isSelfActivityFiltering: true,
      filteredSelfServiceActivities: filteredData,
    });
  };

  deleteCustomerSubscription = async (
    subscriptionId: string,
  ): Promise<void> => {
    interface Response {
      result: boolean;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_CUSTOMER_SUBSCRIPTION,
        variables: { id: subscriptionId },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "deleteCustomerSubscription",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation.result) {
        message.success("Product deleted from customer");
        this.toggleConfirmProductDelete(undefined);
        this.getCustomerSubscriptions();
      }
    } catch (error) {
      message.error(`failed to delete product - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  getChangeLog = (): void => {
    interface Response {
      data: {
        customerChangeLogs: Changelog[];
      };
    }
    request({
      query: GET_CUSTOMER_CHANGELOG,
      variables: {
        customerId: this.state.id,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({ changeLogs: data.data.customerChangeLogs });
    });
  };

  getInteractionChangeLog = (customerInteractionId: string): void => {
    interface Response {
      data: {
        customerInteractionChangeLogs: Changelog[];
      };
    }
    request({
      query: GET_CUSTOMER_INTERACTION_CHANGELOG,
      variables: {
        customerInteractionId,
      },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({
        interactionsChangeLogs: data.data.customerInteractionChangeLogs,
      });
    });
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  setNoteRef = (ref: React.RefObject<FormInstance>): void => {
    this.noteReference = ref;
  };

  setTicketRef = (ref: React.RefObject<FormInstance>): void => {
    this.ticketReference = ref;
  };

  setLienRef = (ref: React.RefObject<FormInstance>): void => {
    this.lienReference = ref;
  };

  setStatementRef = (ref: React.RefObject<FormInstance>): void => {
    this.statementReference = ref;
  };

  updateCustomerPaymentInformation = (
    paymentInfo: UpdatePaymentInformationFnProps,
  ): void => {
    const { customer } = this.state;
    if (!customer) return;
    this.setState({ customer: { ...customer, ...paymentInfo } });
  };

  updateCustomerCompanyAssociationsInformation = (
    associations: UpdateCustomerCompanyAssociationFnProps,
  ): void => {
    const { customer } = this.state;
    if (!customer) return;
    this.setState({
      customer: {
        ...customer,
        customerCompanyAssociations: associations.associations,
      },
    });
  };

  updateBeneficiaryInformation = (
    beneficiaryInfo: UpdateBeneficiaryInformationFnProps,
  ): void => {
    const { customer } = this.state;
    if (!customer) return;
    const customerSubscriptions = customer.customerSubscriptions?.map(
      (subscription) => {
        if (beneficiaryInfo.subscriptionId === subscription.id) {
          return {
            ...subscription,
            customerBeneficiaries: beneficiaryInfo.beneficiaries.map((b) => ({
              ...b,
              customerSubscription: subscription,
            })),
          };
        }

        if (
          beneficiaryInfo.previousSubscriptionId &&
          beneficiaryInfo.beneficiaryId &&
          beneficiaryInfo.previousSubscriptionId === subscription.id &&
          beneficiaryInfo.previousSubscriptionId !==
            beneficiaryInfo.subscriptionId
        ) {
          return {
            ...subscription,
            customerBeneficiaries: (
              subscription.customerBeneficiaries || []
            ).filter(
              (beneficiary) => beneficiary.id !== beneficiaryInfo.beneficiaryId,
            ),
          };
        }

        return subscription;
      },
    );
    this.setState({ customer: { ...customer, customerSubscriptions } });
  };

  markEmailAsPrimary = async (email: AssociatedEmail): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;
    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<{
        data: { markCustomerEmailAsPrimary: { customerEmails: AssociatedEmail[] } };
      }> = await request({
        query: MARK_EMAIL_AS_PRIMARY,
        variables: { emailId: email.id },
      });
      const emailList =
        response.data?.data?.markCustomerEmailAsPrimary?.customerEmails;
      if (emailList) {
        this.setState({
          customer: { ...customer, customerEmails: emailList },
        });
      }
    } catch (error) {
      message.error("Failed to set email as primary");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  markPhoneAsPrimary = async (phone: AssociatedPhone): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;
    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<{
        data: {
          markCustomerPhoneAsPrimary: {
            activeCustomerPhones: AssociatedPhone[];
          };
        };
      }> = await request({
        query: MARK_PHONE_AS_PRIMARY,
        variables: { phoneId: phone.id },
      });
      const phoneList =
        response.data?.data?.markCustomerPhoneAsPrimary?.activeCustomerPhones;
      if (phoneList) {
        this.setState({
          customer: { ...customer, activeCustomerPhones: phoneList },
        });
      }
    } catch (error) {
      message.error("Failed to set phone as primary");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  markAddressAsPrimary = async (address: AssociatedAddress): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;
    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<{
        data: { markCustomerAddressAsPrimary: { customerAddresses: AssociatedAddress[] } };
      }> = await request({
        query: MARK_ADDRESS_AS_PRIMARY,
        variables: { addressId: address.id },
      });
      const addressList =
        response.data?.data?.markCustomerAddressAsPrimary?.customerAddresses;
      if (addressList) {
        this.setState({
          customer: { ...customer, customerAddresses: addressList },
        });
      }
    } catch (error) {
      message.error("Failed to set address as primary");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  reactivateEmail = async (email: AssociatedEmail): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;

    this.setState({ contactModalLoading: true });
    try {
      const response: AxiosResponse = await request({
        query: ASSIGN_EMAIL_TO_CUSTOMER,
        variables: { emailId: email.email.id, customerId: customer.id },
      });

      const { err } = responseHandler(response, "assignEmailToCustomer");
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Email reactivated successfully");
      this.getCustomer();
    } catch (error) {
      message.error("Email reactivation failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  reactivatePhone = async (phone: AssociatedPhone): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;

    this.setState({ contactModalLoading: true });
    try {
      const response: AxiosResponse = await request({
        query: ASSIGN_PHONE_TO_CUSTOMER,
        variables: { phoneId: phone.phone.id, customerId: customer.id },
      });

      const { err } = responseHandler(response, "assignPhoneToCustomer");
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Phone number reactivated successfully");
      this.getCustomer();
    } catch (error) {
      message.error("Phone reactivation failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  reactivateAddress = async (address: AssociatedAddress): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;

    this.setState({ contactModalLoading: true });
    try {
      const response: AxiosResponse = await request({
        query: ASSIGN_ADDRESS_TO_CUSTOMER,
        variables: { addressId: address.address.id, customerId: customer.id },
      });

      const { err } = responseHandler(response, "assignAddressToCustomer");
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Address reactivated successfully");
      this.getCustomer();
    } catch (error) {
      message.error("Address reactivation failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  setPercentage = (_percentage: number): void => {
    console.log(_percentage);
  };

  emptyFunction = (): void => {
    console.log("emptyFunction");
  };

  anotherEmptyFunction = (): string => "";

  redirectToAllCustomersPage = (): void => {
    this.props.history.push("/customers");
  };

  getCountries = (): void => {
    interface Response {
      data: {
        countries: Country[];
      };
    }
    request({
      query: GET_COUNTRIES,
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({ countries: data?.data?.countries });
      })
      .catch(() => {});
  };

  getCustomer = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_QUERY,
      variables: { customerId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            customer: Customer;
          };
        }>): void => {
          if (!data?.data) {
            message.error("Failed to fetch customer data");
            this.setState({ isLoading: false });
          } else if (data.data.customer) {
            this.setState({
              customer: data.data.customer,
              isLoading: false,
              ussdAccount: data.data.customer.ussdAccount,
            });

            const hasContributionsOnlyTag =
              data.data.customer.tags?.some((tag: any) => tag.name === "Contributions Only View (Self Service)") ||
              data.data.customer.exclusionListTags?.some((tag: any) => tag.name === "Contributions Only View (Self Service)");

            if (hasContributionsOnlyTag && !this.hasShownSelfServiceWarning) {
              this.hasShownSelfServiceWarning = true;
              Modal.info({
                icon: <EyeInvisibleOutlined />,
                title: "Restricted Self-Service View",
                content: <RestrictedSelfServiceNotice />,
                okText: "Got it",
                okButtonProps: { className: "base-button" },
                className: "restricted-notice-modal",
                width: 560,
              });
            }
          } else {
            this.redirectToAllCustomersPage();
          }
        },
      )
      .catch(() => {
        message.error("An error occurred while fetching customer");
        this.setState({ isLoading: false });
      });
  };

  getCustomerSubscriptions = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_SUBSCRIPTIONS,
      variables: { customerId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            customer: Customer;
          };
        }>): void => {
          this.setState({
            customer: {
              ...this.state.customer,
              ...data.data.customer,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  getCustomerNotifierRecords = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_NOTIFIER_RECORDS_QUERY,
      variables: { customerId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            customer: Customer;
          };
        }>): void => {
          this.setState({
            customer: {
              ...this.state.customer,
              ...data.data.customer,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  getSyncLogs = (): void => {
    interface Response {
      data: {
        getSyncLogs: SyncLog[];
      };
    }
    const { id } = this.state;
    this.setState({ isSyncLogsLoading: true });
    request({
      query: GET_SYNC_LOGS,
      variables: { entityType: "Customer", entityId: id },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({ syncLogs: data.data.getSyncLogs || [] });
      })
      .catch(() => {
        message.error("Failed to fetch sync logs");
      })
      .finally(() => {
        this.setState({ isSyncLogsLoading: false });
      });
  };

  getCustomerStatements = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_STATEMENTS,
      variables: { customerId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            getCustomerStatements: CustomerStatement[];
          };
        }>): void => {
          this.setState({
            customerStatements: data.data?.getCustomerStatements,
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  getCustomerSelfServiceActivities = (
    ussdAccount: UssdAccount | undefined,
  ): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_SELF_ACTIVITIES_QUERY,
      variables: { customerId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            customer: Customer;
          };
        }>): void => {
          this.setState({
            customer: {
              ...this.state.customer,
              selfServiceActivities:
                data.data?.customer?.selfServiceActivities ?? [],
              ussdAccount: ussdAccount ? ussdAccount : this.state.ussdAccount,
            } as Customer,
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  getCustomerOnlineTransactions = (): void => {
    const { id: customerId } = this.state;
    interface Response {
      data: {
        customerTransactions: CustomerTransaction[];
      };
    }
    this.setState({ isTransactionsLoading: true });
    request({
      query: GET_CUSTOMER_ONLINE_TRANSACTIONS,
      variables: { customerId },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          customerTransactions: data.data.customerTransactions,
        });
      })
      .catch(() => {
        message.error("Failed to fetch online transactions");
      })
      .finally(() => {
        this.setState({ isTransactionsLoading: false });
      });
  };

  getCustomerRedemptionRequests = (): void => {
    const { id: customerId } = this.state;
    interface Response {
      data: {
        customer: {
          redemptionRequests: RedemptionsRequest[];
        };
      };
    }
    this.setState({ isRedemptionLoading: true });
    request({
      query: GET_CUSTOMER_REDEMPTION_REQUESTS,
      variables: { customerId },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          redemptionRequests: data.data.customer.redemptionRequests,
        });
      })
      .catch(() => {
        message.error("Failed to fetch redemption requests");
      })
      .finally(() => {
        this.setState({ isRedemptionLoading: false });
      });
  };

  getCustomerTransferRequests = (): void => {
    const { id: customerId } = this.state;
    interface Response {
      data: {
        customer: {
          transferRequests: TransferRequest[];
        };
      };
    }
    this.setState({ isTransferLoading: true });
    request({
      query: GET_CUSTOMER_TRANSFER_REQUESTS,
      variables: { customerId },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          transferRequests: data.data.customer.transferRequests,
        });
      })
      .catch(() => {
        message.error("Failed to fetch transfer requests");
      })
      .finally(() => {
        this.setState({ isTransferLoading: false });
      });
  };

  getCustomerLoanRequests = (): void => {
    const { id: customerId } = this.state;

    interface Response {
      data: {
        customer: {
          loanRequests: LoanRequest[];
        };
      };
    }

    this.setState({ isLoanRequestsLoading: true });

    request({
      query: GET_CUSTOMER_LOAN_REQUESTS,
      variables: { customerId },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          loanRequests: data.data.customer.loanRequests,
        });
      })
      .catch(() => {
        message.error("Failed to fetch loan requests");
      })
      .finally(() => {
        this.setState({ isLoanRequestsLoading: false });
      });
  };

  getCustomerFixedIncomeRequests = (): void => {
    const { id: customerId } = this.state;

    interface Response {
      data: {
        customer: {
          fixedIncomeRequests: FixedIncomeRequest[];
        };
      };
    }

    this.setState({ isFixedIncomeRequestsLoading: true });

    request({
      query: GET_CUSTOMER_FIXED_INCOME_REQUESTS,
      variables: { customerId },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState({
          fixedIncomeRequests: data.data.customer.fixedIncomeRequests,
        });
      })
      .catch(() => {
        message.error("Failed to fetch fixed income requests");
      })
      .finally(() => {
        this.setState({ isFixedIncomeRequestsLoading: false });
      });
  };

  toggleAllocationDetailsModal = (subscription?: Subscription): void => {
    this.setState((prevState) => ({
      showAllocationDetailsModal: !prevState.showAllocationDetailsModal,
      currentSubscriptionForAllocation: subscription || null,
    }));
  };

  interactionsNextPage = async (page: number): Promise<void> => {
    this.setState(
      {
        isInteractionLoading: true,
        activitiesPage: page,
      },
      () => {
        this.getCustomerInteractions();
      },
    );
  };

  getCustomerInteractions = (): void => {
    const { id: customerId, activitiesPage } = this.state;
    this.setState({ isInteractionLoading: true });
    request({
      query: GET_CUSTOMER_INTERACTIONS_QUERY,
      variables: { customerId, page: activitiesPage.toString() },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            interactionsForCustomer: {
              count: number;
              results: Activity[];
            };
          };
        }>): void => {
          const interactions = data.data.interactionsForCustomer.results;

          const allAttachments = interactions.flatMap(
            (interaction) => interaction.customerInteractionAttachments || [],
          );

          this.setState({
            customerInteractions: interactions,
            activitiesCount: data.data.interactionsForCustomer.count,
            notes: interactions?.[0]?.notes,
            customerInteractionAttachments: allAttachments,
          });
        },
      )
      .finally((): void => {
        this.setState({ isInteractionLoading: false });
      });
  };

  openQuickEditModal = async (): Promise<void> => {
    const { customer, titles, maritalStatuses } = this.state;
    if (!customer) return;

    this.setState(
      {
        showQuickEditModal: true,
        isQuickEditVerified: !!customer.verificationProofUrl,
        signatureUrl: customer.signatureUrl || "",
        ghanaCardIdUrl: customer.ghanaCardIdUrl || "",
        ghanaCardIdBackUrl: customer.ghanaCardIdBackUrl || "",
        photoIdUrl: customer.photoIdUrl || "",
        verificationProofUrl: customer.verificationProofUrl || "",
      },
      () => {
        this.quickEditCustomerReference.current?.setFieldsValue({
          titleId: customer.title?.id,
          firstName: customer.firstName,
          middleName: customer.middleName,
          lastName: customer.lastName,
          ssnitNumber: customer.ssnitNumber,
          dateOfBirth: customer.dateOfBirth || undefined,
          maritalStatusId: customer.maritalStatus?.id,
          countryId: customer.country?.id,
          gender: customer.gender,
          photoIdNumber: customer.photoIdNumber,
          ghanaCardNo: customer.ghanaCardNo,
          idIssueDate: customer.idIssueDate,
          idExpiryDate: customer.idExpiryDate,
          isVerified: !!customer.verificationProofUrl,
          verificationProofUrl: customer.verificationProofUrl,
        });
      },
    );

    if (!titles.length) {
      this.getQuickEditTitles();
    }
    if (!maritalStatuses.length) {
      this.getQuickEditMaritalStatuses();
    }
  };

  closeQuickEditModal = (): void => {
    this.setState({ showQuickEditModal: false });
  };

  getQuickEditTitles = async (): Promise<void> => {
    interface Response {
      data: {
        titles: SelectOptionsInterface[];
      };
    }
    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_TITLES,
      });
      this.setState({ titles: data.titles });
    } catch (error) {
      message.error("Failed to get titles");
      Sentry.captureException(error);
    }
  };

  getQuickEditMaritalStatuses = async (): Promise<void> => {
    interface Response {
      data: {
        maritalStatuses: MaritalStatus[];
      };
    }
    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_MARITAL_STATUSES,
      });
      this.setState({ maritalStatuses: data.maritalStatuses });
    } catch (error) {
      message.error("Failed to get marital statuses");
      Sentry.captureException(error);
    }
  };

  handleQuickEditFileUploadChange = (
    field: string,
    combinedInfo: string,
  ): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleQuickEditDelete = (file: UploadFile): void => {
    let newState = {};
    switch (file.name) {
      case "Ghana Card":
        newState = { ghanaCardIdUrl: "", photoIdUrl: "" };
        break;
      case "Ghana Card (Back)":
        newState = { ghanaCardIdBackUrl: "" };
        break;
      case "Customer Signature":
        newState = { signatureUrl: "" };
        break;
      case "Verification Proof":
        newState = { verificationProofUrl: "" };
        break;
      default:
        newState = { photoIdUrl: "" };
    }
    this.setState(newState);
  };

  changeQuickEditIsVerified = (checkedValue: boolean): void => {
    this.setState({ isQuickEditVerified: checkedValue });
  };

  saveQuickEditCustomer = async (): Promise<void> => {
    const { customer, isQuickEditVerified } = this.state;
    if (!customer || !this.quickEditCustomerReference.current) return;

    interface UpdateCustomerResponse {
      customer: Customer;
    }

    const fieldsToBeValidated: string[] = [
      "titleId",
      "firstName",
      "middleName",
      "lastName",
      "ssnitNumber",
      "dateOfBirth",
      "maritalStatusId",
      "countryId",
      "gender",
      "photoIdNumber",
      "photoIdUrl",
      "ghanaCardIdUrl",
      "ghanaCardIdBackUrl",
      "ghanaCardNo",
      "idIssueDate",
      "idExpiryDate",
      "signatureUrl",
      "isVerified",
      "verificationProofUrl",
    ];

    let data;

    if (isQuickEditVerified && !this.state.verificationProofUrl) {
      message.error(
        "Proof of Verification is required when marking as verified",
      );
      this.quickEditCustomerReference.current.setFields([
        {
          name: "verificationProofUrl",
          errors: ["Please attach proof of verification"],
        },
      ]);
      return;
    }

    try {
      data = await this.quickEditCustomerReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isQuickEditSaving: true });

    try {
      const payload = {
        ...data,
        dateOfBirth: data.dateOfBirth && data.dateOfBirth.split("T")[0],
        idIssueDate: data.idIssueDate && data.idIssueDate.split("T")[0],
        idExpiryDate: data.idExpiryDate && data.idExpiryDate.split("T")[0],
        maritalStatusId: data.maritalStatusId,
        customerId: customer.id,
        isVerified:
          this.state.isQuickEditVerified ||
          data.isVerified ||
          !!customer.verificationProofUrl,
        signatureUrl: this.state.signatureUrl,
        ghanaCardIdUrl: this.state.ghanaCardIdUrl,
        ghanaCardIdBackUrl: this.state.ghanaCardIdBackUrl,
        photoIdUrl: this.state.photoIdUrl,
        verificationProofUrl: this.state.verificationProofUrl,
      };

      const response: AxiosResponse<UpdateCustomerResponse> = await request({
        query: UPDATE_CUSTOMER_MUTATION,
        variables: payload,
      });

      const { err, handled } = responseHandler<UpdateCustomerResponse>(
        response,
        "updateCustomer",
      );

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        const resolvedErrors: FieldData[] = [];
        err.forEach((err: APIError): void => {
          resolvedErrors.push({
            name: err.property,
            errors: [
              `${
                mapServerPropToLocalProp[
                  err.property as keyof typeof mapServerPropToLocalProp
                ]
              } ${err.description}`,
            ],
          });
        });
        this.quickEditCustomerReference.current?.setFields(resolvedErrors);
      } else {
        message.success("Customer updated successfully");
        this.closeQuickEditModal();
        this.getCustomer();
      }
    } catch (error) {
      message.error("Failed to update customer");
      Sentry.captureException(error);
    } finally {
      this.setState({ isQuickEditSaving: false });
    }
  };

  openContactModal = (
    type: "email" | "phone" | "address",
    mode: "add" | "edit",
    record?: AssociatedEmail | AssociatedPhone | AssociatedAddress,
  ): void => {
    const nextState: Partial<State> = {
      showContactModal: true,
      contactModalType: type,
      contactModalMode: mode,
      selectedEmail: null,
      selectedPhone: null,
      selectedAddress: null,
    };

    if (type === "email") {
      nextState.selectedEmail = (record as AssociatedEmail) || null;
    } else if (type === "phone") {
      nextState.selectedPhone = (record as AssociatedPhone) || null;
    } else {
      nextState.selectedAddress = (record as AssociatedAddress) || null;
      if (record && (record as AssociatedAddress).address?.isPostalBox) {
        nextState.addressType = AssociatedEntityConstants.PostalAddressCode;
      } else {
        nextState.addressType = AssociatedEntityConstants.PhysicalAddressCode;
      }
    }

    this.resetContactForm(type);
    this.setState(nextState as State);
  };

  resetContactForm = (type: "email" | "phone" | "address"): void => {
    const reference =
      type === "email"
        ? this.contactEmailReference
        : type === "phone"
        ? this.contactPhoneReference
        : this.contactAddressReference;

    reference.current?.resetFields();
  };

  closeContactModal = (): void => {
    this.setState({ showContactModal: false });
  };

  openDeleteContactModal = (
    type: "email" | "phone" | "address",
    id: string,
  ): void => {
    this.setState({
      showContactDeleteModal: true,
      deleteContactType: type,
      deleteContactId: id,
    });
  };

  closeDeleteContactModal = (): void => {
    this.setState({
      showContactDeleteModal: false,
      deleteContactType: null,
      deleteContactId: null,
    });
  };

  handleAddressTypeChange = (value: string): void => {
    this.setState({ addressType: value });
  };

  saveContactEmail = async (): Promise<void> => {
    const { customer, selectedEmail, contactModalMode } = this.state;
    if (!customer || !this.contactEmailReference.current) return;

    const fieldsToBeValidated = ["email"];
    let data;
    try {
      data = await this.contactEmailReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ contactModalLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_EMAIL,
        variables: { address: data.email },
      });

      const { err, handled, mutation } = responseHandler<{
        email: EmailInterface;
        errors: APIError[];
      }>(response, "createEmail");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.contactEmailReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (!mutation.email) {
        message.error("Email Creation Failed");
        return;
      }

      if (contactModalMode === "edit" && selectedEmail) {
        const updateResponse: AxiosResponse = await request({
          query: UPDATE_CUSTOMER_EMAIL,
          variables: {
            customerEmailId: selectedEmail.id,
            customerId: customer.id,
            emailId: mutation.email.id,
          },
        });

        const { err: updateErr } = responseHandler(updateResponse, "updateCustomerEmail");
        if (updateErr) {
          message.error(generateDisplayErrorMessage(updateErr));
          return;
        }
      } else {
        const assignResponse: AxiosResponse = await request({
          query: ASSIGN_EMAIL_TO_CUSTOMER,
          variables: { emailId: mutation.email.id, customerId: customer.id },
        });

        const { err: assignErr } = responseHandler(assignResponse, "assignEmailToCustomer");
        if (assignErr) {
          message.error(generateDisplayErrorMessage(assignErr));
          return;
        }
      }

      message.success(
        contactModalMode === "edit" ? "Email updated successfully" : "Email added successfully",
      );
      this.closeContactModal();
      this.getCustomer();
    } catch (error) {
      message.error("Email action failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  saveContactPhone = async (): Promise<void> => {
    const { customer, selectedPhone, contactModalMode } = this.state;
    if (!customer || !this.contactPhoneReference.current) return;

    const fieldsToBeValidated = ["phone"];
    let data;
    try {
      data = await this.contactPhoneReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ contactModalLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_PHONE,
        variables: { number: data.phone },
      });

      const { err, handled, mutation } = responseHandler<{
        phone: PhoneInterface;
        errors: APIError[];
      }>(response, "createPhone");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.contactPhoneReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (!mutation.phone) {
        message.error("Phone Creation Failed");
        return;
      }

      if (contactModalMode === "edit" && selectedPhone) {
        const updateResponse: AxiosResponse = await request({
          query: UPDATE_CUSTOMER_PHONE,
          variables: {
            customerPhoneId: selectedPhone.id,
            customerId: customer.id,
            phoneId: mutation.phone.id,
          },
        });

        const { err: updateErr } = responseHandler(updateResponse, "updateCustomerPhone");
        if (updateErr) {
          message.error(generateDisplayErrorMessage(updateErr));
          return;
        }
      } else {
        const assignResponse: AxiosResponse = await request({
          query: ASSIGN_PHONE_TO_CUSTOMER,
          variables: { phoneId: mutation.phone.id, customerId: customer.id },
        });

        const { err: assignErr } = responseHandler(assignResponse, "assignPhoneToCustomer");
        if (assignErr) {
          message.error(generateDisplayErrorMessage(assignErr));
          return;
        }
      }

      message.success(
        contactModalMode === "edit" ? "Phone updated successfully" : "Phone added successfully",
      );
      this.closeContactModal();
      this.getCustomer();
    } catch (error) {
      message.error("Phone action failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  saveContactAddress = async (): Promise<void> => {
    const { customer, selectedAddress, contactModalMode, addressType } = this.state;
    if (!customer || !this.contactAddressReference.current) return;

    const fieldsToBeValidated = [
      "streetName",
      "poBox",
      "city",
      "addressCountry",
      "addressType",
      "ghPost",
      "region",
    ];

    let data;
    try {
      data = await this.contactAddressReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ contactModalLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_ADDRESS,
        variables: {
          streetName: data.streetName,
          poBox: data.poBox,
          city: data.city,
          region: data.region,
          addressCountry: data.addressCountry,
          isPostalBox: addressType === AssociatedEntityConstants.PostalAddressCode,
          ghPost: data.ghPost?.length > 0 ? data.ghPost : undefined,
        },
      });

      const { err, handled, mutation } = responseHandler<{
        address: AddressInterface;
        errors: APIError[];
      }>(response, "createAddress");

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.contactAddressReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (!mutation.address) {
        message.error("Address Creation Failed");
        return;
      }

      if (contactModalMode === "edit" && selectedAddress) {
        const updateResponse: AxiosResponse = await request({
          query: UPDATE_CUSTOMER_ADDRESS,
          variables: {
            customerAddressId: selectedAddress.id,
            customerId: customer.id,
            addressId: mutation.address.id,
          },
        });

        const { err: updateErr } = responseHandler(updateResponse, "updateCustomerAddress");
        if (updateErr) {
          message.error(generateDisplayErrorMessage(updateErr));
          return;
        }
      } else {
        const assignResponse: AxiosResponse = await request({
          query: ASSIGN_ADDRESS_TO_CUSTOMER,
          variables: { addressId: mutation.address.id, customerId: customer.id },
        });

        const { err: assignErr } = responseHandler(assignResponse, "assignAddressToCustomer");
        if (assignErr) {
          message.error(generateDisplayErrorMessage(assignErr));
          return;
        }
      }

      message.success(
        contactModalMode === "edit" ? "Address updated successfully" : "Address added successfully",
      );
      this.closeContactModal();
      this.getCustomer();
    } catch (error) {
      message.error("Address action failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  confirmDeleteContact = async (): Promise<void> => {
    const { customer, deleteContactType, deleteContactId } = this.state;
    if (!customer || !deleteContactType || !deleteContactId) return;

    this.setState({ contactModalLoading: true });
    try {
      let query = "";
      let mutationName = "";
      let variables: Record<string, string> = {};

      if (deleteContactType === "email") {
        query = UNASSIGN_EMAIL_FROM_CUSTOMER;
        mutationName = "unassignEmailFromCustomer";
        variables = { customerEmailId: deleteContactId };
      } else if (deleteContactType === "phone") {
        query = UNASSIGN_PHONE_FROM_CUSTOMER;
        mutationName = "unassignPhoneFromCustomer";
        variables = { customerPhoneId: deleteContactId };
      } else {
        query = UNASSIGN_ADDRESS_FROM_CUSTOMER;
        mutationName = "unassignAddressFromCustomer";
        variables = { customerAddressId: deleteContactId };
      }

      const response: AxiosResponse = await request({
        query,
        variables,
      });

      const { err } = responseHandler(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Deleted successfully");
      this.closeDeleteContactModal();
      this.getCustomer();
    } catch (error) {
      message.error("Delete failed");
      Sentry.captureException(error);
    } finally {
      this.setState({ contactModalLoading: false });
    }
  };

  openBackgroundInfoModal = (mode: "add" | "edit"): void => {
    const backgroundInfo = this.state.customer?.customerBackgroundInformation;
    this.setState(
      {
        showBackgroundInfoModal: true,
        backgroundInfoMode: mode,
        fatherDeceased: backgroundInfo?.fatherDeceased || false,
        motherDeceased: backgroundInfo?.motherDeceased || false,
      },
      () => {
        if (backgroundInfo) {
          this.backgroundInfoReference.current?.setFieldsValue({
            fatherName: backgroundInfo.fatherName,
            fatherDeceased: backgroundInfo.fatherDeceased,
            fatherPhoneNumber: backgroundInfo.fatherPhoneNumber,
            motherName: backgroundInfo.motherName,
            motherDeceased: backgroundInfo.motherDeceased,
            motherPhoneNumber: backgroundInfo.motherPhoneNumber,
            previousSsnitNumber: backgroundInfo.previousSsnitNumber,
            hometown: backgroundInfo.hometown,
            nextOfKinName: backgroundInfo.nextOfKinName,
            nextOfKinPhoneNumber: backgroundInfo.nextOfKinPhoneNumber,
            previousEmployer: backgroundInfo.previousEmployer,
          });
        } else {
          this.backgroundInfoReference.current?.resetFields();
        }
      },
    );
  };

  closeBackgroundInfoModal = (): void => {
    this.setState({ showBackgroundInfoModal: false });
  };

  handleBackgroundFatherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ fatherDeceased: checkedValue });
  };

  handleBackgroundMotherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ motherDeceased: checkedValue });
  };

  saveBackgroundInfo = async (): Promise<void> => {
    const { customer, backgroundInfoMode, fatherDeceased, motherDeceased } =
      this.state;
    if (!customer || !this.backgroundInfoReference.current) return;

    const fieldsToBeValidated = [
      "fatherName",
      "fatherDeceased",
      "fatherPhoneNumber",
      "motherName",
      "motherDeceased",
      "motherPhoneNumber",
      "previousSsnitNumber",
      "hometown",
      "nextOfKinName",
      "nextOfKinPhoneNumber",
      "previousEmployer",
    ];

    let data;
    try {
      data = await this.backgroundInfoReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isBackgroundInfoSaving: true });

    try {
      const response: AxiosResponse = await request({
        query:
          backgroundInfoMode === "edit" && customer.customerBackgroundInformation
            ? UPDATE_CUSTOMER_BACKGROUND_INFORMATION
            : CREATE_CUSTOMER_BACKGROUND_INFORMATION,
        variables: {
          ...data,
          customerId: customer.id,
          fatherDeceased: fatherDeceased || false,
          motherDeceased: motherDeceased || false,
        },
      });

      const mutationName =
        backgroundInfoMode === "edit" && customer.customerBackgroundInformation
          ? "updateCustomerBackgroundInformation"
          : "createCustomerBackgroundInformation";

      const { err, mutation } = responseHandler<{
        customerBackgroundInformation: CustomerBackgroundInformation;
        errors: APIError[];
      }>(response, mutationName);

      if (err) {
        this.backgroundInfoReference.current.setFields(
          err.map(mapAPIErrorToFormError),
        );
        return;
      }

      if (mutation?.customerBackgroundInformation) {
        this.setState({
          customer: {
            ...customer,
            customerBackgroundInformation:
              mutation.customerBackgroundInformation,
          },
        });
        message.success("Background info saved successfully");
        this.closeBackgroundInfoModal();
      }
    } catch (error) {
      message.error("Failed to save background info");
      Sentry.captureException(error);
    } finally {
      this.setState({ isBackgroundInfoSaving: false });
    }
  };

  openBackgroundInfoDeleteModal = (): void => {
    this.setState({ showBackgroundInfoDeleteModal: true });
  };

  closeBackgroundInfoDeleteModal = (): void => {
    this.setState({ showBackgroundInfoDeleteModal: false });
  };

  deleteBackgroundInfo = async (): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;

    this.setState({ isBackgroundInfoSaving: true });
    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_BACKGROUND_INFORMATION,
        variables: {
          customerId: customer.id,
          hometown: "",
          previousSsnitNumber: "",
          nextOfKinName: "",
          nextOfKinPhoneNumber: "",
          fatherName: "",
          fatherDeceased: false,
          fatherPhoneNumber: "",
          motherName: "",
          motherDeceased: false,
          motherPhoneNumber: "",
          previousEmployer: "",
        },
      });

      const { err } = responseHandler(response, "updateCustomerBackgroundInformation");
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      this.setState({
        customer: {
          ...customer,
          customerBackgroundInformation: undefined,
        },
      });
      message.success("Background info deleted");
      this.closeBackgroundInfoDeleteModal();
    } catch (error) {
      message.error("Failed to delete background info");
      Sentry.captureException(error);
    } finally {
      this.setState({ isBackgroundInfoSaving: false });
    }
  };

  getFinancialInstitutions = async (): Promise<void> => {
    interface Response {
      data: {
        financialInstitutions: FinancialInstitution[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_FINANCIAL_INSTITUTIONS,
      });

      const financialInstitutions = data.financialInstitutions || [];
      const branchMap: BranchMap = {};
      financialInstitutions.forEach((institution) => {
        branchMap[institution.id] = institution.bankBranches;
      });

      this.setState({ financialInstitutions, branchMap });
    } catch (error) {
      message.error("Failed to get financial institutions");
      Sentry.captureException(error);
    }
  };

  getCompanies = async (): Promise<void> => {
    interface Response {
      data: {
        companies: Company[];
      };
    }

    try {
      const {
        data: { data },
      }: AxiosResponse<Response> = await request({
        query: GET_COMPANIES,
      });
      this.setState({ companies: data.companies || [] });
    } catch (error) {
      message.error("Failed to get companies");
      Sentry.captureException(error);
    }
  };

  openBeneficiariesModal = (beneficiary?: Beneficiary): void => {
    this.setState(
      {
        showBeneficiariesModal: true,
        beneficiariesActiveKey: CustomerOnboardingViews.beneficiary,
      },
      () => {
      if (beneficiary) {
        this.beneficiariesModalRef.current?.setCurrentlyEditing(
          CustomerOnboardingViews.beneficiary,
          beneficiary,
        );
      } else {
        this.beneficiariesModalRef.current?.resetState();
      }
      },
    );
  };

  openBeneficiariesDeleteModal = (beneficiary: Beneficiary): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: "beneficiary",
      deleteTargetId: beneficiary.id,
    });
  };

  closeBeneficiariesModal = (): void => {
    this.beneficiariesModalRef.current?.resetState();
    this.setState({ showBeneficiariesModal: false });
  };

  openPaymentAccountsModal = (
    account?: PaymentAccount,
    type?: "payout" | "debit",
  ): void => {
    if (!this.state.financialInstitutions.length) {
      this.getFinancialInstitutions();
    }
    this.setState(
      {
        showPaymentAccountsModal: true,
        paymentAccountsActiveKey:
          type === "debit"
            ? CustomerOnboardingViews.debit
            : CustomerOnboardingViews.payout,
      },
      () => {
      if (account && type) {
        this.paymentAccountsModalRef.current?.setCurrentlyEditing(
          type === "payout"
            ? CustomerOnboardingViews.payout
            : CustomerOnboardingViews.debit,
          account,
        );
      } else {
        this.paymentAccountsModalRef.current?.resetState();
      }
      },
    );
  };

  openPaymentAccountsDeleteModal = (
    account: PaymentAccount,
    type: "payout" | "debit",
  ): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: type,
      deleteTargetId: account.id,
    });
  };

  closePaymentAccountsModal = (): void => {
    this.paymentAccountsModalRef.current?.resetState();
    this.setState({ showPaymentAccountsModal: false });
  };

  toggleFileModal = (open?: boolean): void => {
    this.setState((prevState) => ({
      showAddDocumentModal:
        open !== undefined ? open : !prevState.showAddDocumentModal,
    }));
  };

  handleDocumentUploadSuccess = (): void => {
    this.getCustomer();
  };

  setEntityToBeDeleted = (modalTarget: string): void => {
    this.setState({ modalTarget });
  };

  toggleDeleteConfirmationModal = (id?: string): void => {
    this.setState((prevState) => ({
      showDeleteConfirmationModal: !prevState.showDeleteConfirmationModal,
      idToBeDeleted: id ?? prevState.idToBeDeleted,
    }));
  };

  deleteFile = async (id: string): Promise<void> => {
    if (!id) return;

    this.setState({ isLoading: true });
    try {
      const response = await request({
        query: DELETE_FILE,
        variables: { id },
      });
      const { mutation } = responseHandler<{ result: boolean; errors: APIError[] }>(
        response,
        "deleteFileUpload",
      );
      if (mutation.result) {
        message.success("File deleted successfully");
        await this.getCustomer();
      } else {
        message.error("Failed to delete file");
      }
    } catch (error) {
      message.error("Failed to delete a file");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
      this.toggleDeleteConfirmationModal();
    }
  };

  deleteSignedApplicationForm = async (): Promise<void> => {
    const customer = this.state.customer;
    const signedForm = customer?.customerInfoFileUploads?.find(
      (file) => file.fileType === FileTypes.signedApplicationForm,
    );
    if (signedForm?.id) {
      await this.deleteFile(signedForm.id);
    }
  };

  deleteDirectDebitAuthorizationForm = async (): Promise<void> => {
    const customer = this.state.customer;
    const directDebitForm = customer?.customerInfoFileUploads?.find(
      (file) => file.fileType === FileTypes.directDebitAuthorizationForm,
    );
    if (directDebitForm?.id) {
      await this.deleteFile(directDebitForm.id);
    }
  };

  deleteCustomerDocument = async (): Promise<void> => {
    const { idToBeDeleted } = this.state;
    if (!idToBeDeleted) return;
    await this.deleteFile(idToBeDeleted);
  };

  confirmDeleteDocument = async (): Promise<void> => {
    const { modalTarget } = this.state;
    if (modalTarget === FileTypes.signedApplicationForm) {
      await this.deleteSignedApplicationForm();
      return;
    }
    if (modalTarget === FileTypes.directDebitAuthorizationForm) {
      await this.deleteDirectDebitAuthorizationForm();
      return;
    }
    await this.deleteCustomerDocument();
  };

  openAssociationsModal = (association?: CustomerCompanyAssociation): void => {
    if (!this.state.companies.length) {
      this.getCompanies();
    }
    this.setState(
      {
        showAssociationsModal: true,
        associationsActiveKey: CustomerOnboardingViews.customerAssociation,
      },
      () => {
      if (association) {
        this.associationsModalRef.current?.setCurrentlyEditing(
          CustomerOnboardingViews.customerAssociation,
          association,
        );
      } else {
        this.associationsModalRef.current?.resetState();
      }
      },
    );
  };

  openAssociationsDeleteModal = (
    association: CustomerCompanyAssociation,
  ): void => {
    this.setState({
      showStandaloneDeleteModal: true,
      deleteTargetType: "association",
      deleteTargetId: association.id,
    });
  };

  setAssociationEmploymentStatus = async (
    association: CustomerCompanyAssociation,
    isEmployee: boolean,
  ): Promise<void> => {
    const { customer } = this.state;
    if (!customer) return;

    const failureMessage = isEmployee
      ? "Failed to mark as current"
      : "Failed to mark as former";

    this.setState({ isLoading: true });

    try {
      interface Response {
        customerCompanyAssociation: CustomerCompanyAssociation;
      }

      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_COMPANY_ASSOCIATION,
        variables: {
          id: association.id,
          customerId: customer.id,
          companyId: association.company.id,
          isEmployee,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerCompanyAssociation",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (mutation?.customerCompanyAssociation) {
        this.getCustomer();
        message.success(
          isEmployee
            ? "Marked as current employer"
            : "Marked as former employer",
        );
      } else {
        message.error(failureMessage);
      }
    } catch (error) {
      message.error(failureMessage);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  markAssociationAsFormer = (
    association: CustomerCompanyAssociation,
  ): Promise<void> => this.setAssociationEmploymentStatus(association, false);

  markAssociationAsCurrent = (
    association: CustomerCompanyAssociation,
  ): Promise<void> => this.setAssociationEmploymentStatus(association, true);

  closeAssociationsModal = (): void => {
    this.associationsModalRef.current?.resetState();
    this.setState({ showAssociationsModal: false });
  };

  closeStandaloneDeleteModal = (): void => {
    this.setState({
      showStandaloneDeleteModal: false,
      deleteTargetType: null,
      deleteTargetId: null,
    });
  };

  confirmStandaloneDelete = async (): Promise<void> => {
    const { deleteTargetType, deleteTargetId } = this.state;
    if (!deleteTargetType || !deleteTargetId) return;

    this.setState({ isLoading: true });
    try {
      let query = "";
      let mutationName = "";
      if (deleteTargetType === "beneficiary") {
        query = DELETE_BENEFICIARY;
        mutationName = "deleteCustomerBeneficiary";
      } else if (deleteTargetType === "association") {
        query = DELETE_CUSTOMER_ASSOCIATION;
        mutationName = "deleteCustomerCompanyAssociation";
      } else if (deleteTargetType === "payout") {
        query = DELETE_PAYOUT_ACCOUNT;
        mutationName = "deleteCustomerPayoutAccount";
      } else {
        query = DELETE_DEBIT_ACCOUNT;
        mutationName = "deleteCustomerDebitAccount";
      }

      const response: AxiosResponse = await request({
        query,
        variables: { id: deleteTargetId },
      });

      const { err, mutation } = responseHandler<any>(response, mutationName);
      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      if (deleteTargetType === "beneficiary") {
        if (mutation?.result) {
          const beneficiaries: Beneficiary[] = mutation.beneficiaries || [];
          this.updateBeneficiaryInformation({
            beneficiaries,
            subscriptionId: beneficiaries[0]?.customerSubscription?.id || "",
          });
        }
      } else if (deleteTargetType === "association") {
        if (mutation?.customerCompanyAssociations) {
          this.updateCustomerCompanyAssociationsInformation({
            associations: mutation.customerCompanyAssociations,
          });
          this.getCustomer();
        }
      } else {
        this.getCustomer();
      }

      message.success("Deleted successfully");
      this.closeStandaloneDeleteModal();
    } catch (error) {
      message.error("Failed to delete");
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  createInteraction = async (): Promise<void> => {
    console.log("Just an empty function for now");
  };

  toggleAccountMergerModal = (): void => {
    const prevState = this.state.showAccountMergerModal;
    this.setState({ showAccountMergerModal: !prevState });
  };

  toggleReverseMergeModal = (): void => {
    const prevState = this.state.showReverseMergeModal;
    this.setState({ showReverseMergeModal: !prevState });
  };

  updateCustomerFirstContribution = async (): Promise<void> => {
    const { proofOfContributionDocumentUrl } = this.state;

    interface Response {
      customerSubscription: Subscription;
    }

    const fieldsToBeValidated: string[] = [
      "firstContributionDate",
      "proofOfContributionDocumentUrl",
    ];

    if (!this.contributionReference.current) return;

    let data;

    try {
      data = await this.contributionReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });
    const { currentContribution } = this.state;

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_FIRST_CONTRIBUTION,
        variables: {
          ...data,
          customerSubscriptionId: currentContribution?.subscription?.id,
          proofOfContributionDocumentUrl,
        },
      });
      this.setState({ isLoading: false });

      const { err } = responseHandler<Response>(
        response,
        "updateCustomerSubscription",
      );

      if (err) {
        message.error(`Failed to Update First Contribution - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({ showFirstContributionModal: false });
        message.success("First Contribution Updated Successfully");
        this.getCustomerSubscriptions();
      }
    } catch (error) {
      Sentry.captureException(error);
      this.setState({ isLoading: false });
      message.error(`Failed to Update First Contribution - ${generateDisplayErrorMessage(error)}`);
    }
  };

  mergeAccounts = async (): Promise<void> => {
    interface Response {
      customer: Customer;
    }

    this.setState({ isMerging: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: MERGE_CUSTOMERS,
        variables: {
          mainId: this.state.customer?.id,
          duplicateId: this.state.selectedCustomers[0],
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "mergeCustomers",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        this.toggleAccountMergerModal();
        message.success(
          "Merge Successful",
          1,
          this.loadResultantCustomerPage(mutation.customer.id),
        );
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isMerging: false });
  };

  reverseMerge = async (duplicateId?: string): Promise<void> => {
    interface Response {
      customer: Customer;
    }

    this.setState({ isReversingMerge: true });
    const { id: customerId } = this.state;

    try {
      const response: AxiosResponse<Response> = await request({
        query: REVERSE_MERGE_CUSTOMERS,
        variables: { mainId: customerId, duplicateId },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "reverseMergeCustomers",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        message.success(
          "Merge Reverse Successful",
          1,
          this.loadResultantCustomerPage(mutation.customer.id),
        );
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isReversingMerge: false });
    }
  };

  reverseAllMerges = async (duplicateIds?: string[]): Promise<void> => {
    interface Response {
      customer: Customer;
    }

    this.setState({ isBulkReversingMerge: true });
    const { id: customerId } = this.state;

    try {
      const response: AxiosResponse<Response> = await request({
        query: BULK_REVERSE_MERGE_CUSTOMERS,
        variables: { mainId: customerId, duplicateIds },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "bulkReverseMergeCustomers",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        message.success(
          "All Merges Reversed Successfully",
          1,
          this.loadResultantCustomerPage(mutation.customer.id),
        );
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isBulkReversingMerge: false });
    }
  };

  updateUssdAccountStatus = async (
    id: string,
    accountStatus: number,
  ): Promise<void> => {
    interface Response {
      ussdAccount: UssdAccount;
      errors: APIError[];
    }

    const mapStatusToType: Record<number, string> = {
      0: "deactivate",
      1: "enable",
      2: "block",
    };

    const statusText = mapStatusToType[accountStatus];
    this.setState({ isLoading: true });
    const selfActivities = this.state.customer?.selfServiceActivities;
    const customer = this.state.customer as Customer;

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_USSD_ACCOUNT_STATUS,
        variables: { id, accountStatus },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateUssdAccount",
      );

      if (err) {
        this.setState({ isLoading: false });
        message.error(`Failed to ${statusText} ussd account`);
        this.logSelfServiceActivity(
          `Account ${statusText}${statusText === "block" ? "ed" : "d"} by ${
            user.name
          }`,
          "Ussd",
          "My Account",
          "Account Status",
        );
        this.getCustomerSelfServiceActivities(this.state.ussdAccount);
      } else {
        this.setState(
          {
            customer: {
              ...customer,
              selfServiceActivities: selfActivities,
            },
            ussdAccount: mutation.ussdAccount,
            isLoading: false,
          },
          () => {
            this.logSelfServiceActivity(
              `Account ${statusText}${statusText === "block" ? "ed" : "d"} by ${
                user?.name
              }`,
              "Ussd",
              "My Account",
              "Account Status",
            );
            this.getCustomerSelfServiceActivities(mutation.ussdAccount);
          },
        );
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  updateUssdAccountPhone = async (): Promise<void> => {
    interface Response {
      ussdAccount: UssdAccount;
      errors: APIError[];
    }
    const selfActivities = this.state.customer?.selfServiceActivities;
    const customer = this.state.customer as Customer;

    this.setState({ isLoading: true });

    if (!this.reference.current) return;

    try {
      const data = await this.reference.current.validateFields(["phone"]);

      if (data.phone) {
        const response: AxiosResponse<Response> = await request({
          query: UPDATE_USSD_ACCOUNT_PHONE,
          variables: {
            ussdAccountId: this?.state?.customer?.ussdAccount?.id,
            phoneNumber: data?.phone,
          },
        });

        const { err, mutation } = responseHandler<Response>(
          response,
          "updateUssdAccountPhone",
        );

        if (err) {
          const notCustomerNumber = err.find(
            (item) => item.property === "phone_number",
          );
          if (notCustomerNumber) {
            message.error("This number is not associated with the customer");
          } else {
            message.error("Failed to update ussd account phone number err");
          }
          this.logSelfServiceActivity(
            `Failed to update account number by ${user.name}`,
            "Ussd",
            "My Account",
            "Account number",
          );
        }

        if (mutation.ussdAccount) {
          this.setState(
            {
              customer: {
                ...customer,
                ussdAccount: mutation.ussdAccount,
                selfServiceActivities: selfActivities,
              },
              ussdAccount: mutation.ussdAccount,
            },
            () => {
              this.logSelfServiceActivity(
                `Account number updated by ${user.name}`,
                "Ussd",
                "My Account",
                "Account number",
              );
              this.getCustomerSelfServiceActivities(undefined);
            },
          );
          message.success("Successfully updated ussd account phone number");
        }
      }
    } catch (error) {
      message.error(`Failed to update ussd account phone number! - ${generateDisplayErrorMessage(error)}`);
      this.logSelfServiceActivity(
        `Failed to update account number by ${user.name}`,
        "Ussd",
        "My Account",
        "Account number",
      );
    }

    this.getCustomerSelfServiceActivities(undefined);

    this.setState({ isLoading: false, showConfirmUpdateModal: false });
  };

  logSelfServiceActivity = async (
    description: string,
    channel: string,
    category: string,
    subCategory: string,
  ): Promise<void> => {
    interface Response {
      ussdAccount: UssdAccount;
      errors: APIError[];
    }
    this.setState({ isLoading: true });

    const customer = this.state.customer as Customer;
    const selfActivities = this.state.customer?.selfServiceActivities;

    try {
      const response: AxiosResponse<Response> = await request({
        query: LOG_SELF_SERVICE_ACTIVITIES,
        variables: {
          petraId: this.state.customer?.petraId,
          description,
          channel,
          category,
          subCategory,
        },
      });
      const { err, mutation } = responseHandler<Response>(
        response,
        "logSelfServiceActivity",
      );

      if (err) {
        this.setState({ isLoading: false });
        message.error(`Failed to log activity - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customer: {
            ...customer,
            ussdAccount: mutation.ussdAccount,
            selfServiceActivities: selfActivities,
          },
          isLoading: false,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  resetUssdAccountPin = async (id: string): Promise<void> => {
    interface Response {
      ussdAccount: UssdAccount;
      errors: APIError[];
    }
    this.setState({ isLoading: true });
    const selfActivities = this.state.customer?.selfServiceActivities;
    const customer = this.state.customer as Customer;

    try {
      const response: AxiosResponse<Response> = await request({
        query: RESET_USSD_ACCOUNT_PIN,
        variables: { id },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "resetUssdAccountPin",
      );

      if (err) {
        this.setState({ isLoading: false });
        message.error(`Failed to reset ussd account pin - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({
          customer: {
            ...customer,
            ussdAccount: mutation.ussdAccount,
            selfServiceActivities: selfActivities,
          },
          isLoading: false,
        });
        message.success("Successfully reset ussd account pin");
        this.logSelfServiceActivity(
          `Account reset by ${user.name}`,
          "Ussd",
          "My Account",
          "PIN Reset",
        );
        this.getCustomerSelfServiceActivities(undefined);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  loadResultantCustomerPage =
    (id: string): (() => void) =>
    () => {
      window.location.assign(`/customers/${id}`);
    };

  getAllSalesPerson(): void {
    const { salesPersonSearchTerm } = this.state;
    request({
      query: GET_SALES_PEOPLE,
      variables: {
        page: 1,
        q: salesPersonSearchTerm,
      },
    }).then(({ data: { data } }: any): void => {
      this.setState({
        salesPersons: data.salesPeople.results,
        isLoading: false,
      });
    });
  }

  triggerSalesPersonDelayedSearch = debounce(async (): Promise<void> => {
    this.getAllSalesPerson();
  }, DEBOUNCE_TIME);

  handleSalesPersonSearchInput = async (
    salesPersonSearchTerm: string,
  ): Promise<void> => {
    this.setState({ salesPersonSearchTerm }, () => {
      this.triggerSalesPersonDelayedSearch();
    });
  };

  handleSalesPersonFocus = (): void => {
    if (
      this.state.salesPersons.length === 0 ||
      this.state.salesPersonSearchTerm !== ""
    ) {
      this.setState({ salesPersonSearchTerm: "" }, () => {
        this.getAllSalesPerson();
      });
    }
  };

  phasedOutProductNameFor = (subscriptionId: string): string | undefined => {
    const subscription = this.state.customer?.customerSubscriptions?.find(
      (candidate) => candidate.id === subscriptionId,
    );

    return subscription?.product?.isActive === false
      ? subscription?.product?.name
      : undefined;
  };

  updateCustomerSubscription = async (
    subscriptionId: string,
    salesPersonId: string,
  ): Promise<void> => {
    const phasedOutProduct = this.phasedOutProductNameFor(subscriptionId);
    if (phasedOutProduct) {
      message.error(`${phasedOutProduct} has been phased out`);
      return;
    }

    interface Response {
      customerSubscription: Subscription;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_SUBSCRIPTION,
        variables: { customerSubscriptionId: subscriptionId, salesPersonId },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerSubscription",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation.customerSubscription) {
        message.success("Sales Person updated");
        this.toggleConfirmProductDelete(undefined);
        this.getCustomerSubscriptions();
      }
    } catch (error) {
      message.error(`failed to add sales person - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  openSubscriptionEditModal = (subscription: Subscription): void => {
    if (!this.state.allCustomerTags.length) {
      this.fetchCustomerTags();
    }
    if (!this.state.productTags.length) {
      this.fetchProductTags();
    }
    if (!this.state.salesPersons.length) {
      this.getAllSalesPerson();
    }
    const latestSubscription = this.state.customer?.customerSubscriptions?.find((sub) => sub.id === subscription.id) || subscription;
    this.subscriptionEditReference.current?.resetFields();
    this.setState({
      showSubscriptionEditModal: true,
      subscriptionToEdit: latestSubscription,
      proofOfContributionDocumentUrl: latestSubscription.proofOfContributionDocumentUrl,
      hasNewProofUpload: false,
    });
  };

  closeSubscriptionEditModal = (): void => {
    this.subscriptionEditReference.current?.resetFields();
    this.setState({
      showSubscriptionEditModal: false,
      subscriptionToEdit: undefined,
      proofOfContributionDocumentUrl: undefined,
      hasNewProofUpload: false,
    });
  };

  assignCustomerSubscriptionTags = async (
    subscriptionId: string,
    tagIds: string[],
  ): Promise<void> => {
    interface Response {
      assignTagsToCustomerSubscription: {
        errors: APIError[];
      };
    }

    const response: AxiosResponse = await request({
      query: ASSIGN_TAGS_TO_CUSTOMER_SUBSCRIPTION,
      variables: { customerSubscriptionId: subscriptionId, tagIds },
    });

    const { err } = responseHandler<Response>(
      response,
      "assignTagsToCustomerSubscription",
    );

    if (err) {
      throw new Error(generateDisplayErrorMessage(err));
    }
  };

  updateCustomerSubscriptionSalesPerson = async (
    subscriptionId: string,
    salesPersonId?: string,
  ): Promise<void> => {
    const phasedOutProduct = this.phasedOutProductNameFor(subscriptionId);
    if (phasedOutProduct) {
      message.error(`${phasedOutProduct} has been phased out`);
      return;
    }

    interface Response {
      customerSubscription: Subscription;
    }

    const variables: Record<string, unknown> = {
      customerSubscriptionId: subscriptionId,
    };
    if (salesPersonId !== undefined) {
      variables.salesPersonId = salesPersonId;
    }

    const response: AxiosResponse = await request({
      query: UPDATE_CUSTOMER_SUBSCRIPTION,
      variables,
    });

    const { err } = responseHandler<Response>(
      response,
      "updateCustomerSubscription",
    );

    if (err) {
      throw new Error(generateDisplayErrorMessage(err));
    }
  };

  updateCustomerSubscriptionFirstContribution = async (
    subscriptionId: string,
    firstContributionDate?: Moment,
    proofOfContributionDocumentUrl?: string,
  ): Promise<void> => {
    const phasedOutProduct = this.phasedOutProductNameFor(subscriptionId);
    if (phasedOutProduct) {
      message.error(`${phasedOutProduct} has been phased out`);
      return;
    }

    interface Response {
      customerSubscription: Subscription;
    }

    if (!firstContributionDate && !proofOfContributionDocumentUrl) return;

    const response: AxiosResponse = await request({
      query: UPDATE_CUSTOMER_FIRST_CONTRIBUTION,
      variables: {
        customerSubscriptionId: subscriptionId,
        firstContributionDate,
        proofOfContributionDocumentUrl,
      },
    });

    const { err } = responseHandler<Response>(
      response,
      "updateCustomerSubscription",
    );

    if (err) {
      throw new Error(generateDisplayErrorMessage(err));
    }
  };

  saveSubscriptionEdits = async (): Promise<void> => {
    const subscription = this.state.subscriptionToEdit;
    if (!subscription || !this.subscriptionEditReference.current) return;

    const fieldsToBeValidated = [
      "salesPersonId",
      "subscriptionTagIds",
      "firstContributionDate",
      "proofOfContributionDocumentUrl",
    ];
    let data: {
      salesPersonId?: string;
      subscriptionTagIds?: string[];
      firstContributionDate?: Moment;
      proofOfContributionDocumentUrl?: string;
    };
    try {
      data = await this.subscriptionEditReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const isTierThree = isTierThreeProduct(subscription.product);
    const originalFirstContributionDate =
      subscription.computedFirstContributionDate;
    const firstContributionDateChanged =
      isTierThree &&
      !!data.firstContributionDate &&
      (!originalFirstContributionDate ||
        !data.firstContributionDate.isSame(
          moment(originalFirstContributionDate, "YYYY/MM/DD"),
          "day",
        ));

    if (firstContributionDateChanged && !this.state.hasNewProofUpload) {
      message.error(
        "Upload a new proof of contribution to change the first contribution date",
      );
      return;
    }

    this.setState({ isSavingSubscriptionEdits: true });

    try {
      if (data.salesPersonId !== undefined) {
        await this.updateCustomerSubscriptionSalesPerson(
          subscription.id,
          data.salesPersonId,
        );
      }

      if (data.subscriptionTagIds !== undefined) {
        await this.assignCustomerSubscriptionTags(
          subscription.id,
          data.subscriptionTagIds,
        );
      }

      if (firstContributionDateChanged || this.state.hasNewProofUpload) {
        await this.updateCustomerSubscriptionFirstContribution(
          subscription.id,
          data.firstContributionDate,
          this.state.proofOfContributionDocumentUrl,
        );
      }

      message.success("Subscription updated");
       await this.getCustomerSubscriptions();
       this.closeSubscriptionEditModal();
    } catch (error) {
      message.error("Failed to update subscription");
      Sentry.captureException(error);
    } finally {
      this.setState({ isSavingSubscriptionEdits: false });
    }
  };

  handleSearchInput = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (event.target.value.length > 0) {
      this.setState({
        customerSearchQuery: event.target.value,
      });
      await this.triggerDelayedSearch();
    } else {
      this.setState({
        customerSearchQuery: event.target.value,
        customers: [],
      });
    }
  };

  triggerDelayedSearch = debounce(async (): Promise<void> => {
    return await this.triggerImmediateSearch();
  }, DEBOUNCE_TIME);

  triggerImmediateSearch = (): void => {
    const { customerSearchQuery } = this.state;

    interface Response {
      data: {
        findCustomerByPetraId: Customer;
      };
    }

    this.setState({ isSearchingForCustomer: true });

    request({
      query: SEARCH_CUSTOMER_BY_PETRAID,
      variables: { q: customerSearchQuery },
    })
      .then((response: AxiosResponse<Response>): void => {
        const customers = response.data.data.findCustomerByPetraId
          ? [response.data.data.findCustomerByPetraId]
          : [];

        this.setState({
          customers,
          selectedCustomers: [],
        });
      })
      .finally((): void => {
        this.setState({ isSearchingForCustomer: false });
      });
  };

  handleCheckboxGroupChange = (
    selectedCustomers: CheckboxValueType[],
  ): void => {
    this.setState({ selectedCustomers: selectedCustomers.map(String) });
  };

  handleFileUploadChange = (field: string, combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState((prevState) => ({
      ...prevState,
      [field]: url,
    }));
  };

  handleSubscriptionProofUpload = (combinedInfo: string): void => {
    const [url] = combinedInfo.split("@@@");
    this.setState({
      proofOfContributionDocumentUrl: url,
      hasNewProofUpload: true,
    });
  };

  async componentDidMount(): Promise<void> {
    this.setState(
      {
        id: this.props.match.params.id,
        currentCustomerToProcess:
          this.props?.location?.state?.requests?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      () => {
        this.getCountries();
        this.getCustomer();
        this.fetchCustomerTags();
        this.fetchProductTags();
      },
    );
  }

  executeDrawerQuery(currentDrawer: ActiveDrawerContent): void {
    switch (currentDrawer) {
      case "audits":
        this.getChangeLog();
        break;
      case "syncLogs":
        this.getSyncLogs();
        break;
      case "notifierRecords":
        this.getCustomerNotifierRecords();
        break;
      case "selfServiceActivity":
        this.getCustomerSelfServiceActivities(this.state.customer?.ussdAccount);
        break;
      default:
        break;
    }
    return;
  }

  toggleDrawer = (activeDrawerContent: ActiveDrawerContent): (() => void) => {
    return (): void => {
      this.executeDrawerQuery(activeDrawerContent);
      this.setState({
        activeDrawerContent,
        showDrawer: !this.state.showDrawer,
        showCancelModal: false,
      });
    };
  };

  toggleViewInteractionSection = (currentInteractionId: string): void => {
    const interaction = this.findInteractionById(currentInteractionId);

    this.setState({
      currentInteractionId,
      activeDrawerContent: "customerInteraction",
      showDrawer: !this.state.showDrawer,
      notes: interaction?.notes,
    });

    this.getInteractionChangeLog(interaction?.id as string);
  };

  toggleViewRedemptionRequestsSection = (
    currentInteractionId: string,
  ): void => {
    this.setState({
      currentInteractionId,
      activeDrawerContent: "redemptionRequests",
    });

    const interaction = this.findInteractionById(currentInteractionId);
    this.getInteractionChangeLog(interaction?.id as string);
  };

  toggleLienSection = (fundCode: string): void => {
    this.setState({
      currentFundCode: fundCode,
      activeDrawerContent: "liens",
      showDrawer: !this.state.showDrawer,
    });
  };

  toggleVansSection = (
    van?: VirtualAccountNumber,
    productName?: string,
  ): void => {
    this.setState({
      currentCustomerVan: van,
      currentCustomerVanIsSavingsBooster: isSavingsBoosterProduct(productName),
      activeDrawerContent: "vans",
      showDrawer: !this.state.showDrawer,
    });
  };

  toggleViewRequestsSection = (currentInteractionId: string): void => {
    this.setState({
      currentInteractionId,
      activeDrawerContent: "transferRequests",
    });
  };

  toggleFirstContributionModal = (
    contribution?: ProductContributionSubscriptionMapping,
  ): void => {
    this.setState((prevState) => ({
      showFirstContributionModal: !prevState.showFirstContributionModal,
      currentContribution: contribution,
    }));
  };

  getTitleBasedOnDrawerContent = (): React.ReactNode => {
    const { activeDrawerContent } = this.state;
    switch (activeDrawerContent) {
      case "audits":
        return "Audit Logs";
      case "syncLogs":
        return "SyncLogs";
      case "notifierRecords":
        return "Notification Records";
      case "customerInteraction":
        return "Customer Interaction";
      case "transferRequests":
        return "Transfer Requests";
      case "liens":
        return "Liens";
      case "redemptionRequests":
        return "Redemption Requests";
      case "productSubscription":
        return "Add Product to Customer";
      case "selfServiceActivity":
        return "Self Service Activities";
      case "vans":
        return "Customer Van";
      case "loanRequest":
        return "Loan Requests";
      case "mergers":
        return "Account Mergers";
      default:
        return "Entity";
    }
  };

  findInteractionById = (id: string): Activity | undefined => {
    return this.state.customerInteractions.find(
      (interaction: Activity): boolean => interaction.id === id,
    );
  };

  findSubscriptionByProductCode = (code: string): Subscription | undefined => {
    const { customer } = this.state;
    if (!customer) return;
    if (customer.customerSubscriptions) {
      for (const subscription of customer.customerSubscriptions) {
        if (subscription.product.fundCode === code) {
          return subscription;
        }
      }
    } else {
      return;
    }
  };

  toggleShowConfirmCreateModal = (): void => {
    this.setState({
      showConfirmCreateModal: true,
    });
  };

  toggleShowConfirmUpdateModal = (): void => {
    this.setState({
      showConfirmUpdateModal: !this.state.showConfirmUpdateModal,
    });
  };

  getDrawerContent = (): JSX.Element => {
    const {
      activeDrawerContent,
      currentInteractionId,
      currentFundCode,
      currentCustomerVan,
      currentCustomerVanIsSavingsBooster,
      isFiltering,
      filteredChangelogs,
      changeLogs,
      showConfirmCreateModal,
      showConfirmUpdateModal,
      notes,
      isLoading,
      customer,
      isInteractionLoading,
    } = this.state;

    const selfServiceActivities = this.state.isSelfActivityFiltering
      ? this.state.filteredSelfServiceActivities
      : this.state.customer?.selfServiceActivities || [];

    const changeNumberFormConfig: FormFieldConfig[] = [
      {
        id: "phone",
        name: "phone",
        inputType: "phone",
        rule: "string",
        label: "Change Phone Number",
        placeholder: "Enter phone number",
        initialValue: this.state.customer?.ussdAccount?.phone?.number,
        required: false,
        colConfig: {
          span: 24,
        },
      },
    ];

    const ussdAccount = {
      ...this.state.ussdAccount,
      customer: { ...this.state.customer },
    } as UssdAccount;
    const memberPortalAccount = this.state.customer?.memberPortalAccount;

    switch (activeDrawerContent) {
      case "audits":
        return (
          <Fragment>
            <div style={{ marginBottom: "20px" }}>
              <Input.Search
                size="middle"
                type="text"
                placeholder="Filter by date, audit, user, action,	comments..."
                onChange={this.filterLogs}
              />
            </div>
            {isFiltering
              ? ChangelogTableFactory(filteredChangelogs)
              : ChangelogTableFactory(changeLogs)}
          </Fragment>
        );
      case "selfServiceActivity":
        return (
          <SelfServiceActivityUI
            ussdAccount={ussdAccount}
            portalAccount={memberPortalAccount}
            activities={selfServiceActivities}
            filterActivity={this.filterSelfServiceActivities}
            changeUssdAccountStatus={this.updateUssdAccountStatus}
            resetUssdAccountPin={this.resetUssdAccountPin}
            isLoading={isLoading}
            updateUssdAccountPhone={this.updateUssdAccountPhone}
            toggleModal={this.toggleModal}
            toggleShowConfirmCreateModal={this.toggleShowConfirmCreateModal}
            toggleShowConfirmUpdateModal={this.toggleShowConfirmUpdateModal}
            reference={this.reference}
            changeNumberFormConfig={changeNumberFormConfig}
            showConfirmCreateModal={showConfirmCreateModal}
            showConfirmUpdateModal={showConfirmUpdateModal}
          />
        );
      case "notifierRecords":
        const records = this.state.customer
          ?.notifierRecords as NotificationRecord[];
        return (
          <Fragment>
            {NotificationRecordTableFactory(records, notificationColumns)}
          </Fragment>
        );
      case "syncLogs":
        const syncLogsData = (this.state.syncLogs || []).map(
          (log: SyncLog, index: number) => ({
            key: String(index),
            ...log,
          }),
        );
        const syncLogsColumns: ColumnsType<SyncLog> = [
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render(value: boolean): JSX.Element {
              return <span>{value ? "success" : "failed"}</span>;
            },
          },
          {
            title: "Synced At",
            dataIndex: "syncedAt",
            key: "syncedAt",
            render(value: string): JSX.Element {
              return (
                <span>
                  {value
                    ? moment(value, "YYYY-MM-DD HH:mm:ss").format("llll")
                    : "N/A"}
                </span>
              );
            },
          },
          {
            title: "Entity Type",
            dataIndex: ["additionalInfo", "entity_name"],
            key: "entity_name",
            render(_text: string, record: SyncLog): JSX.Element {
              return (
                <span>{record?.additionalInfo?.entity_name || "N/A"}</span>
              );
            },
          },
          {
            title: "System Synced",
            dataIndex: ["additionalInfo", "system"],
            key: "system",
            render(_text: string, record: SyncLog): JSX.Element {
              return <span>{record?.additionalInfo?.system || "N/A"}</span>;
            },
          },
          {
            title: "Error Message",
            dataIndex: "errorMessage",
            key: "errorMessage",
            render(value: string): JSX.Element {
              return <span>{value || "N/A"}</span>;
            },
          },
        ];
        return (
          <Spin spinning={this.state.isSyncLogsLoading}>
            <Table
              bordered={true}
              columns={syncLogsColumns}
              dataSource={syncLogsData}
              pagination={false}
            />
          </Spin>
        );
      case "mergers":
        return (
          <MergerSection
            mergeLogs={customer?.customerMergeLogs || []}
            onReverse={(duplicateId) => this.reverseMerge(duplicateId)}
            onReverseAll={this.reverseAllMerges}
            onInitiate={this.toggleAccountMergerModal}
            isReversing={this.state.isReversingMerge}
            isBulkReversing={this.state.isBulkReversingMerge}
            type="customer"
            canManage={true}
          />
        );
      case "transferRequests":
        const customerInteraction = this.state.customerInteractions.find(
          (activity) => activity.id === this.state.currentInteractionId,
        );
        return (
          <Fragment>
            <NewTransferRequest
              {...this.props}
              handleSuccess={this.toggleDrawer("")}
              customerActivityId={this.state.currentInteractionId}
              customer={this.state.customer}
              customerInteraction={customerInteraction}
            />
          </Fragment>
        );
      case "redemptionRequests":
        const currentInteraction = this.state.customerInteractions.find(
          (activity) => activity.id === this.state.currentInteractionId,
        );
        return (
          <Fragment>
            <NewRedemptionRequest
              {...this.props}
              handleSuccess={this.toggleDrawer("")}
              customerActivityId={this.state.currentInteractionId}
              customer={this.state.customer}
              customerInteraction={currentInteraction}
            />
          </Fragment>
        );
      case "productSubscription":
        return (
          <Fragment>
            <CreateCustomerSubscription
              {...this.props}
              handleSuccess={() => {
                this.toggleDrawer("")();
                this.getCustomerSubscriptions();
              }}
              customer={this.state.customer}
            />
          </Fragment>
        );
      case "loanRequest":
        return (
          <Fragment>
            <NewLoanRequest
              onSuccess={this.toggleDrawer("")}
              customer={customer}
            />
          </Fragment>
        );
      case "fixedIncomeRequest":
        return (
          <Fragment>
            <NewFixedIncomeRequest
              onSuccess={this.toggleDrawer("")}
              customer={customer}
            />
          </Fragment>
        );
      case "customerInteraction":
        const interaction = this.findInteractionById(
          currentInteractionId as string,
        );
        const transfers = this.state.customer
          ? this.state.customer.transferRequests
          : [];
        return (
          <CustomerInteractionSection
            showRequests={true}
            forCustomer={true}
            forCompany={false}
            showInteraction={this.state.showInteractionModal}
            toggleCreateInteractionModal={this.toggleShowInteractionModal}
            isInteractionLoading={isInteractionLoading}
            updateInteraction={this.updateCustomerInteraction}
            isEditingInteraction={this.state.isEditingInteraction}
            isCreateTicketLoading={this.state.isTicketLoading}
            toggleInteractionEdit={this.toggleInteractionEdit}
            audits={ChangelogTableFactory(this.state.interactionsChangeLogs)}
            auditCount={this.state.interactionsChangeLogs.length}
            notes={notes || []}
            customer={this.state.customer}
            tickets={this.state.tickets}
            interaction={interaction}
            createNote={this.createNote}
            createTicket={this.createTicket}
            activityReference={this.reference}
            noteReference={this.noteReference}
            showTicketForm={this.state.canCreateTicket}
            toggleTicketCreate={this.toggleTicketCreate}
            ticketReference={this.ticketReference}
            createInteraction={this.createCustomerInteraction}
            toggleViewRequestsSection={this.toggleViewRequestsSection}
            toggleViewInteractionSection={this.toggleViewInteractionSection}
            toggleViewRedemptionRequestsSection={
              this.toggleViewRedemptionRequestsSection
            }
            updateAttachments={this.updateAttachments}
            removeAttachment={this.unassignAttachmentFromCustomerInteraction}
            holdInteraction={this.holdInteraction}
            closeInteraction={this.closeInteraction}
            openInteraction={this.openInteraction}
            openDelayUpdateModal={this.openDelayUpdateModal}
            transferRequests={transfers}
            entity="CustomerInteraction"
            handleNoteUpdate={this.handleNoteUpdate}
            assignAttachmentsToCustomerInteraction={
              this.assignAttachmentsToCustomerInteraction
            }
            currentInteractionId={currentInteractionId}
          />
        );

      case "liens":
        const liens =
          this.findSubscriptionByProductCode(currentFundCode)?.customerLiens ||
          [];

        // const subscriptionsWithContributions = mapSubscriptionToContribution(
        //   [],
        //   customer?.customerSubscriptions,
        //   customerBalanceSummary,
        // );

        // const clientNames: SelectOptionsInterface[] =
        //   subscriptionsWithContributions
        //     .flatMap(
        //       (item) =>
        //         item.scheme.contributions?.map(
        //           (contribution) => contribution.client_name,
        //         ) || [],
        //     )
        //     .filter(
        //       (name, index, self): name is string =>
        //         !!name && self.indexOf(name) === index,
        //     )
        //     .map((name) => ({
        //       id: name,
        //       name: name,
        //     }));

        const clients: SelectOptionsInterface[] =
          convertCompaniesToSelectOptions([
            ...(this.state.customer?.employers ?? []),
            ...(this.state.customer?.formerCompanies ?? []),
          ]);

        return (
          <Fragment>
            <CustomerLienSection
              showHeading={false}
              reference={this.lienReference}
              createLien={this.createLien}
              liens={liens}
              showForm={true}
              updateLien={this.updateLien}
              deleteLien={this.deleteLien}
              isLoading={isLoading}
              handleFileUploadChange={this.handleFileUploadChange}
              clients={clients}
            />
          </Fragment>
        );

      case "vans":
        return (
          <VanUI
            van={currentCustomerVan}
            showOldVan={currentCustomerVanIsSavingsBooster}
          />
        );
      default:
        return <Fragment />;
    }
  };

  createLien = async (loanPaid?: boolean): Promise<void> => {
    const { currentFundCode, lienAttachmentUrl } = this.state;

    interface Response {
      customerLien: CustomerLien;
      errors: APIError[];
    }

    const fieldsToBeValidated: string[] = [
      "currentAmount",
      "totalAmount",
      "loanPaid",
      "provider",
      "startDate",
      "endDate",
      "employeePreTax",
      "employeePostTax",
      "employerPreTax",
      "employerPostTax",
      "lienAttachmentUrl",
      "companyId",
    ];

    if (!this.lienReference.current) return;

    let data;

    try {
      data = await this.lienReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_CUSTOMER_LIEN,
        variables: {
          ...data,
          lienAttachmentUrl,
          loanPaid: loanPaid !== undefined ? loanPaid : false,
          customerSubscriptionId:
            this.findSubscriptionByProductCode(currentFundCode)?.id,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCustomerLien",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation?.customerLien) {
        message.success("Lien created successfully");
        this.getCustomerSubscriptions();

        this.lienReference.current.resetFields(fieldsToBeValidated);
      } else {
        message.error("Error creating lien");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  updateLien = async (lienId: string, loanPaid?: boolean): Promise<void> => {
    const { currentFundCode, lienAttachmentUrl } = this.state;

    interface Response {
      customerLien: CustomerLien;
      errors: APIError[];
    }

    const fieldsToBeValidated: string[] = [
      "currentAmount",
      "totalAmount",
      "loanPaid",
      "provider",
      "startDate",
      "endDate",
      "employeePreTax",
      "employeePostTax",
      "employerPreTax",
      "employerPostTax",
      "lienAttachmentUrl",
      "companyId",
    ];

    if (!this.lienReference.current) return;

    let data;

    try {
      data = await this.lienReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_LIEN,
        variables: {
          ...data,
          id: lienId,
          lienAttachmentUrl,
          loanPaid: loanPaid !== undefined ? loanPaid : false,
          customerSubscriptionId:
            this.findSubscriptionByProductCode(currentFundCode)?.id,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerLien",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));

        return;
      }

      if (mutation?.customerLien) {
        message.success("Lien updated successfully");
        this.getCustomerSubscriptions();

        this.lienReference.current.resetFields(fieldsToBeValidated);
      } else {
        message.error("Error updating lien");
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  deleteLien = async (lienId: string | undefined): Promise<void> => {
    if (lienId === undefined) return;

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: DELETE_CUSTOMER_LIEN,
        variables: {
          lienId,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "deleteCustomerLien",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));

        return;
      }

      if (mutation?.result) {
        message.success("Lien deleted successfully");
        this.getCustomerSubscriptions();
      } else {
        message.error("Error deleting lien");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  getTicketSubCategory = async (
    subCategoryId: string,
  ): Promise<AxiosResponse<any>> => {
    return request({
      query: CALCULATE_DUE_DATE_QUERY,
      variables: {
        subCategoryId,
      },
    });
  };

  createTicket = async (): Promise<void> => {
    const { currentInteractionId, customer, customerInteractions } = this.state;
    const fieldsToBeValidated = [
      "firstName",
      "lastName",
      "phoneNumber",
      "description",
      "subject",
      "source",
      "type",
      "mentions",
      "assigneeId",
      "ticketSubCategoryId",
    ];
    interface Response {
      ticket: Ticket;
    }

    if (!this.ticketReference.current) return;

    let data;

    try {
      data = await this.ticketReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isTicketLoading: true });

    const selectedInteraction = customerInteractions.find(
      (interaction: Activity) => {
        return interaction.id === currentInteractionId;
      },
    );

    let ticketDetails;

    try {
      ticketDetails = await this.getTicketSubCategory(data.ticketSubCategoryId);
    } catch (error) {
      ticketDetails = {
        data: {
          data: {
            calculateSubCategoryDueDate: "",
          },
        },
      };
    }

    try {
      const response: AxiosResponse = await request({
        query: CREATE_CUSTOMER_TICKET,
        variables: {
          customerInteractionId: currentInteractionId,
          properties: {
            ...data,
            first_name: data.firstName,
            last_name: data.lastName,
            system_user: this.props.user.email,
            title: `${selectedInteraction?.subCategory.name} - ${customer?.firstName} ${customer?.lastName}`,
            group: "Users",
            priority: "2 normal",
            state: "open",
            petra_id: customer?.petraId,
            due_date:
              ticketDetails?.data?.data?.calculateSubCategoryDueDate ?? "",
          },
          assigneeId: data.assigneeId,
          source: data.source,
          ticketSubCategoryId: data.ticketSubCategoryId,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createCustomerInteractionTicket",
      );
      if (err) {
        message.error(generateDisplayErrorMessage(err));
      } else {
        this.setState((oldState) => ({
          tickets: [...oldState.tickets, mutation.ticket],
          canCreateTicket: !this.state.canCreateTicket,
        }));
        this.ticketReference.current.resetFields(fieldsToBeValidated);
        this.getCustomerInteractions();
      }
    } catch (error) {
      Sentry.captureException(error);
    }
    this.setState({ isTicketLoading: false });
  };

  createNote = async (): Promise<void> => {
    const { user } = this.props;

    interface Response {
      note: Note;
    }

    const fieldsToBeValidated: string[] = ["description"];

    if (!this.noteReference.current) return;

    let data;

    try {
      data = await this.noteReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_NOTE,
        variables: {
          description: data.description,
          userId: user.id,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "createNote",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      } else if (mutation.note) {
        await this.assignNoteToInteraction({
          noteId: mutation.note.id,
          activityId: this.state?.currentInteractionId as string,
        });
        message.success("Note added successfullt");
      }
      this.noteReference.current.resetFields(fieldsToBeValidated);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  assignNoteToInteraction = async (props: {
    activityId: string;
    noteId: string;
  }): Promise<void> => {
    interface Response {
      data: {
        assignNoteToCustomerInteraction: {
          customerInteractionNote: {
            note: Note;
          };
        };
      };
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: ASSIGN_NOTE_TO_CUSTOMER_INTERACTION,
        variables: {
          noteId: props.noteId,
          customerInteractionId: props.activityId,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "assignNoteToCustomerInteraction",
      );

      if (err) {
        message.error(`failed to assign note to interaction - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.getCustomerInteractions();
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  assignAttachmentsToCustomerInteraction = async (
    attachmentIds: string[],
    customerInteractionId: string,
  ): Promise<void> => {
    this.setState({ isLoading: true });

    interface Response {
      data: {
        assignAttachmentsToCustomerInteraction: {
          customerInteraction: Activity;
        };
      };
    }

    return request({
      query: ASSIGN_ATTACHMENT_TO_CUSTOMER_INTERACTION,
      variables: {
        attachmentIds,
        customerInteractionId,
      },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        this.setState(() => ({
          isLoading: false,
          attachments: [],
          customerInteractionAttachments:
            data.data.assignAttachmentsToCustomerInteraction.customerInteraction
              .customerInteractionAttachments || [],
        }));
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        message.error("Failed to attach files");
        Sentry.captureException(error);
      });
  };

  mutateCustomerInteractions = (interaction: Activity): void => {
    if (!this.state.customer) return;
    const oldInteractions = this.state.customerInteractions.filter(
      (i: Activity): boolean => {
        return i.id !== interaction.id;
      },
    );
    this.setState({
      customerInteractions: [...oldInteractions, interaction],
    });
  };

  updateCustomerInteraction = async (): Promise<void> => {
    const fieldsToBeValidated: string[] = [
      "description",
      "revertDate",
      "comment",
      "complaintType",
    ];
    interface Response {
      customerInteraction: Activity;
    }

    if (!this.reference.current) return;

    let data;
    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    const interaction = this.findInteractionById(
      this.state.currentInteractionId as string,
    );

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_INTERACTION,
        variables: {
          ...interaction,
          interactionSourceId: interaction?.interactionSource.id,
          subCategoryId: interaction?.subCategory.id,
          userId: interaction?.user?.id,
          customerId: this.state.id,
          comment: "",
          ...data,
        },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "updateCustomerInteraction",
      );

      this.mutateCustomerInteractions(mutation.customerInteraction);
      this.reference.current?.resetFields(fieldsToBeValidated);
      message.success("Interaction successfully updated!");
      this.toggleInteractionEdit();
      this.getInteractionChangeLog(mutation.customerInteraction.id);
    } catch (error) {
      message.error(`Interaction failed to update! - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    this.setState({ isLoading: false });
  };

  createCustomerInteraction = async (
    ccIds?: CompanyWithCustomerCompanyAssociationId[],
    customerSubscriptionIds?: ProductWithSubscriptionId[],
  ): Promise<void> => {
    const { id, customer, customerInteractions } = this.state;
    const { user } = this.props;

    interface Validated {
      description: string;
      activityModeId: string;
      activityDate: Moment;
      interactionSourceId: string;
    }

    interface Response {
      customerInteraction: Activity;
    }

    const fieldsToBeValidated: string[] = [
      "description",
      "subCategoryId",
      "activityDate",
      "interactionSourceId",
      "interactionResolved",
      "desiredOutcome",
      "preferredCommMode",
      "complaintType",
      "email",
      "phone",
      "interactionResolveStatus",
      "customerCompanyAssociation",
      "customerSubscription",
      "sourceContactInformation",
      "createTicket",
      "mentions",
      "assigneeId",
      "ticketSubCategoryId",
      "ticketDescription",
      "interactionCategory",
    ];

    if (!this.reference.current) return;

    let data;

    try {
      data = await this.reference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({
      isInteractionLoading: true,
      disableCreateInteractionButton: true,
    });

    const variables = {
      ...data,
      subscriberIds: data.mentions,
      interactionResolved: data.interactionResolved === "yes",
      createTicket: data.createTicket === "yes",
      contactInformation: data[data.preferredCommMode],
      activityDate: data.activityDate,
      customerId: id,
      userId: user.id,
    };

    if (this.state.attachmentIds.length > 0) {
      variables["attachmentIds"] = this.state.attachmentIds;
    }

    try {
      const response: AxiosResponse = await request({
        query: CREATE_CUSTOMER_INTERACTION,
        variables,
      });

      const { mutation, err } = responseHandler<Response>(
        response,
        "createCustomerInteraction",
      );

      if (err) {
        const errMessage = err[0].description;
        message.error(errMessage);
      } else {
        customer &&
          this.setState({
            customerInteractions: [
              ...[mutation.customerInteraction],
              ...customerInteractions,
            ],
            attachmentIds: [],
            attachments: [],
          });

        const newCustomerInteraction: Activity = mutation.customerInteraction;

        ccIds?.forEach((element) => {
          this.interactionCustomerCompanyAssociation(
            newCustomerInteraction,
            element.customerCompanyAssociationId,
          );
        });

        customerSubscriptionIds?.forEach((element) => {
          this.interactionCustomerSubscription(
            newCustomerInteraction,
            element.subscriptionId,
          );
        });

        this.reference.current?.resetFields(fieldsToBeValidated);
        message.success("Interaction created successfully");
        this.toggleShowInteractionModal();
        this.toggleViewInteractionSection(mutation.customerInteraction.id);
      }
    } catch (error) {
      message.error(`Error encountered during interaction creation - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({
        isInteractionLoading: false,
        disableCreateInteractionButton: false,
      });
    }
  };

  interactionCustomerCompanyAssociation = async (
    customerInteraction: Activity,
    customerCompanyAssociationId: string,
  ): Promise<void> => {
    const customerInteractionId = customerInteraction.id;
    try {
      const response: AxiosResponse = await request({
        query: CREATE_INTERACTION_CUSTOMER_COMPANY,
        variables: {
          customerInteractionId,
          customerCompanyAssociationId,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "createInteractionCustomerCompany",
      );
      if (err) {
        message.error(`Failed to create interaction customer company - ${generateDisplayErrorMessage(err)}`);
        return;
      }
      this.getCustomerInteractions();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  interactionCustomerSubscription = async (
    customerInteraction: Activity,
    customerSubscriptionId: string,
  ): Promise<void> => {
    const customerInteractionId = customerInteraction.id;
    try {
      const response: AxiosResponse = await request({
        query: CREATE_INTERACTION_CUSTOMER_SUBSCRIPTION,
        variables: {
          customerInteractionId,
          customerSubscriptionId,
        },
      });

      const { err } = responseHandler<Response>(
        response,
        "createInteractionCustomerSubscription",
      );
      if (err) {
        message.error(`Failed to change status of interaction - ${generateDisplayErrorMessage(err)}`);
        return;
      }
      this.getCustomerInteractions();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  holdInteraction = async (): Promise<void> => {
    await this.changeInteractionStatus(
      HOLD_CUSTOMER_INTERACTION,
      "holdCustomerInteraction",
    );
  };

  openInteraction = async (): Promise<void> => {
    await this.changeInteractionStatus(
      OPEN_CUSTOMER_INTERACTION,
      "openCustomerInteraction",
    );
  };

  closeInteraction = async (
    closePayload: CloseInteractionPayload,
  ): Promise<boolean> => {
    return await this.changeInteractionStatus(
      CLOSE_CUSTOMER_INTERACTION,
      "closeCustomerInteraction",
      closePayload,
    );
  };

  openDelayUpdateModal = (): void => {
    this.triggerDelayPreviewRefresh.cancel();
    this.setState(
      (prevState) => ({
        showDelayUpdateModal: !prevState.showDelayUpdateModal,
        selectedDelayTemplateId: undefined,
        delayUpdateComment: "",
        delayNotificationPreview: null,
      }),
      () => {
        if (
          this.state.showDelayUpdateModal &&
          this.state.delayNotificationTemplates.length === 0
        ) {
          this.getDelayNotificationTemplates();
        }
      },
    );
  };

  getDelayNotificationTemplates = async (): Promise<void> => {
    this.setState({ isDelayTemplatesLoading: true });
    try {
      const {
        data: { data },
      }: AxiosResponse<{
        data: { templates: Template[] };
      }> = await request({ query: GET_DELAY_NOTIFICATION_TEMPLATES });

      this.setState({
        delayNotificationTemplates: data.templates || [],
      });
    } catch (error) {
      message.error(`Failed to load delay notification templates - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isDelayTemplatesLoading: false });
    }
  };

  loadDelayNotificationPreview = async (
    customerInteractionId: string,
    templateId: string,
    comment: string,
  ): Promise<void> => {
    this.setState({ isDelayPreviewLoading: true });

    try {
      const variables: {
        customerInteractionId: string;
        templateId: string;
        comment?: string;
      } = {
        customerInteractionId,
        templateId,
      };

      if (comment.trim() !== "") {
        variables.comment = comment;
      }

      const response: AxiosResponse<{
        data: {
          customerInteractionDelayUpdatePreview: {
            preview: InteractionNotificationPreview | null;
            errors: APIError[];
          };
        };
      }> = await request({
        query: GET_CUSTOMER_INTERACTION_NOTIFICATION_PREVIEW,
        variables,
      });

      const previewResponse =
        response.data.data.customerInteractionDelayUpdatePreview;

      if (previewResponse.errors?.length) {
        message.error(generateDisplayErrorMessage(previewResponse.errors));
        this.setState({ delayNotificationPreview: null });
        return;
      }

      this.setState({
        delayNotificationPreview: previewResponse.preview,
      });
    } catch (error) {
      message.error(`Failed to load delay notification preview - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isDelayPreviewLoading: false });
    }
  };

  triggerDelayPreviewRefresh = debounce(
    async (
      customerInteractionId: string,
      templateId: string,
      comment: string,
    ): Promise<void> => {
      this.loadDelayNotificationPreview(
        customerInteractionId,
        templateId,
        comment,
      );
    },
    DEBOUNCE_TIME,
  );

  handleDelayTemplateChange = async (templateId: string): Promise<void> => {
    const { currentInteractionId } = this.state;
    const customerInteractionId = currentInteractionId;
    if (!customerInteractionId) return;

    this.triggerDelayPreviewRefresh.cancel();
    this.setState({
      selectedDelayTemplateId: templateId,
      delayUpdateComment: "",
      delayNotificationPreview: null,
    });

    this.loadDelayNotificationPreview(customerInteractionId, templateId, "");
  };

  handleDelayCommentChange = (delayUpdateComment: string): void => {
    const { currentInteractionId, selectedDelayTemplateId } = this.state;

    this.setState({
      delayUpdateComment,
      delayNotificationPreview: null,
    });

    if (!currentInteractionId || !selectedDelayTemplateId) return;

    this.triggerDelayPreviewRefresh(
      currentInteractionId,
      selectedDelayTemplateId,
      delayUpdateComment,
    );
  };

  selectedDelayTemplate = (): Template | undefined => {
    return this.state.delayNotificationTemplates.find(
      (template) => template.id === this.state.selectedDelayTemplateId,
    );
  };

  selectedDelayTemplateRequiresCustomComment = (): boolean => {
    return this.selectedDelayTemplate()?.body.includes("{custom_comment}") || false;
  };

  sendDelayUpdate = async (): Promise<void> => {
    const {
      currentInteractionId,
      selectedDelayTemplateId,
      delayUpdateComment,
    } = this.state;
    if (!currentInteractionId || !selectedDelayTemplateId) return;
    if (
      this.selectedDelayTemplateRequiresCustomComment() &&
      delayUpdateComment.trim() === ""
    ) {
      message.error("Comment is required for the selected template");
      return;
    }

    interface Response {
      customerInteraction: Activity;
      errors: APIError[];
    }

    this.triggerDelayPreviewRefresh.cancel();
    this.setState({ isSendingDelayUpdate: true });

    try {
      const variables: {
        customerInteractionId: string;
        templateId: string;
        comment?: string;
      } = {
        customerInteractionId: currentInteractionId,
        templateId: selectedDelayTemplateId,
      };

      if (delayUpdateComment.trim() !== "") {
        variables.comment = delayUpdateComment;
      }

      const response: AxiosResponse = await request({
        query: SEND_CUSTOMER_INTERACTION_DELAY_UPDATE,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "sendCustomerInteractionDelayUpdate",
      );

      if (err) {
        message.error(generateDisplayErrorMessage(err));
        return;
      }

      message.success("Delay update sent successfully");
      if (mutation?.customerInteraction) {
        const existing = this.state.customerInteractions.find(
          (i) => i.id === mutation.customerInteraction.id,
        );
        if (existing) {
          this.mutateCustomerInteractions({
            ...existing,
            revertDate: mutation.customerInteraction.revertDate,
          });
        }
      }
      this.setState({
        showDelayUpdateModal: false,
        selectedDelayTemplateId: undefined,
        delayUpdateComment: "",
        delayNotificationPreview: null,
      });
      this.getCustomerInteractions();
      this.getInteractionChangeLog(currentInteractionId);
    } catch (error) {
      message.error(`Failed to send delay update - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSendingDelayUpdate: false });
    }
  };

  changeInteractionStatus = async (
    query: string,
    mutationName: string,
    closePayload?: CloseInteractionPayload,
  ): Promise<boolean> => {
    const customerInteractionId = this.state.currentInteractionId;
    try {
      const response: AxiosResponse = await request({
        query,
        variables: {
          customerInteractionId,
          closureType: closePayload?.closureType,
          templateId: closePayload?.templateId,
          transferRequestId: closePayload?.transferRequestId,
          sendSurvey: closePayload?.sendSurvey,
          customComment: closePayload?.customComment,
          recipientOverride: closePayload?.recipientOverride,
        },
      });

      const { err } = responseHandler<Response>(response, mutationName);
      if (err) {
        message.error(`Failed to change status of interaction - ${generateDisplayErrorMessage(err)}`);
        return false;
      }
      this.getCustomerInteractions();
      if (customerInteractionId) {
        this.getInteractionChangeLog(customerInteractionId);
      }
      return true;
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
  };

  toggleShowInteractionModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showInteractionModal: !previousState.showInteractionModal,
    }));
  };

  toggleShowStatementModal = (): void => {
    this.setState((previousState: Readonly<State>) => ({
      showStatementModal: !previousState.showStatementModal,
      generatedStatementUrl: "",
    }));
  };

  setCurrentSubscription = (
    contribution: ProductContributionSubscriptionMapping,
  ): void => {
    this.toggleShowStatementModal();
    this.setState({
      currentContribution: contribution,
    });
  };

  getProductContributions = async (fundCode: string): Promise<void> => {
    interface Response {
      data: {
        getSchemeExtended: Scheme;
      };
    }

    this.setState({ isLoading: true });

    const customer = this.state.customer;

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_CUSTOMER_SCHEME_EXTENDED,
        variables: { customerPetraId: customer?.petraId, fundCode },
      });

      this.setState({
        extendedScheme: response.data.data.getSchemeExtended,
      });
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({ isLoading: false });
  };

  getProductContributionSummary = async () => {
    interface Response {
      data: {
        getAllSchemesExtended: Scheme[];
      };
    }

    const { hasLoadedContributions, customer } = this.state;
    if (hasLoadedContributions) return;

    this.setState({ contributionsLoader: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_CUSTOMER_SCHEMES,
        variables: { customerPetraId: customer?.petraId },
      });

      this.setState({
        customerBalanceSummary: response.data.data.getAllSchemesExtended,
      });
      this.getCustomerSubscriptions();
      this.handleSalesPersonFocus();
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({
      contributionsLoader: false,
      hasLoadedContributions: true,
    });
  };

  getContributionData = async (key: string | string[]): Promise<void> => {
    await this.getProductContributionSummary();
  };

  generateCustomerStatement = async () => {
    if (!this.statementReference.current) return;
    interface Response {
      data: {
        generateCustomerStatement: {
          url: string;
          error: string;
        };
      };
    }

    const fieldsToBeValidated: string[] = ["startDate", "endDate", "companyId"];
    let data;
    try {
      data = await this.statementReference.current.validateFields(
        fieldsToBeValidated,
      );
    } catch (error) {
      handleValidationErrors(error);
      return;
    }
    this.setState({ isStatementLoading: true });
    request({
      query: GENERATE_CUSTOMER_STATEMENT,
      variables: {
        ...data,
        customerSubscriptionId: this.state.currentContribution?.subscription.id,
      },
    })
      .then(({ data }: AxiosResponse<Response>): void => {
        if (data.data.generateCustomerStatement.error) {
          message.error(data.data.generateCustomerStatement.error);
          return;
        }
        this.setState({
          generatedStatementUrl: data.data.generateCustomerStatement.url,
        });
      })
      .catch((error) => {
        message.error("Failed to generate customer statement");
        Sentry.captureException(error);
      })
      .finally(() => {
        this.setState({ isStatementLoading: false });
      });
  };

  unassignAttachmentFromCustomerInteraction = async (
    id: string,
  ): Promise<void> => {
    const { customerInteractionAttachments } = this.state;

    const attachmentToDelete = customerInteractionAttachments.find(
      (att) => att.attachment.id === id,
    );

    interface Response {
      result: boolean;
      errors: APIError[];
    }

    try {
      const response: AxiosResponse = await request({
        query: UNASSIGN_ATTACHMENT_FROM_CUSTOMER_INTERACTION,
        variables: { id: attachmentToDelete?.id },
      });

      const { mutation } = responseHandler<Response>(
        response,
        "unassignAttachmentFromCustomerInteraction",
      );

      if (!mutation.result) {
        message.error("Failed to delete attachment from customer interaction");
      } else {
        message.success("Deleted Attachment Successfully");
        this.getCustomerInteractions();
      }
    } catch (error) {
      message.error(`An error occurred, please refresh and try again later - ${generateDisplayErrorMessage(error)}`);
    }
  };

  updateAttachments = (attachment: Attachment): void => {
    this.setState((state) => ({
      attachments: [...state.attachments, attachment],
      attachmentIds: [...state.attachmentIds, attachment.id],
    }));
  };

  getPhoenixTickets = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_TICKETS,
      variables: { customerId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          customer: Customer;
        };
      }>): void => {
        if (data.data.customer) {
          this.setState({
            phoenixTickets: data.data?.customer?.tickets ?? [],
            isLoading: false,
          });
        }
      },
    );
  };

  panelDataLoaders: Record<
    string,
    { fetch: () => void; stateField: keyof State }
  > = {
    customerStatements: {
      fetch: this.getCustomerStatements,
      stateField: "customerStatements",
    },
    phoenixTickets: {
      fetch: this.getPhoenixTickets,
      stateField: "phoenixTickets",
    },
    interactions: {
      fetch: () => this.interactionsNextPage && this.interactionsNextPage(1),
      stateField: "customerInteractions",
    },
    redemptionRequests: {
      fetch: this.getCustomerRedemptionRequests,
      stateField: "redemptionRequests",
    },
    transferRequests: {
      fetch: this.getCustomerTransferRequests,
      stateField: "transferRequests",
    },
    customerTransactions: {
      fetch: this.getCustomerOnlineTransactions,
      stateField: "customerTransactions",
    },
    loanRequests: {
      fetch: this.getCustomerLoanRequests,
      stateField: "loanRequests",
    },
    fixedIncomeRequests: {
      fetch: this.getCustomerFixedIncomeRequests,
      stateField: "fixedIncomeRequests",
    },
  };

  handlePanelChange = (key: string | string[]) => {
    const keys = Array.isArray(key) ? key : [key];
    Object.entries(this.panelDataLoaders).forEach(
      ([panelKey, { fetch, stateField }]) => {
        if (
          keys.includes(panelKey) &&
          (this.state[stateField] as any[]).length === 0
        ) {
          fetch.call(this);
        }
      },
    );
  };

  handleNoteUpdate = (updatedNotes: Note[]) => {
    this.setState({ notes: updatedNotes });
  };

  render(): JSX.Element | null {
    const {
      isLoading,
      customer,
      countries,
      customerBalanceSummary,
      showInteractionModal,
      isInteractionLoading,
      customerTransactions,
      currentCustomerToProcess,
      phoenixTickets,
      showConfirmProductDelete,
      subscriptionIdToDelete,
      showSubscriptionEditModal,
      subscriptionToEdit,
      isSavingSubscriptionEdits,
      customerStatements,
      disableCreateInteractionButton,
      showFirstContributionModal,
      currentContribution,
      redemptionRequests,
      transferRequests,
      isRedemptionLoading,
      isTransferLoading,
      isTransactionsLoading,
      isLoanRequestsLoading,
      loanRequests,
      fixedIncomeRequests,
      isFixedIncomeRequestsLoading,
      showAllocationDetailsModal,
      currentSubscriptionForAllocation,
      productTags,
      showQuickEditModal,
      isQuickEditSaving,
      titles,
      maritalStatuses,
      isQuickEditVerified,
      showContactModal,
      contactModalType,
      contactModalMode,
      contactModalLoading,
      selectedEmail,
      selectedPhone,
      selectedAddress,
      addressType,
      showContactDeleteModal,
      deleteContactType,
      showBackgroundInfoModal,
      backgroundInfoMode,
      isBackgroundInfoSaving,
      showBackgroundInfoDeleteModal,
      fatherDeceased,
      motherDeceased,
      showBeneficiariesModal,
      showPaymentAccountsModal,
      showAssociationsModal,
      paymentAccountsActiveKey,
      beneficiariesActiveKey,
      associationsActiveKey,
      showStandaloneDeleteModal,
      deleteTargetType,
      financialInstitutions,
      branchMap,
      companies,
    } = this.state;
    let userInterface: JSX.Element;

    const moveToNextLink = `/customers/${
      this.props.location?.state?.requests?.[currentCustomerToProcess + 1]?.id
    }`;

    const moveToPreviousLink = `/customers/${
      this.props.location?.state?.requests?.[currentCustomerToProcess - 1]?.id
    }`;

    const nextPreviousUI = (
      <>
        <Row
          gutter={10}
          style={{
            float: "right",
            marginTop: "10px",
          }}
        >
          <Col>
            {this.state.currentCustomerToProcess > 0 && (
              <NavLink
                to={{
                  pathname: moveToPreviousLink,
                  state: {
                    type: "redemption",
                    requests: this.props?.location?.state?.requests ?? [],
                  },
                }}
              >
                <Tooltip placement="left" title={"Previous"}>
                  <LeftOutlined style={{ color: "#1D3764" }} />
                </Tooltip>
              </NavLink>
            )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              this.state.currentCustomerToProcess <
                this.props?.location?.state?.requests?.length - 1 && (
                <NavLink
                  to={{
                    pathname: moveToNextLink,
                    state: {
                      type: "redemption",
                      requests: this.props?.location?.state?.requests ?? [],
                    },
                  }}
                >
                  <Tooltip placement="right" title={"Next"}>
                    <RightOutlined style={{ color: "#1D3764" }} />
                  </Tooltip>
                </NavLink>
              )}
          </Col>

          <Col>
            {this.props?.location?.state?.requests &&
              `${currentCustomerToProcess + 1} / ${
                this.props?.location?.state?.requests?.length
              }`}
          </Col>
        </Row>
      </>
    );

    const productSubscriptionTagOptions = productTags;

    const quickEditFileList =
      customer?.photoIdUrl && customer?.idType
        ? [
            {
              size: 1000,
              type: customer.idType.name,
              uid: "1",
              url: customer.photoIdUrl,
              name: customer.idType.name,
              thumbUrl: customer.photoIdUrl,
            },
          ]
        : [];

    const quickEditSignatureUpload = customer?.signatureUrl
      ? [
          {
            size: 1000,
            type: customer.signatureUrl,
            uid: "1",
            url: customer.signatureUrl,
            name: "Customer Signature",
            thumbUrl: customer.signatureUrl,
          },
        ]
      : [];

    const quickEditGhanaCardFileList =
      customer?.ghanaCardNo && customer?.ghanaCardIdUrl
        ? [
            {
              size: 1000,
              type: "Ghana Card",
              uid: "1",
              url: customer.ghanaCardIdUrl,
              name: "Ghana Card",
              thumbUrl: customer.ghanaCardIdUrl,
            },
          ]
        : [];

    const quickEditGhanaCardBackFileList =
      customer?.ghanaCardNo && customer?.ghanaCardIdBackUrl
        ? [
            {
              size: 1000,
              type: "Ghana Card (Back)",
              uid: "1",
              url: customer.ghanaCardIdBackUrl,
              name: "Ghana Card (Back)",
              thumbUrl: customer.ghanaCardIdBackUrl,
            },
          ]
        : [];

    const quickEditFields: FormFieldConfig[] = customer
      ? [
          {
            id: "titleId",
            name: "titleId",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Title",
            label: "Title",
            selectOptions: titles,
            initialValue: customer.title ? customer.title.id : undefined,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "firstName",
            name: "firstName",
            inputType: "string",
            rule: "string",
            placeholder: "First Name",
            label: "First Name",
            initialValue: customer.firstName,
            required: true,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "middleName",
            name: "middleName",
            inputType: "string",
            rule: "string",
            placeholder: "Middle Name",
            initialValue: customer.middleName,
            label: "Middle Name",
            required: false,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "lastName",
            name: "lastName",
            inputType: "string",
            rule: "string",
            placeholder: "Last Name",
            initialValue: customer.lastName,
            label: "Last Name",
            required: true,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "ssnitNumber",
            name: "ssnitNumber",
            inputType: "string",
            rule: "string",
            initialValue: customer.ssnitNumber,
            required: false,
            disabled: false,
            placeholder: "SSNIT Number",
            label: "SSNIT Number",
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "dateOfBirth",
            name: "dateOfBirth",
            inputType: "date",
            rule: "string",
            placeholder: "Date of birth",
            label: "Date of birth",
            initialValue: customer.dateOfBirth || undefined,
            required: false,
            disabledDate: disabledDate,
            dateOptions: {
              limitTo18: true,
            },
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "maritalStatusId",
            name: "maritalStatusId",
            inputType: "select",
            rule: "string",
            required: false,
            placeholder: "Marital Status",
            label: "Marital Status",
            selectOptions: maritalStatuses,
            initialValue: customer.maritalStatus
              ? customer.maritalStatus.id
              : undefined,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "countryId",
            name: "countryId",
            inputType: "select",
            rule: "string",
            required: true,
            placeholder: "Nationality",
            label: "Nationality",
            selectOptions: countries,
            initialValue: customer.country ? customer.country.id : undefined,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "gender",
            name: "gender",
            inputType: "select",
            rule: "string",
            placeholder: "Gender",
            label: "Gender",
            initialValue: customer.gender ? customer.gender : undefined,
            selectOptions: [
              { id: "0", name: "Male", value: "male" },
              { id: "1", name: "Female", value: "female" },
            ],
            required: true,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "photoIdNumber",
            name: "photoIdNumber",
            inputType: "string",
            rule: "string",
            initialValue: customer.photoIdNumber,
            required: false,
            disabled: false,
            placeholder: "Enter ID Number",
            label: "ID Number",
            colConfig: {
              span: 7,
            },
          },
          {
            id: "photoIdUrl",
            name: "photoIdUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: customer.photoIdUrl,
            required: false,
            disabled: false,
            label: "ID Upload",
            changeHandler: (combinedInfo: string) =>
              this.handleQuickEditFileUploadChange("photoIdUrl", combinedInfo),
            defaultFileList: quickEditFileList,
            onFileDelete: this.handleQuickEditDelete,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "ghanaCardIdUrl",
            name: "ghanaCardIdUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: customer.ghanaCardIdUrl,
            required: false,
            disabled: false,
            label: "Ghana Card ID Upload",
            defaultFileList: quickEditGhanaCardFileList,
            changeHandler: (combinedInfo: string) =>
              this.handleQuickEditFileUploadChange(
                "ghanaCardIdUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleQuickEditDelete,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "ghanaCardIdBackUrl",
            name: "ghanaCardIdBackUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: customer.ghanaCardIdBackUrl,
            required: false,
            disabled: false,
            label: "Ghana Card ID Upload (Back)",
            defaultFileList: quickEditGhanaCardBackFileList,
            changeHandler: (combinedInfo: string) =>
              this.handleQuickEditFileUploadChange(
                "ghanaCardIdBackUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleQuickEditDelete,
            colConfig: {
              span: 7,
            },
          },
          {
            id: "ghanaCardNo",
            name: "ghanaCardNo",
            inputType: "string",
            rule: "string",
            initialValue: customer.ghanaCardNo,
            required: false,
            disabled: false,
            placeholder: "Enter Ghana Card ID Number",
            label: "Ghana Card ID Number",
            pattern: ghanaCardRegex,
            patternErrorMessage:
              "Invalid Ghana Card Number format. Expected format: ABC-123456789-1",
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "idIssueDate",
            name: "idIssueDate",
            inputType: "date",
            rule: "string",
            placeholder: "Ghana Card Issue Date",
            label: "Ghana Card Issue Date",
            initialValue: customer?.idIssueDate,
            required: false,
            elementStyle: {
              width: "100%",
            },
            dateOptions: {
              limitTo18: false,
              startYear: 2017,
            },
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "idExpiryDate",
            name: "idExpiryDate",
            inputType: "date",
            rule: "string",
            placeholder: "Ghana Card Expiry Date",
            label: "Ghana Card Expiry Date",
            initialValue: customer?.idExpiryDate,
            required: false,
            elementStyle: {
              width: "100%",
            },
            dateOptions: {
              limitTo18: false,
              startYear: 2017,
            },
            colConfig: {
              span: 7,
            },
          },
          {
            id: "signatureUrl",
            name: "signatureUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: customer.signatureUrl,
            required: false,
            disabled: false,
            label: "Signature Upload",
            defaultFileList: quickEditSignatureUpload,
            changeHandler: (combinedInfo: string) =>
              this.handleQuickEditFileUploadChange(
                "signatureUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleQuickEditDelete,
            colConfig: {
              offset: 1,
              span: 7,
            },
          },
          {
            id: "isVerified",
            name: "isVerified",
            inputType: "checkbox",
            rule: "boolean",
            label: "Ghana Card & Date of Birth Verified?",
            checkboxCheckedValue:
              isQuickEditVerified || !!customer.verificationProofUrl,
            required: false,
            checkboxChangeHandler: (e) =>
              this.changeQuickEditIsVerified(e.target.checked),
            colConfig: {
              span: 7,
              offset: 1,
            },
          },
          {
            id: "verificationProofUrl",
            name: "verificationProofUrl",
            inputType: "fileUpload",
            rule: "string",
            initialValue: customer.verificationProofUrl,
            required: isQuickEditVerified || false,
            disabled: false,
            label: "Proof of Verification",
            changeHandler: (combinedInfo: string) =>
              this.handleQuickEditFileUploadChange(
                "verificationProofUrl",
                combinedInfo,
              ),
            onFileDelete: this.handleQuickEditDelete,
            defaultFileList: customer.verificationProofUrl
              ? [
                  {
                    size: 1000,
                    type: "Verification Proof",
                    uid: "1",
                    url: customer.verificationProofUrl,
                    name: "Verification Proof",
                    thumbUrl: customer.verificationProofUrl,
                  },
                ]
              : [],
            colConfig: {
              span: 7,
            },
          },
        ]
      : [];

    const contactModalFields =
      contactModalType === "email"
        ? createEmailFields({
            createEmail: this.saveContactEmail,
            initialValue:
              contactModalMode === "edit" ? selectedEmail || undefined : undefined,
          }).filter(
            (field) =>
              field.inputType !== "button" && field.inputType !== "buttonGroup",
          )
        : contactModalType === "phone"
          ? createPhoneFields({
              createPhone: this.saveContactPhone,
              initialValue:
                contactModalMode === "edit" ? selectedPhone || undefined : undefined,
            }).filter(
              (field) =>
                field.inputType !== "button" && field.inputType !== "buttonGroup",
            )
          : contactModalType === "address"
            ? createAddressFields({
                countries,
                createAddress: this.saveContactAddress,
                initialValue:
                  contactModalMode === "edit" ? selectedAddress || undefined : undefined,
                changeHandler: this.handleAddressTypeChange,
                addressType,
              }).filter(
                (field) =>
                  field.inputType !== "button" && field.inputType !== "buttonGroup",
              )
            : [];

    const contactAddressFieldsToHide =
      addressType === AssociatedEntityConstants.PostalAddressCode
        ? ["ghPost"]
        : ["poBox"];

    const backgroundInfoFields = createBackgroundInfoFields({
      handleFatherDeceasedChange: this.handleBackgroundFatherDeceasedChange,
      handleMotherDeceasedChange: this.handleBackgroundMotherDeceasedChange,
      createBackgroundInfo: this.saveBackgroundInfo,
      updateBackgroundInfo: this.saveBackgroundInfo,
      initialValue: customer?.customerBackgroundInformation || undefined,
      fatherDeceased,
      motherDeceased,
    }).filter(
      (field) =>
        field.inputType !== "button" && field.inputType !== "buttonGroup",
    );

    if (customer) {
      userInterface = (
          <Fragment>
            {showConfirmProductDelete &&
              subscriptionIdToDelete &&
              ModalCreator({
                title: "Delete Product Subscription",
                okType: "primary",
                okText: "Delete",
                showModal: showConfirmProductDelete,
                onOkay: () =>
                  this.deleteCustomerSubscription(subscriptionIdToDelete),
                toggleModal: () => this.toggleConfirmProductDelete(undefined),
                inscription: "Are you sure you want to delete product ?",
              })}
            {ModalCreator({
              title: "Edit Subscription",
              okType: "primary",
              okText: "Save",
              showModal: showSubscriptionEditModal,
              onOkay: this.saveSubscriptionEdits,
              toggleModal: this.closeSubscriptionEditModal,
              isLoading: isSavingSubscriptionEdits,
              width: 700,
              inscription: subscriptionToEdit ? (
                <BaseForm
                  key={subscriptionToEdit.id + (JSON.stringify(subscriptionToEdit))}
                  layout="vertical"
                  reference={this.subscriptionEditReference}
                  fieldsToHide={
                    isTierThreeProduct(subscriptionToEdit.product)
                      ? []
                      : [
                          "firstContributionDate",
                          "proofOfContributionDocumentUrl",
                        ]
                  }
                  fields={[
                    {
                      id: "salesPersonId",
                      name: "salesPersonId",
                      placeholder: "Enter user's name ...",
                      required: false,
                      inputType: "search",
                      label: "Sales Person",
                      changeHandler: this.handleSalesPersonSearchInput,
                      selectOptions: subscriptionToEdit.salesPerson?.id
                        ? [
                            ...(this.state.salesPersons ?? []),
                            {
                              id: subscriptionToEdit.salesPerson.id,
                              name: subscriptionToEdit.salesPerson.name,
                            },
                          ]
                        : this.state.salesPersons ?? [],
                      initialValue: subscriptionToEdit.salesPerson?.id,
                      elementStyle: {
                        width: "100%",
                        color: "#333",
                      },
                      colConfig: {
                        span: 24,
                      },
                    },
                    {
                      id: "subscriptionTagIds",
                      name: "subscriptionTagIds",
                      placeholder: "Add or remove tags...",
                      required: false,
                      inputType: "multipleSelect",
                      label: "Tags",
                      selectOptions: productSubscriptionTagOptions,
                      initialValue: subscriptionInitialTags(
                        subscriptionToEdit.customerSubscriptionTags ?? [],
                      ),
                      elementStyle: {
                        width: "100%",
                      },
                      colConfig: {
                        span: 24,
                      },
                    },
                    {
                      id: "firstContributionDate",
                      name: "firstContributionDate",
                      inputType: "defaultDate",
                      required: false,
                      rule: "object",
                      defaultPickerValue:
                        subscriptionToEdit.computedFirstContributionDate
                          ? moment(
                              subscriptionToEdit.computedFirstContributionDate,
                              "YYYY/MM/DD",
                            )
                          : undefined,
                      label: "First Contribution Date",
                      elementStyle: { width: "100%" },
                      colConfig: {
                        span: 24,
                      },
                    },
                    {
                      id: "proofOfContributionDocumentUrl",
                      name: "proofOfContributionDocumentUrl",
                      label: "Proof of Contribution",
                      inputType: "fileUpload",
                      placeholder: "Proof of Contribution",
                      rule: "string",
                      initialValue:
                        subscriptionToEdit.proofOfContributionDocumentUrl,
                      changeHandler: this.handleSubscriptionProofUpload,
                      defaultFileList:
                        subscriptionToEdit.proofOfContributionDocumentUrl
                          ? [
                              {
                                uid: "1",
                                name:
                                  subscriptionToEdit.proofOfContributionDocumentUrl
                                    ?.split("/")
                                    .pop() || "file",
                                status: "done",
                                url:
                                  subscriptionToEdit.proofOfContributionDocumentUrl,
                              },
                            ]
                          : [],
                      required: false,
                      colConfig: {
                        span: 24,
                      },
                    },
                  ]}
                />
              ) : (
                <div />
              ),
            })}
            {ModalCreator({
              title: "Edit Customer",
              okType: "primary",
              okText: "Save",
              showModal: showQuickEditModal,
              onOkay: this.saveQuickEditCustomer,
              toggleModal: this.closeQuickEditModal,
              isLoading: isQuickEditSaving,
              width: 1000,
              inscription: (
                <BaseForm
                  layout="vertical"
                  reference={this.quickEditCustomerReference}
                  fieldsToHide={[]}
                  fields={quickEditFields}
                />
              ),
            })}
            {ModalCreator({
              title:
                backgroundInfoMode === "edit"
                  ? "Edit Background Info"
                  : "Add Background Info",
              okType: "primary",
              okText: backgroundInfoMode === "edit" ? "Update" : "Save",
              showModal: showBackgroundInfoModal,
              onOkay: this.saveBackgroundInfo,
              toggleModal: this.closeBackgroundInfoModal,
              isLoading: isBackgroundInfoSaving,
              width: 900,
              inscription: (
                <BaseForm
                  layout="vertical"
                  reference={this.backgroundInfoReference}
                  fieldsToHide={[]}
                  fields={backgroundInfoFields}
                />
              ),
            })}
            {ModalCreator({
              title: "Delete Background Info",
              okType: "primary",
              okText: "Delete",
              showModal: showBackgroundInfoDeleteModal,
              onOkay: this.deleteBackgroundInfo,
              toggleModal: this.closeBackgroundInfoDeleteModal,
              isLoading: isBackgroundInfoSaving,
              inscription:
                "Are you sure you want to delete background information?",
            })}
            {ModalCreator({
              title: "Confirm Action",
              okType: "primary",
              okText: "Delete",
              showModal: showStandaloneDeleteModal,
              onOkay: this.confirmStandaloneDelete,
              toggleModal: this.closeStandaloneDeleteModal,
              isLoading: isLoading,
              inscription:
                deleteTargetType === "association"
                  ? "Are you sure you want to delete this association?"
                  : deleteTargetType === "beneficiary"
                    ? "Are you sure you want to delete this beneficiary?"
                    : deleteTargetType === "payout"
                      ? "Are you sure you want to delete this payout account?"
                      : deleteTargetType === "debit"
                        ? "Are you sure you want to delete this debit account?"
                        : "Are you sure you want to delete this item?",
            })}
            {ModalCreator({
              title:
                contactModalType && contactModalMode
                  ? `${contactModalMode === "edit" ? "Edit" : "Add"} ${
                    contactModalType === "email"
                      ? "Email"
                      : contactModalType === "phone"
                        ? "Phone"
                        : "Address"
                  }`
                  : "Contact",
              okType: "primary",
              okText: contactModalMode === "edit" ? "Update" : "Save",
              showModal: showContactModal,
              onOkay:
                contactModalType === "email"
                  ? this.saveContactEmail
                  : contactModalType === "phone"
                    ? this.saveContactPhone
                    : this.saveContactAddress,
              toggleModal: this.closeContactModal,
              isLoading: contactModalLoading,
              width: contactModalType === "address" ? 800 : 560,
              inscription: (
                <BaseForm
                  key={`${contactModalType}-${contactModalMode}-${
                    contactModalType === "email"
                      ? selectedEmail?.id ?? "new"
                      : contactModalType === "phone"
                        ? selectedPhone?.id ?? "new"
                        : selectedAddress?.id ?? "new"
                  }`}
                  layout="vertical"
                  reference={
                    contactModalType === "email"
                      ? this.contactEmailReference
                      : contactModalType === "phone"
                        ? this.contactPhoneReference
                        : this.contactAddressReference
                  }
                  fieldsToHide={
                    contactModalType === "address"
                      ? contactAddressFieldsToHide
                      : []
                  }
                  fields={contactModalFields}
                />
              ),
            })}
            {ModalCreator({
              title:
                deleteContactType === "email"
                  ? "Delete Email"
                  : deleteContactType === "phone"
                    ? "Delete Phone"
                    : "Delete Address",
              okType: "primary",
              okText: "Delete",
              showModal: showContactDeleteModal,
              onOkay: this.confirmDeleteContact,
              toggleModal: this.closeDeleteContactModal,
              isLoading: contactModalLoading,
              inscription:
                deleteContactType === "email"
                  ? "Are you sure you want to delete this email?"
                  : deleteContactType === "phone"
                    ? "Are you sure you want to delete this phone?"
                    : "Are you sure you want to delete this address?",
            })}
            {ModalCreator({
              title: "Customer Beneficiaries",
              showModal: showBeneficiariesModal,
              toggleModal: this.closeBeneficiariesModal,
              showFooter: false,
              width: 900,
              inscription: customer ? (
                <CustomerOnboardingDetails
                  ref={this.beneficiariesModalRef}
                  isEditing={true}
                  opportunity={{ customer } as Opportunity}
                  customer={customer}
                  loadOpportunity={this.getCustomer}
                  financialInstitutions={financialInstitutions}
                  branchMap={branchMap}
                  companies={companies}
                  subscriptions={customer.customerSubscriptions || []}
                  customerAssociations={customer.customerCompanyAssociations || []}
                  showBeneficiarySection={true}
                  showCustomerAssociationSection={false}
                  showOnboardingDetailsSection={false}
                  visibleViews={[CustomerOnboardingViews.beneficiary]}
                  showList={false}
                  renderInline={true}
                  hideFormOnDelete={true}
                  onCancelEdit={this.closeBeneficiariesModal}
                  activekey={beneficiariesActiveKey}
                  updateBeneficiaryInformation={this.updateBeneficiaryInformation}
                  updateCustomerPaymentInformation={
                    this.updateCustomerPaymentInformation
                  }
                  updateCustomerCompanyAssociationsInformation={
                    this.updateCustomerCompanyAssociationsInformation
                  }
                />
              ) : (
                <div />
              ),
            })}
            {ModalCreator({
              title: "Payment Accounts",
              showModal: showPaymentAccountsModal,
              toggleModal: this.closePaymentAccountsModal,
              showFooter: false,
              width: 900,
              inscription: customer ? (
                <CustomerOnboardingDetails
                  ref={this.paymentAccountsModalRef}
                  isEditing={true}
                  opportunity={{ customer } as Opportunity}
                  customer={customer}
                  loadOpportunity={this.getCustomer}
                  financialInstitutions={financialInstitutions}
                  branchMap={branchMap}
                  companies={companies}
                  subscriptions={customer.customerSubscriptions || []}
                  customerAssociations={customer.customerCompanyAssociations || []}
                  showBeneficiarySection={false}
                  showCustomerAssociationSection={false}
                  showOnboardingDetailsSection={false}
                  visibleViews={
                    paymentAccountsActiveKey === CustomerOnboardingViews.debit
                      ? [CustomerOnboardingViews.debit]
                      : [CustomerOnboardingViews.payout]
                  }
                  showList={false}
                  renderInline={true}
                  hideFormOnDelete={true}
                  onCancelEdit={this.closePaymentAccountsModal}
                  activekey={paymentAccountsActiveKey}
                  updateBeneficiaryInformation={this.updateBeneficiaryInformation}
                  updateCustomerPaymentInformation={
                    this.updateCustomerPaymentInformation
                  }
                  updateCustomerCompanyAssociationsInformation={
                    this.updateCustomerCompanyAssociationsInformation
                  }
                />
              ) : (
                <div />
              ),
            })}
            {ModalCreator({
              title: "Customer Associations",
              showModal: showAssociationsModal,
              toggleModal: this.closeAssociationsModal,
              showFooter: false,
              width: 900,
              inscription: customer ? (
                <CustomerOnboardingDetails
                  ref={this.associationsModalRef}
                  isEditing={true}
                  opportunity={{ customer } as Opportunity}
                  customer={customer}
                  loadOpportunity={this.getCustomer}
                  financialInstitutions={financialInstitutions}
                  branchMap={branchMap}
                  companies={companies}
                  subscriptions={customer.customerSubscriptions || []}
                  customerAssociations={customer.customerCompanyAssociations || []}
                  showBeneficiarySection={false}
                  showCustomerAssociationSection={true}
                  showOnboardingDetailsSection={false}
                  visibleViews={[CustomerOnboardingViews.customerAssociation]}
                  showList={false}
                  renderInline={true}
                  hideFormOnDelete={true}
                  onCancelEdit={this.closeAssociationsModal}
                  activekey={associationsActiveKey}
                  updateBeneficiaryInformation={this.updateBeneficiaryInformation}
                  updateCustomerPaymentInformation={
                    this.updateCustomerPaymentInformation
                  }
                  updateCustomerCompanyAssociationsInformation={
                    this.updateCustomerCompanyAssociationsInformation
                  }
                />
              ) : (
                <div />
              ),
            })}
            {this.state.showAddDocumentModal &&
              ModalCreator({
                title: "Add Required Document",
                okType: "primary",
                okText: "Close",
                showModal: this.state.showAddDocumentModal,
                showFooter: true,
                onOkay: () => this.toggleFileModal(false),
                toggleModal: () => this.toggleFileModal(false),
                width: 700,
                inscription: customer ? (() => {
                  const opportunity =
                    customer?.opportunities?.[0] || ({ customer } as Opportunity);
                  const signedApplicationUpload =
                    customer?.customerInfoFileUploads?.find(
                      (file) => file.fileType === FileTypes.signedApplicationForm,
                    );
                  const directDebitUpload =
                    customer?.customerInfoFileUploads?.find(
                      (file) =>
                        file.fileType === FileTypes.directDebitAuthorizationForm,
                    );

                  let allFields = createDocumentUploadFormFields({
                    opportunity,
                    customer,
                    handleFileUploadSuccess: this.handleDocumentUploadSuccess,
                    initialValues: {
                      signedApplicationForm: signedApplicationUpload,
                      directDebitAuthorizationForm: directDebitUpload,
                      customerInfoFileUploads: customer?.customerInfoFileUploads,
                    },
                  });

                  allFields = allFields.map((field) => {
                    let fileTypeToCheck = "";
                    if (field.name === "signedApplicationFormUrl") {
                      fileTypeToCheck = FileTypes.signedApplicationForm;
                    } else if (field.name === "directDebitAuthorizationForm") {
                      fileTypeToCheck = FileTypes.directDebitAuthorizationForm;
                    }
                    if (
                      fileTypeToCheck &&
                      customer?.customerInfoFileUploads?.some(
                        (f) => f.fileType === fileTypeToCheck,
                      )
                    ) {
                      return { ...field, initialValue: "exists" };
                    }
                    return field;
                  });

                  const documentModalFields = allFields.filter((field) => !field.initialValue);
                  return documentModalFields.length === 0 ? (
                    <Empty description="All required documents are already on file." />
                  ) : (
                    <BaseForm
                      reference={this.docsReference}
                      fields={documentModalFields}
                      layout="vertical"
                      fieldsToHide={[]}
                    />
                  );
                })() : (
                  <div />
                ),
              })}
            {ModalCreator({
              title: "Delete Document",
              okType: "primary",
              okText: "Delete",
              showModal: this.state.showDeleteConfirmationModal,
              onOkay: this.confirmDeleteDocument,
              toggleModal: this.toggleDeleteConfirmationModal,
              isLoading: isLoading,
              inscription: "Are you sure you want to delete this document?",
            })}

            <Details
              createInteraction={this.createInteraction}
              interactionReference={this.reference}
              contributionsLoader={this.state.contributionsLoader}
              toggleAccountMergerModal={this.toggleAccountMergerModal}
              toggleReverseMergeModal={this.toggleReverseMergeModal}
              toggleDrawer={this.toggleDrawer}
              customer={customer}
              quickEditButton={
                <EntitledSection entitlement={entitlements.Customers.edit}>
                  <EditButton handleClick={this.openQuickEditModal} />
                </EntitledSection>
              }
              openContactModal={this.openContactModal}
              openDeleteContactModal={this.openDeleteContactModal}
              openBackgroundInfoModal={this.openBackgroundInfoModal}
              openBackgroundInfoDeleteModal={this.openBackgroundInfoDeleteModal}
              openBeneficiariesModal={this.openBeneficiariesModal}
              openBeneficiariesDeleteModal={this.openBeneficiariesDeleteModal}
              openPaymentAccountsModal={this.openPaymentAccountsModal}
              openPaymentAccountsDeleteModal={this.openPaymentAccountsDeleteModal}
              openAssociationsModal={this.openAssociationsModal}
              openAssociationsDeleteModal={this.openAssociationsDeleteModal}
              markAssociationAsFormer={this.markAssociationAsFormer}
              markAssociationAsCurrent={this.markAssociationAsCurrent}
              onMakePrimaryEmail={this.markEmailAsPrimary}
              onMakePrimaryPhone={this.markPhoneAsPrimary}
              onMakePrimaryAddress={this.markAddressAsPrimary}
              onReactivateEmail={this.reactivateEmail}
              onReactivatePhone={this.reactivatePhone}
              onReactivateAddress={this.reactivateAddress}
              toggleFileModal={this.toggleFileModal}
              toggleDeleteConfirmationModal={this.toggleDeleteConfirmationModal}
              setEntityToBeDeleted={this.setEntityToBeDeleted}
              toggleLienSection={this.toggleLienSection}
              setContribution={this.setCurrentSubscription}
              getProductContribution={this.getContributionData}
              customerBalanceSummary={customerBalanceSummary}
              toggleVansSection={this.toggleVansSection}
              customerTransactions={customerTransactions}
              phoenixTickets={phoenixTickets}
              toggleConfirmProductDelete={this.toggleConfirmProductDelete}
              openSubscriptionEditModal={this.openSubscriptionEditModal}
              handlePanelChange={this.handlePanelChange}
              customerStatements={customerStatements}
              toggleFirstContributionModal={this.toggleFirstContributionModal}
              createInteractionButtonRef={this.createInteractionButtonRef}
              redemptionRequests={redemptionRequests}
              transferRequests={transferRequests}
              isRedemptionLoading={isRedemptionLoading}
              isTransferLoading={isTransferLoading}
              isTransactionsLoading={isTransactionsLoading}
              isLoanRequestsLoading={isLoanRequestsLoading}
              loanRequests={loanRequests}
              fixedIncomeRequests={fixedIncomeRequests}
              isFixedIncomeRequestsLoading={isFixedIncomeRequestsLoading}
              toggleAllocationDetailsModal={this.toggleAllocationDetailsModal}
              onReverse={(duplicateId) => this.reverseMerge(duplicateId)}
              onReverseAll={this.reverseAllMerges}
              isReversingMerge={this.state.isReversingMerge}
              isBulkReversing={this.state.isBulkReversingMerge}
              {...({ syncCustomerManually: this.syncCustomerManually } as any)}
            />

            {customer && (
              <EntitledSection entitlement={entitlements.Customers.tags.manage}>
                <div className="section-card" style={{ marginTop: "30px" }}>
                  <Descriptions title={<div>Tags</div>} />
                  <Collapse style={{ marginBottom: "12px" }}>
                    <Panel
                      key="tags"
                      header={
                        <span>
                          Customer Tags
                          <Badge
                            count={
                              this.state.allCustomerTags.filter(
                                (r) => r.isActive,
                              ).length
                            }
                            style={tabPaneStyle}
                          />
                        </span>
                      }
                    >
                      <TagsManagement
                        tagRecords={this.state.allCustomerTags}
                        tagScope={{ forCustomer: true }}
                        isSubmitting={this.state.isLoadingCustomerTags}
                        onAssignTags={this.assignCustomerTags}
                      />
                    </Panel>
                  </Collapse>
                </div>
              </EntitledSection>
            )}

            {this.state.customer && (
              <ActivitiesSection
                showRequests={true}
                showBorder={true}
                style={{ marginTop: "30px" }}
                forCustomer={true}
                forCompany={false}
                showHeading={true}
                isInteractionLoading={isInteractionLoading}
                showInteraction={showInteractionModal}
                toggleInteractionEdit={this.toggleInteractionEdit}
                toggleViewRequestsSection={this.toggleViewRequestsSection}
                toggleViewInteractionSection={this.toggleViewInteractionSection}
                toggleViewRedemptionRequestsSection={
                  this.toggleViewRedemptionRequestsSection
                }
                toggleCreateTicket={this.toggleTicketCreate}
                toggleCreateInteractionModal={this.toggleShowInteractionModal}
                activities={this.state.customerInteractions}
                createInteraction={this.createCustomerInteraction}
                reference={this.reference}
                showForm={true}
                hasAttachment={true}
                attachments={this.state.attachments}
                removeAttachment={
                  this.unassignAttachmentFromCustomerInteraction
                }
                updateAttachments={this.updateAttachments}
                useBaseColumn={false}
                transferRequests={this.state.customer.transferRequests}
                customer={this.state.customer}
                activitiesCount={this.state.activitiesCount}
                nextPage={this.interactionsNextPage}
                disableCreateInteractionButton={disableCreateInteractionButton}
                createInteractionButtonRef={this.createInteractionButtonRef}
                currentInteractionId={this.state.currentInteractionId}
                handlePanelChange={this.handlePanelChange}
              />
            )}
          </Fragment>
      );
    } else {
      userInterface = <Fragment />;
    }

    return (
      <>
        <EntitledEntity
          id={String(this.state.id)}
          entitlement={this.props.entitlement}
          user={this.props.user}
        >
          {this.state.showAccountMergerModal &&
            ModalCreator({
              title: "Account Merger Section",
              okType: "primary",
              okText: "Merge Account",
              okayButtonProps: {
                disabled: this.state.selectedCustomers.length === 0,
              },
              showModal: this.state.showAccountMergerModal,
              onOkay: this.mergeAccounts,
              toggleModal: this.toggleAccountMergerModal,
              inscription: AccountMergerUI({
                selectedCustomers: this.state.selectedCustomers,
                handleSearchInput: this.handleSearchInput,
                searchQuery: this.state.customerSearchQuery,
                customers: this.state.customers,
                isLoading: this.state.isSearchingForCustomer,
                isMerging: this.state.isMerging,
                handleCheckboxGroupChange: this.handleCheckboxGroupChange,
              }),
            })}

          {this.state.showStatementModal &&
            ModalCreator({
              title: "Generate Customer Statement",
              okType: "primary",
              okText: "Generate Statement",
              showModal: this.state.showStatementModal,
              isLoading: this.state.isStatementLoading,
              onOkay: this.generateCustomerStatement,
              toggleModal: this.toggleShowStatementModal,
              inscription: GenerateStatementUI({
                reference: this.statementReference,
                url: this.state.generatedStatementUrl,
                employers: [
                  ...(this.state.customer?.employers ?? []),
                  ...(this.state.customer?.formerCompanies ?? []),
                ] as unknown as Employer[],
              }),
            })}
          {ModalCreator({
            title: "Update Customer First Contribution",
            okType: "primary",
            okText: "Update Contribution",
            showModal: showFirstContributionModal,
            isLoading: isLoading,
            onOkay: this.updateCustomerFirstContribution,
            toggleModal: () =>
              this.toggleFirstContributionModal(currentContribution),
            inscription: FirstContributionUI({
              reference: this.contributionReference,
              initialContributionDate:
                currentContribution?.subscription.computedFirstContributionDate,
              contributionDocumentUrl:
                currentContribution?.subscription
                  ?.proofOfContributionDocumentUrl,
              handleFileUploadChange: this.handleFileUploadChange,
            }),
          })}
          <DelayUpdateModal
            showModal={this.state.showDelayUpdateModal}
            templates={this.state.delayNotificationTemplates}
            selectedTemplateId={this.state.selectedDelayTemplateId}
            comment={this.state.delayUpdateComment}
            requiresComment={this.selectedDelayTemplateRequiresCustomComment()}
            preview={this.state.delayNotificationPreview}
            isLoading={
              this.state.isDelayTemplatesLoading ||
              this.state.isDelayPreviewLoading
            }
            isSending={this.state.isSendingDelayUpdate}
            onTemplateChange={this.handleDelayTemplateChange}
            onCommentChange={this.handleDelayCommentChange}
            onSend={this.sendDelayUpdate}
            onClose={this.openDelayUpdateModal}
          />
          {showAllocationDetailsModal &&
            currentSubscriptionForAllocation &&
            customer && (
              <Modal
                title="Investment Allocation Details"
                visible={showAllocationDetailsModal}
                onCancel={() => this.toggleAllocationDetailsModal()}
                width={1400}
                footer={null}
                style={{ top: 20 }}
                bodyStyle={{ padding: "24px" }}
              >
                <CustomerBalanceBreakdown
                  customerPetraId={customer.petraId}
                  fundCode={
                    currentSubscriptionForAllocation.product.fundCode || ""
                  }
                  companies={[
                    ...(customer.employers || []),
                    ...(customer.formerCompanies || []),
                  ].map((company) => ({
                    ...company,
                    customerCompanyAssociationId:
                      customer.customerCompanyAssociations?.find(
                        (assoc) => assoc.company.id === company.id,
                      )?.id || "",
                  }))}
                  clientPetraId={
                    isSavingsBooster(currentSubscriptionForAllocation)
                      ? "CO00000363" // Petra Individual Choice by default
                      : currentSubscriptionForAllocation.company?.petraId
                  }
                  showDatePicker
                  showExtendedColumns
                  showTitle={false}
                  showPendingRedemptionsCard={true}
                  compact={false}
                />
              </Modal>
            )}
          <Spin spinning={isLoading}>
            <Row>
              <Card
                style={{
                  overflow: "hidden",
                  borderTopWidth: 1,
                  borderTopColor: "#f0f0f0",
                }}
                className="view-customer-card"
              >
                <Row
                  style={{
                    marginBottom: "20px",
                  }}
                >
                  <Col span={24}>{userInterface}</Col>
                </Row>
              </Card>
            </Row>
          </Spin>
          <Drawer
            title={this.getTitleBasedOnDrawerContent()}
            width={1300}
            onClose={this.toggleModal}
            visible={this.state.showDrawer}
            bodyStyle={{ paddingBottom: 80 }}
            maskClosable={false}
          >
            {this.getDrawerContent()}
          </Drawer>
          {this.state.showCancelModal &&
            ModalCreator({
              title: "Cancel",
              okType: "primary",
              okText: "Yes",
              width: 400,
              showModal: true,
              onOkay: this.toggleDrawer(""),
              toggleModal: this.toggleModal,
              showFooter: true,
              inscription: `Are you sure you want to cancel ${spaceCamel(
                this.state.activeDrawerContent,
              ).toLocaleLowerCase()} ?`,
            })}
        </EntitledEntity>
        <span style={{ position: "absolute", top: 60, right: 40 }}>
          {nextPreviousUI}
        </span>
      </>
    );
  }
}
